<template>
  <div v-cloak class="rpd-mls-page">
    <PageLoader v-if="initialLoading" />

    <!-- ─── Page Header ─── -->
    <div class="rpd-ph">
      <div class="rpd-ph__left">
        <div class="rpd-ph__breadcrumb">
          Reports
          <font-awesome-icon :icon="['fas', 'chevron-right']" class="rpd-ph__sep" />
          {{ isInvestorReport ? 'Shadow Market' : isMarketStrata ? 'Market Strata' : 'Market Rental' }}
          <font-awesome-icon :icon="['fas', 'chevron-right']" class="rpd-ph__sep" />
          <span class="rpd-ph__breadcrumb-cur">MLS</span>
        </div>
        <div class="rpd-ph__title-row">
          <h1 class="rpd-ph__title">{{ reportTitle }}</h1>
          <button class="rpd-ph__summary-btn" @click="unitReportModal = true">Unit Summary</button>
          <button class="rpd-ph__summary-btn" @click="openCityChart">City Breakdown</button>
          <span class="rpd-ph__updated">Last updated <strong>{{ reportFormatDate(currentDate) }}</strong></span>
        </div>
      </div>
      <div class="rpd-ph__right">
        <button class="rpd-icon-btn" title="Save view" @click="saveFilters()">
          <font-awesome-icon :icon="['fas', 'bookmark']" />
        </button>
        <div class="rpd-ph__divider" />
        <!-- Map is an Investor-tab affordance: only those rows carry the
             geo_location the map plots. -->
        <button v-if="isInvestorReport" class="rpd-pill-ghost"
          :class="{ 'rpd-pill-ghost--active': mapView }" @click="toggleMapView">
          <font-awesome-icon :icon="['fas', mapView ? 'table' : 'map-marked-alt']" />
          {{ mapView ? 'View Table' : 'View Map' }}
        </button>
        <button class="rpd-pill-ghost" @click="exportData()">
          <font-awesome-icon :icon="['fas', 'file-export']" />
          Export CSV
        </button>
        <button class="rpd-pill-ghost" @click="generateArrayOfImages()">
          <font-awesome-icon :icon="['fas', 'download']" />
          Download PDF
        </button>
      </div>

      <!-- Report tabs — the two reports this page hosts sit on the seam between
           the header and the table they load, so the active tab reads as
           physically attached to its own data. Mirrors MarketOverview.vue's
           Project / Planning tab row, including the route-driven active state. -->
      <div class="rpd-report-tabs" :class="isInvestorReport ? 'rpd-report-tabs--investor' : 'rpd-report-tabs--units'"
        role="tablist">
        <button type="button" role="tab" class="rpd-report-tab"
          :class="{ 'rpd-report-tab--active': !isInvestorReport }"
          :aria-selected="!isInvestorReport ? 'true' : 'false'" @click="switchReport('units')">
          <font-awesome-icon :icon="['fas', 'file-invoice']" class="rpd-report-tab-icon" />
          Unit Report
        </button>
        <button type="button" role="tab" class="rpd-report-tab"
          :class="{ 'rpd-report-tab--active': isInvestorReport }"
          :aria-selected="isInvestorReport ? 'true' : 'false'" @click="switchReport('investor')">
          <font-awesome-icon :icon="['fas', 'building']" class="rpd-report-tab-icon" />
          Investor Rental
        </button>
      </div>
    </div>

    <!-- ─── Content Area ─── -->
    <div class="rpd-content">

      <!-- KPI Row -->
      <div class="rpd-kpi-row">
        <div class="rpd-kpi rpd-kpi--navy">
          <div class="rpd-kpi__label">Total Units</div>
          <div class="rpd-kpi__value">{{ totalRows.toLocaleString() }}</div>
          <div class="rpd-kpi__sub">{{ activeCount.toLocaleString() }} active</div>
        </div>
        <div class="rpd-kpi rpd-kpi--violet">
          <div class="rpd-kpi__label">{{ isInvestorReport ? 'Avg Rent' : 'Avg List Price' }}</div>
          <div class="rpd-kpi__value">{{ kpiAvgList }}</div>
        </div>
        <div class="rpd-kpi">
          <div class="rpd-kpi__label">Avg $PSF</div>
          <div class="rpd-kpi__value">{{ kpiAvgPsf }}</div>
        </div>
        <!-- The Investor feed carries no incentives, so the two net cards would
             read as the gross ones repeated. Average size and the priced-unit
             count qualify the rate instead. -->
        <template v-if="isInvestorReport">
          <div class="rpd-kpi">
            <div class="rpd-kpi__label">Avg Size</div>
            <div class="rpd-kpi__value">
              {{ currentAvailability && currentAvailability.size
                ? Math.round(currentAvailability.size).toLocaleString() : '-' }}
            </div>
            <div class="rpd-kpi__sub">sqft · across available pricing</div>
          </div>
          <div class="rpd-kpi">
            <div class="rpd-kpi__label">Priced Units</div>
            <div class="rpd-kpi__value">
              {{ currentAvailability ? currentAvailability.units.toLocaleString() : '-' }}
            </div>
            <div class="rpd-kpi__sub">active inventory only</div>
          </div>
        </template>
        <template v-else>
          <div class="rpd-kpi">
            <div class="rpd-kpi__label">Avg Net Price</div>
            <div class="rpd-kpi__value">{{ kpiAvgNet }}</div>
          </div>
          <div class="rpd-kpi">
            <div class="rpd-kpi__label">Avg Net $PSF</div>
            <div class="rpd-kpi__value">{{ kpiAvgNetPsf }}</div>
          </div>
        </template>
      </div>

      <!-- ─── Toolbar Row 1: Search + City + More filters + Save/Fields ─── -->
      <div class="rpd-toolbar ms-filter-bar">
        <div class="ms-search-wrap">
          <font-awesome-icon :icon="['fas', 'search']" class="ms-search-icon" />
          <input v-model="filter_rows.filterText" class="ms-search" placeholder="Search project, address, unit…"
            type="text" />
        </div>

        <!-- Cities tree selector. The Investor tab resolves its selection through
             `citySelected` (which also matches sub-communities on `neighbour`);
             the Unit Report keeps the custom_filter.city model its saved views
             persist. -->
        <area-selector-tree v-if="isInvestorReport" v-model="citySelected" :options="cityOptions" placeholder="City…"
          search-label="Search areas" search-input-placeholder="City or neighbourhood…" item-singular="area"
          item-plural="areas" />
        <area-selector-tree v-else v-model="custom_filter.city.city_selected" :options="cityOptions" placeholder="City…"
          search-label="Search areas" search-input-placeholder="City or neighbourhood…" item-singular="area"
          item-plural="areas" />

        <!-- More Filters toggle -->
        <button class="rpd-pill-ghost" :class="{ 'rpd-pill-ghost--active': moreFiltersOpen || activeFilterCount > 0 }"
          @click="moreFiltersOpen = !moreFiltersOpen" type="button">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
            stroke-linecap="round">
            <line x1="4" y1="6" x2="20" y2="6" />
            <line x1="8" y1="12" x2="16" y2="12" />
            <line x1="11" y1="18" x2="13" y2="18" />
          </svg>
          More filters
          <span v-if="activeFilterCount > 0" class="rpd-pill-ghost__count">{{ activeFilterCount }}</span>
        </button>

        <div class="rpd-spacer" />

        <button class="rpd-pill-ghost" @click="saveFilters()" type="button">
          <font-awesome-icon :icon="['fas', 'bookmark']" />
          Save view
        </button>
      </div>

      <!-- ─── More Filters Panel ─── -->
      <div v-if="moreFiltersOpen" class="rpd-mf-panel">
        <div class="rpd-mf-groups">
          <div class="rpd-mf-group">
            <span class="rpd-mf-label">Construction Status</span>
            <div class="rpd-mf-chips">
              <button v-for="o in filter_options[0].children" :key="o.id" class="rpd-tag-chip"
                :class="{ 'rpd-tag-chip--on': filter_rows.filterArray.includes(o.id) }" @click="toggleFilterChip(o.id)"
                type="button">{{ o.label }}</button>
            </div>
          </div>
          <div class="rpd-mf-group">
            <span class="rpd-mf-label">Project Type</span>
            <div class="rpd-mf-chips">
              <button v-for="o in filter_options[1].children" :key="o.id" class="rpd-tag-chip"
                :class="{ 'rpd-tag-chip--on': filter_rows.filterArray.includes(o.id) }" @click="toggleFilterChip(o.id)"
                type="button">{{ o.label }}</button>
            </div>
          </div>
          <div class="rpd-mf-group">
            <span class="rpd-mf-label">Product Type</span>
            <div class="rpd-mf-chips">
              <button v-for="o in filter_options[2].children" :key="o.id" class="rpd-tag-chip"
                :class="{ 'rpd-tag-chip--on': filter_rows.filterArray.includes(o.id) }" @click="toggleFilterChip(o.id)"
                type="button">{{ o.label }}</button>
            </div>
          </div>
        </div>
        <button v-if="activeFilterCount > 0" class="rpd-mf-clear" @click="filter_rows.filterArray = []" type="button">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
            stroke-linecap="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
          Clear all
        </button>
      </div>

      <!-- ─── Toolbar Row 2: Segments + Dropdowns + Date Filters ─── -->
      <div class="rpd-toolbar rpd-toolbar--r2">
        <div class="rpd-seg">
          <span class="rpd-seg__label">Unit</span>
          <div class="rpd-seg__btns">
            <button v-for="opt in ['All'].concat(statusOpts)" :key="'st-' + opt"
              :class="['rpd-seg__btn', { 'rpd-seg__btn--on': getSegVal('status') === opt }]"
              @click="setSegVal('status', opt)">{{ opt }}</button>
          </div>
        </div>

        <div class="rpd-seg">
          <span class="rpd-seg__label">Listing</span>
          <div class="rpd-seg__btns">
            <button v-for="opt in ['All'].concat(listingOpts)" :key="'lt-' + opt"
              :class="['rpd-seg__btn', { 'rpd-seg__btn--on': getSegVal('listing') === opt }]"
              @click="setSegVal('listing', opt)">{{ opt }}</button>
          </div>
        </div>

        <div class="rpd-seg">
          <span class="rpd-seg__label">Project</span>
          <div class="rpd-seg__btns">
            <button v-for="opt in ['All'].concat(projectStatusOpts)" :key="'ps-' + opt"
              :class="['rpd-seg__btn', { 'rpd-seg__btn--on': getSegVal('projectStatus') === opt }]"
              @click="setSegVal('projectStatus', opt)">{{ opt }}</button>
          </div>
        </div>

        <!-- Type (Beds) styled dropdown -->
        <div class="rpd-udrop">
          <button class="rpd-udrop__btn" :class="{ 'rpd-udrop__btn--active': singleBedsVal }"
            @click.stop="typeOpen = !typeOpen" type="button">
            {{ singleBedsLabel }}
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#8A8A8A" stroke-width="2.5"
              stroke-linecap="round">
              <polyline :points="typeOpen ? '18 15 12 9 6 15' : '6 9 12 15 18 9'" />
            </svg>
          </button>
          <template v-if="typeOpen">
            <div class="rpd-udrop__backdrop" @click.stop="typeOpen = false" />
            <div class="rpd-udrop__menu" @click.stop>
              <button class="rpd-udrop__opt" :class="{ 'rpd-udrop__opt--on': !singleBedsVal }"
                @click="setSingleBed(''); typeOpen = false">All Types</button>
              <button v-for="o in custom_filter.beds.beds_options" :key="o.id" class="rpd-udrop__opt"
                :class="{ 'rpd-udrop__opt--on': singleBedsVal === o.id }"
                @click="setSingleBed(o.id); typeOpen = false">{{ o.label }}</button>
            </div>
          </template>
        </div>

        <!-- Baths styled dropdown -->
        <div class="rpd-udrop">
          <button class="rpd-udrop__btn" :class="{ 'rpd-udrop__btn--active': singleBathsVal }"
            @click.stop="bathsOpen = !bathsOpen" type="button">
            {{ singleBathsLabel }}
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#8A8A8A" stroke-width="2.5"
              stroke-linecap="round">
              <polyline :points="bathsOpen ? '18 15 12 9 6 15' : '6 9 12 15 18 9'" />
            </svg>
          </button>
          <template v-if="bathsOpen">
            <div class="rpd-udrop__backdrop" @click.stop="bathsOpen = false" />
            <div class="rpd-udrop__menu" @click.stop>
              <button class="rpd-udrop__opt" :class="{ 'rpd-udrop__opt--on': !singleBathsVal }"
                @click="setSingleBath(''); bathsOpen = false">Any Baths</button>
              <button v-for="o in custom_filter.baths.baths_options" :key="o.id" class="rpd-udrop__opt"
                :class="{ 'rpd-udrop__opt--on': singleBathsVal === o.id }"
                @click="setSingleBath(o.id); bathsOpen = false">{{ o.label }}</button>
            </div>
          </template>
        </div>

        <!-- Unit Features (FP Specs) styled dropdown -->
        <div class="rpd-udrop">
          <button class="rpd-udrop__btn"
            :class="{ 'rpd-udrop__btn--active': custom_filter.fpSpecs.fpSpecs_selected.length > 0 }"
            @click.stop="featuresOpen = !featuresOpen" type="button">
            {{ featuresDisplay }}
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#8A8A8A" stroke-width="2.5"
              stroke-linecap="round">
              <polyline :points="featuresOpen ? '18 15 12 9 6 15' : '6 9 12 15 18 9'" />
            </svg>
          </button>
          <template v-if="featuresOpen">
            <div class="rpd-udrop__backdrop" @click.stop="featuresOpen = false" />
            <div class="rpd-udrop__menu" @click.stop>
              <button v-for="o in custom_filter.fpSpecs.fpSpecs_options" :key="o.id" class="rpd-udrop__opt"
                :class="{ 'rpd-udrop__opt--on': custom_filter.fpSpecs.fpSpecs_selected.includes(o.label) }"
                @click="toggleFpSpec(o.label)">{{ o.label }}</button>
            </div>
          </template>
        </div>

        <!-- Price Date filter -->
        <template v-if="isAdmin">
          <div class="rpd-date-toggle-wrap">
            <RpdDoubleSlider :toggle-title="'Price'" :enable-range="enablePricedate" :range-value="rangeValue"
              v-on:switchRangeValue="switchRangeValue" :dateSelected.sync="custom_filter.priceDate.priceDate_selected"
              :max-date="'180'" :isMarketRental="!isMarketStrata" />
          </div>
        </template>
        <template v-else>
          <DateSlider label="Price Date" v-model="priceDateDays" />
        </template>

        <!-- Sold Date filter -->
        <template v-if="isAdmin">
          <div class="rpd-date-toggle-wrap">
            <RpdDoubleSlider :toggle-title="'Sold'" :enable-range="enableSoldDate" :range-value="rangeValueSoldDate"
              v-on:switchRangeValue="switchRangeValue" :dateSelected.sync="custom_filter.priceDate.soldDate_selected"
              :max-date="'180'" :isMarketRental="!isMarketStrata" />
          </div>
        </template>
        <template v-else>
          <DateSlider label="Sold Date" v-model="soldDateDays" />
        </template>
      </div>

      <!-- ─── Fields Panel ─── -->
      <FieldsPanel :shown-cols="shownFields" :hidden-cols="availableFields" title-key="label" field-key="id"
        @update:shownCols="shownFields = $event" @update:hiddenCols="availableFields = $event" @reset="resetFields" />

      <!-- Map — Investor tab only (see the header toggle). The listing list sits
           left of the map, each entry focusing its marker. -->
      <div v-if="isInvestorReport && mapView" class="rpd-map-layout">
        <div class="rpd-lp-list">
          <div class="rpd-lp-list-header">
            <div>
              <div class="rpd-lp-list-title">Rental listings</div>
              <div class="rpd-lp-list-count">
                <strong>{{ mapRows.length }}</strong> of {{ totalRows }} on map
              </div>
            </div>
            <button class="rpd-lp-sort" @click="toggleListingSort" :title="'Sort by ' + listingSortLabel">
              <font-awesome-icon :icon="['fas', 'sort']" />
              {{ listingSortLabel }} {{ listingSortDir === 'desc' ? '↓' : '↑' }}
            </button>
          </div>
          <div class="rpd-lp-list-body">
            <div v-for="row in sortedMapRows" :key="row.uid" class="rpd-lp-item"
              :class="{ 'rpd-lp-item--selected': row.uid === selectedListingUid }" @click="selectListing(row)">
              <div class="rpd-lp-thumb" :style="listingThumbStyle(row)">
                <span v-if="!listingThumbUrl(row)" class="rpd-lp-thumb-fallback">
                  <font-awesome-icon :icon="['fas', 'building']" />
                </span>
              </div>
              <div class="rpd-lp-meta">
                <div class="rpd-lp-name-row">
                  <span class="rpd-lp-status-dot" :style="{ background: statusColor(row.status) }"></span>
                  <span class="rpd-lp-name" :title="row.name">{{ row.name || 'Untitled listing' }}</span>
                </div>
                <div class="rpd-lp-dev" :title="row.developer">{{ row.developer || '—' }}</div>
                <div class="rpd-lp-location">
                  <font-awesome-icon :icon="['fas', 'map-marker-alt']" class="rpd-lp-location-icon" />
                  <span>{{ listingLocation(row) }}</span>
                </div>
                <div v-if="listingBedType(row) || row.project_type || row.status" class="rpd-lp-types">
                  <span v-if="listingBedType(row)" class="rpd-lp-chip">{{ listingBedType(row) }}</span>
                  <span v-if="row.project_type" class="rpd-lp-chip">{{ row.project_type }}</span>
                  <span v-if="row.status" :class="listingBadgeClass(row.status)">{{ row.status }}</span>
                </div>
              </div>
              <div class="rpd-lp-stats">
                <div class="rpd-lp-psf">{{ formatPsf(row.total_net_psf) }}</div>
                <div class="rpd-lp-psf-unit">/sqft</div>
              </div>
            </div>
            <div v-if="!sortedMapRows.length" class="rpd-lp-empty">
              No listings match the current filters.
            </div>
          </div>
        </div>
        <div class="rpd-map-host">
          <project-map :rows="mapRows" :selected-uid="selectedListingUid" page-title="Market Rental"
            @select="onMarkerSelect" @open-detail="openListingDetail" />
        </div>
      </div>

      <!-- Tab-switch loading. Sits in the table region rather than over the page,
           because the header, tabs and filters above are unchanged — only the
           rows are being swapped (mirrors MarketOverview.vue's .ms-state). -->
      <div v-if="tableLoading" class="rpd-state" role="status" aria-live="polite">
        <div class="rpd-spinner" aria-hidden="true"></div>
        <span>Loading {{ isInvestorReport ? 'rental listings' : 'units' }}…</span>
      </div>

      <!-- v-show (not v-if) so Tabulator keeps its DOM while the map is up or a
           tab switch is loading — it only needs a redraw when it comes back
           (see toggleMapView). -->
      <div class="ms-table-wrap" v-show="!tableLoading && !(isInvestorReport && mapView)">
        <MlsReportTable ref="mlsTable" :row-data="filteredMlsData" :is-market-strata="isMarketStrata"
          :fields="shownFieldIds" :server-mode="false" @row-click="popHistory" @reached-last-row="onReachedLastRow" />
        <div v-if="isFetchingMore" class="rpd-loading-more" role="status" aria-live="polite">
          <span class="rpd-loading-more__spinner" aria-hidden="true"></span>
          <span>Loading more…</span>
        </div>
      </div>

    </div><!-- /rpd-content -->
    <b-modal ref="floorplan_modal" header-bg-variant="vb-secondary" title="Floor Plan" v-for="(row, key) in mappedRow"
      :id="'mls-fp-' + row.field_mls_floor_plan_fid" hide-footer :key="key">
      <div v-if="row.floor_plan.fid" class="d-block text-center">
        <p>
          <a target="_blank" :href="row.floor_plan.link"><img class="floorplan-pdf" :src="row.floor_plan.preview" /></a>
        </p>
      </div>
    </b-modal>
    <b-modal id="mlspop" hide-header hide-footer size="lg" body-class="p-0" @hidden="mlsHistory = null">
      <SummaryPopUp :data="unitHistoryData" :mlsHistory="mlsHistory" :reportHistoryData="unitHistoryData"
        :isMarketStrata="isMarketStrata" title="Unit History" @close="$bvModal.hide('mlspop')" />
    </b-modal>


    <transition name="cp-modal">
      <div v-if="unitReportModal" class="cp-overlay" @click.self="unitReportModal = false">
        <div class="cp-dialog cp-dialog--wide">
          <div class="cp-header">
            <div class="cp-header-left">
              <span class="cp-icon">
                <font-awesome-icon :icon="['fas', 'chart-bar']" />
              </span>
              <span class="cp-title">Unit Report Summary</span>
            </div>
            <div class="cp-header-actions">
              <button class="cp-action-btn" @click="exportSummary">
                <font-awesome-icon :icon="['fas', 'file-export']" />
                Export CSV
              </button>
              <button class="cp-close" @click="unitReportModal = false" aria-label="Close">
                <font-awesome-icon :icon="['fas', 'times']" />
              </button>
            </div>
          </div>
          <div class="cp-body">
            <div class="cp-section-title">Projects</div>
            <div class="cp-table-wrap">
              <table class="cp-table">
                <thead>
                  <tr>
                    <th v-for="h in projectsHeader" :key="h.key" :class="h.key === 'name' ? '' : 'cp-num'">
                      {{ h.label }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, idx) in projects" :key="'proj-' + idx">
                    <td v-for="h in projectsHeader" :key="h.key" :class="h.key === 'name' ? 'cp-city-name' : 'cp-num'">
                      <template v-if="h.key === 'net_psf'">
                        {{ haveValue(row[h.key]) ? `$${Number(row[h.key]).toLocaleString()}` : '-' }}
                      </template>
                      <template v-else>
                        {{ getDefaultValue({ value: row[h.key], field: h }) }}
                      </template>
                    </td>
                  </tr>
                  <tr v-if="!projects.length" class="cp-empty-row">
                    <td :colspan="projectsHeader.length">No data available</td>
                  </tr>
                </tbody>
                <tfoot v-if="projects.length">
                  <tr class="cp-total-row">
                    <td class="cp-city-name">Total / Average</td>
                    <td class="cp-num">{{ getUnitReportSummaryAverage.units.toLocaleString() }}</td>
                    <td class="cp-num">{{ getUnitReportSummaryAverage.size.toLocaleString() }}</td>
                    <td class="cp-num">${{ getUnitReportSummaryAverage.list_price.toLocaleString() }}</td>
                    <td class="cp-num">{{ kpiAvgPsf }}</td>
                    <td class="cp-num">{{ kpiAvgNet }}</td>
                    <td class="cp-num">{{ kpiAvgNetPsf }}</td>
                  </tr>
                </tfoot>
              </table>
            </div>

            <div class="cp-section-title cp-section-title--spaced">Unit Mix</div>
            <div class="cp-table-wrap">
              <table class="cp-table">
                <thead>
                  <tr>
                    <th v-for="h in previewHeader" :key="h.key" :class="h.key === 'Type' ? '' : 'cp-num'">
                      {{ h.label }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, idx) in sortedGroupedBeds" :key="'gb-' + idx">
                    <td v-for="h in previewHeader" :key="h.key" :class="h.key === 'Type' ? 'cp-city-name' : 'cp-num'">
                      <template v-if="h.key === 'Type'">
                        <b style="font-weight: 700">{{ row.Type.split(' ')[0] }}
                          <span>{{ row.Type.split(' ').slice(1).join(' ') }}</span></b>
                      </template>
                      <template v-else-if="h.key === 'view'">
                        <button class="cp-view-btn" @click="setUnits(row['Units'], idx)">View Unit Mix</button>
                      </template>
                      <template v-else>
                        {{ row[h.key] }}
                      </template>
                    </td>
                  </tr>
                  <tr v-if="!groupedBeds.length" class="cp-empty-row">
                    <td :colspan="previewHeader.length">No data available</td>
                  </tr>
                </tbody>
                <tfoot v-if="groupedBeds.length">
                  <tr class="cp-total-row">
                    <td class="cp-city-name">Total / Average</td>
                    <td class="cp-num"></td>
                    <td class="cp-num"></td>
                    <td class="cp-num">{{ getGroupedBedsAverage.units.toLocaleString() }}</td>
                    <td class="cp-num"></td>
                    <td class="cp-num">{{ haveValue(getGroupedBedsAverage.conf_sold) ?
                      Math.round(getGroupedBedsAverage.conf_sold) : '-' }}</td>
                    <td class="cp-num"></td>
                    <td class="cp-num">{{ getGroupedBedsAverage.size.toLocaleString() }}</td>
                    <td class="cp-num">${{ getGroupedBedsAverage.list_price.toLocaleString() }}</td>
                    <td class="cp-num">${{ isMarketStrata ? Math.round(getGroupedBedsAverage.psf).toLocaleString() :
                      getGroupedBedsAverage.psf.toFixed(2) }}</td>
                    <td class="cp-num">${{ getGroupedBedsAverage.net_price.toLocaleString() }}</td>
                    <td class="cp-num">${{ isMarketStrata ? Math.round(getGroupedBedsAverage.net_psf).toLocaleString() :
                      getGroupedBedsAverage.net_psf.toFixed(2) }}</td>
                    <td class="cp-num"></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <!-- ─── City Breakdown Chart Popup ─── -->
    <transition name="cp-modal">
      <div v-if="chartModal" class="cp-overlay" @click.self="closeCityChart">
        <div class="cp-dialog cp-dialog--wide rpd-chart-dialog">
          <div class="cp-header">
            <div class="cp-header-left">
              <span class="cp-icon">
                <font-awesome-icon :icon="['fas', 'chart-bar']" />
              </span>
              <span class="cp-title">Breakdown by City · Per Quarter</span>
            </div>
            <div class="cp-header-actions">
              <button type="button"
                :class="['rpd-chart-citiesbtn', { 'rpd-chart-citiesbtn--on': showCityControls }]"
                @click="showCityControls = !showCityControls">
                <font-awesome-icon :icon="['fas', 'sliders-h']" />
                Cities &amp; Colors
              </button>
              <div class="rpd-chart-typetoggle">
                <button type="button"
                  :class="['rpd-chart-typebtn', { 'rpd-chart-typebtn--on': chartType === 'bar' }]"
                  @click="chartType = 'bar'">Bar</button>
                <button type="button"
                  :class="['rpd-chart-typebtn', { 'rpd-chart-typebtn--on': chartType === 'line' }]"
                  @click="chartType = 'line'">Line</button>
              </div>
              <button class="cp-close" @click="closeCityChart" aria-label="Close">
                <font-awesome-icon :icon="['fas', 'times']" />
              </button>
            </div>
          </div>
          <div class="cp-body">
            <template v-if="cityQuarterChartData.quarters.length && cityQuarterChartData.cities.length">
              <div v-if="showCityControls" class="rpd-chart-controls">
                <div class="rpd-chart-controls-head">
                  <span class="rpd-chart-controls-title">Select cities &amp; colors</span>
                  <button type="button" class="rpd-chart-reset" @click="resetChartCities">
                    Reset
                  </button>
                </div>
                <div class="rpd-chart-controls-list">
                  <label v-for="city in cityQuarterChartData.allCities" :key="city"
                    class="rpd-chart-cityrow">
                    <input type="checkbox" class="rpd-chart-citycheck"
                      :checked="isCitySelected(city)" @change="toggleChartCity(city)" />
                    <span class="rpd-chart-cityname">{{ city }}</span>
                    <input type="color" class="rpd-chart-colorinput"
                      :value="cityColor(city)"
                      :disabled="!isCitySelected(city)"
                      @input="setCityColor(city, $event.target.value)" />
                  </label>
                </div>
              </div>
              <div class="rpd-chart-legend">
                <span v-for="l in chartLegend" :key="l.city" class="rpd-chart-legenditem">
                  <span class="rpd-chart-legenddot" :style="{ background: l.color }"></span>{{ l.city }}
                </span>
              </div>
              <div class="rpd-chart-grid">
                <div v-for="m in cityQuarterChartData.metrics" :key="m.key" class="rpd-chart-card">
                  <div class="rpd-chart-cardtitle">{{ m.label }}</div>
                  <div class="rpd-chart-canvas-wrap">
                    <canvas ref="cityChartCanvas"></canvas>
                  </div>
                </div>
              </div>
              <div v-if="cityQuarterChartData.totalCities > cityQuarterChartData.cities.length"
                class="rpd-chart-note">
                Showing top {{ cityQuarterChartData.cities.length }} of
                {{ cityQuarterChartData.totalCities }} cities by unit count.
              </div>
            </template>
            <div v-else class="rpd-chart-empty">No data available for the current filters.</div>
          </div>
        </div>
      </div>
    </transition>

    <!-- style="position: absolute; top:-20rem;" -->
    <ReportFooter id="erl-div" :userData="user_data" style="position: absolute; top: -20rem" />
  </div>
</template>
<script>
import DatePicker from "vue2-datepicker";
import "vue2-datepicker/index.css";
import { BACKEND_URL } from "../variables.js";
import {
  BTabs,
  BTab,
  BPopover,
  BPopoverDirective,
  BModal,
  BModalDirective,
  VBModal,
  BIconFileEarmarkXFill,
} from "bootstrap-vue";
import Treeselect from "@riophae/vue-treeselect";
import "@riophae/vue-treeselect/dist/vue-treeselect.css";
import AreaSelectorTree from "./common/AreaSelectorTree.vue";
import { cloneDeep } from "lodash";
import { useRequestCounter } from "../store/RequestCounter";
import draggable from "vuedraggable";
import RpdTable from "./common/RpdTable.vue";
import { useUserStore } from "../store/UserStore";
import { useUserDataStore } from "../store/UserDataStore";
import { mapState } from "pinia";
import { BBadge } from "bootstrap-vue";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { BSpinner } from "bootstrap-vue";
import ReportFooter from "./common/ReportFooter.vue";
import { text } from "@fortawesome/fontawesome-svg-core";
import html2canvas from "html2canvas";
import PageLoader from "../components/common/PageLoader.vue";
import SummaryPopUp from "./common/SummaryPopUp.vue";
import axios from "axios";
import MlsTable from "./RpdTables/MlsTable.vue";
import MlsReportTable from "./RpdTables/MlsReportTable.vue";
import {
  sortedCities,
  nFraserOptions,
  sFraserOptions,
  vanCities,
  okaCities,
  BC_REGION_CITIES,
  EAST_COAST_CITIES,
  getAlbertaCities,
  INVESTOR_RENTAL_MIDS,
} from "../composables/cities.js";
import { projectStatusLabel } from "../data/utcFieldOptions";
import ProjectMap from "./common/ProjectMap.vue";
import AvailablePricing from "../components/common/AvailablePricing.vue";
import RpdDoubleSlider from "./common/RpdDoubleSlider.vue";
import { saveView, loadView } from "../composables/asyncView.js";
import FpSpecsFilter from "../shared/components/FpSpecsFilter.vue";
import DateSlider from "./filters/DateSlider.vue";
import FieldsPanel from "./filters/FieldsPanel.vue";
import {
  Chart as ChartJS,
  Title as ChartTitle,
  Tooltip as ChartTooltip,
  Legend as ChartLegend,
  BarElement,
  BarController,
  LineElement,
  LineController,
  PointElement,
  CategoryScale,
  LinearScale,
  Filler,
} from "chart.js";

ChartJS.register(
  ChartTitle,
  ChartTooltip,
  ChartLegend,
  BarElement,
  BarController,
  LineElement,
  LineController,
  PointElement,
  CategoryScale,
  LinearScale,
  Filler
);

// Metrics available in the "Breakdown by city per quarter" chart. `get` pulls
// the raw numeric value from an enriched MLS row (mirroring the KPI computeds),
// and `fmt` renders it for tooltips (strata = whole $, rental = 2 decimals).
const CHART_METRICS = [
  {
    key: "size",
    label: "Avg Size",
    get: (r) => Number(r.field_mls_unit_size_value),
    fmt: (v) => `${Math.round(v).toLocaleString()} sf`,
  },
  {
    key: "price",
    label: "Avg Price",
    get: (r) => Number(r.field_mls_list_price_value),
    fmt: (v) => `$${Math.round(v).toLocaleString()}`,
  },
  {
    key: "psf",
    label: "Avg $PSF",
    get: (r) => Number(String(r.psf).replace(/,/g, "")),
    fmt: (v, strata) =>
      strata ? `$${Math.round(v).toLocaleString()}` : `$${v.toFixed(2)}`,
  },
  {
    key: "netPrice",
    label: "Avg Net Price",
    get: (r) => (r.net_price !== "-" ? Number(r.net_price) : NaN),
    fmt: (v) => `$${Math.round(v).toLocaleString()}`,
  },
  {
    key: "netPsf",
    label: "Avg Net $PSF",
    get: (r) => Number(String(r.custom_net_psf).replace(/,/g, "")),
    fmt: (v, strata) =>
      strata ? `$${Math.round(v).toLocaleString()}` : `$${v.toFixed(2)}`,
  },
];

// One colour per city series; wraps if more cities than colours.
const CHART_PALETTE = [
  "#343376", "#1F9D55", "#FC5868", "#F5A623", "#2D9CDB",
  "#8E44AD", "#00A3A3", "#E67E22", "#12283F", "#EB5757",
];

const ALL_FIELDS = [
  { id: 'project', label: 'Project & Developer', required: true },
  { id: 'location', label: 'Location' },
  { id: 'unit', label: 'Unit #' },
  { id: 'type', label: 'Type' },
  { id: 'baths', label: 'Baths' },
  { id: 'size', label: 'Size' },
  { id: 'listPrice', label: 'List Price' },
  { id: 'psf', label: '$PSF' },
  { id: 'incentives', label: 'Incentives' },
  { id: 'netPrice', label: 'Net Price' },
  { id: 'netPsf', label: 'Net $PSF' },
  { id: 'priceDate', label: 'Price Date' },
  { id: 'unitStatus', label: 'Unit Status' },
  { id: 'projectStatus', label: 'Project Status' },
  { id: 'listing', label: 'Listing' },
  { id: 'planName', label: 'Plan Name', off: true },
  { id: 'age', label: 'Age', off: true },
];

// Field set for the Investor Rental tab. Same column vocabulary as ALL_FIELDS
// (both resolve through MlsReportTable.colDef), minus the incentive-derived
// columns the rental endpoint does not populate, plus the rental-only extras.
// Kept as its own list so switching tabs swaps the whole layout rather than
// leaving stale strata columns on screen.
const INVESTOR_FIELDS = [
  { id: 'project', label: 'Project & Developer', required: true },
  { id: 'location', label: 'Location' },
  { id: 'unit', label: 'Unit #' },
  { id: 'type', label: 'Type' },
  { id: 'baths', label: 'Baths' },
  { id: 'size', label: 'Size' },
  { id: 'viewOrientation', label: 'View' },
  { id: 'listPrice', label: 'List Price' },
  { id: 'psf', label: '$PSF' },
  { id: 'netPsf', label: 'Net $PSF' },
  { id: 'priceDate', label: 'Price Date' },
  { id: 'unitStatus', label: 'Unit Status' },
  { id: 'projectStatus', label: 'Project Status' },
  { id: 'soldDate', label: 'Leased Date' },
  { id: 'listing', label: 'Listing', off: true },
  { id: 'netPrice', label: 'Net Price', off: true },
  { id: 'balcony', label: 'Balcony', off: true },
  { id: 'balconyArea', label: 'Balcony Area', off: true },
  { id: 'parking', label: 'Parking', off: true },
  { id: 'stalls', label: 'Stalls', off: true },
];

// How the two reports this page hosts are identified on the route. The active
// tab follows ?rpt rather than a local flag — mirroring MarketOverview.vue's
// Project/Planning tabs — so a tab is directly linkable and Back walks them.
const REPORT_QUERY_KEY = 'rpt';
const INVESTOR_REPORT_VALUE = 'investor';

export default {
  components: {
    FpSpecsFilter,
    DatePicker,
    "b-tabs": BTabs,
    "b-tab": BTab,
    "b-popover": BPopover,
    "b-modal": BModal,
    "b-badge": BBadge,
    "b-spinner": BSpinner,
    Treeselect,
    AreaSelectorTree,
    draggable,
    RpdTable,
    ReportFooter,
    PageLoader,
    SummaryPopUp,
    MlsTable,
    MlsReportTable,
    AvailablePricing,
    RpdDoubleSlider,
    DateSlider,
    FieldsPanel,
    ProjectMap,
  },
  directives: {
    "b-popover": BPopoverDirective,
    "b-modal": VBModal,
  },
  data() {
    return {
      isBusy: false,
      selectedFpSpecs: [],
      field_selected: [],
      custom_fields: [],
      rangeValue: {
        min: 0,
        max: 10000,
        minValue: 0,
        maxValue: 10000,
        startDate: null,
        endDate: null,
      },
      rangeValueSoldDate: {
        min: 0,
        max: 10000,
        minValue: 0,
        maxValue: 10000,
        startDate: null,
        endDate: null,
      },
      enablePricedate: false,
      enableSoldDate: false,
      currentAvailability: null,
      sortedCities: sortedCities,
      sFraserOptions: sFraserOptions,
      nFraserOptions: nFraserOptions,
      vanCities: vanCities,
      okaCities,
      okaCities,
      custom_filter: {
        soldDate: {
          soldDate_selected: "",
        },
        priceDate: {
          priceDate_selected: "180",
        },
        status: {
          status_selected: [],
          status_options: [],
        },
        city: {
          city_selected: [],
          city_options: [],
        },
        beds: {
          beds_selected: [],
          beds_options: [
            { id: "bed_Studio", label: "Studio" },
            { id: "bed_Jr 1-bed", label: "Jr 1-bed" },
            { id: "bed_1-bed", label: "1-bed" },
            { id: "bed_1-bed + Jr Bed", label: "1-bed + Jr Bed" },
            { id: "bed_2-bed", label: "2-bed" },
            { id: "bed_2-bed + Jr Bed", label: "2-bed + Jr Bed" },
            { id: "bed_3-bed", label: "3-bed" },
            { id: "bed_3-bed + Jr Bed", label: "3-bed + Jr Bed" },
            { id: "bed_4-bed", label: "4-bed" },
          ],
        },
        baths: {
          baths_selected: [],
          baths_options: [
            { id: "bath_1", label: "1" },
            { id: "bath_1.5", label: "1.5" },
            { id: "bath_2", label: "2" },
            { id: "bath_2.5", label: "2.5" },
            { id: "bath_3", label: "3" },
            { id: "bath_3.5", label: "3.5" },
            { id: "bath_4", label: "4" },
            { id: "bath_4.5", label: "4.5" },
            { id: "bath_5", label: "5" },
          ],
        },
        fpSpecs: {
          fpSpecs_selected: [],
          fpSpecs_options: [
            { id: "fp_Bonus Room", label: "Bonus Room" },
            { id: "fp_Den", label: "Den" },
            { id: "fp_Den(2)", label: "Den(2)" },
            { id: "fp_Furnished", label: "Furnished" },
            { id: "fp_Jr Bed(2)", label: "Jr Bed(2)" },
            { id: "fp_Jr Bed", label: "Jr Bed" },
            { id: "fp_Live/Work", label: "Live/Work" },
            { id: "fp_Lock-Off", label: "Lock-Off" },
            { id: "fp_Loft", label: "Loft" },
            { id: "fp_PH", label: "PH" },
            { id: "fp_Roof Deck", label: "Roof Deck" },
            { id: "fp_Sub-PH", label: "Sub-PH" },
            { id: "fp_TH", label: "TH" },
          ],
        },
        listingType: {
          listingType_selected: [],
          listingType_options: [],
        },
        projectStatus: {
          projectStatus_selected: [],
          projectStatus_options: [],
        },
      },
      beds_options: [],
      baths_selected: [],
      baths_options: [
        { id: "bath_1", label: "Bath - 1" },
        { id: "bath_1.5", label: "Bath - 1.5" },
        { id: "bath_2", label: "Bath - 2" },
        { id: "bath_2.5", label: "Bath - 2.5" },
        { id: "bath_3", label: "Bath - 3" },
        { id: "bath_3.5", label: "Bath - 3.5" },
        { id: "bath_4", label: "Bath - 4" },
        { id: "bath_4.5", label: "Bath - 4.5" },
        { id: "bath_5", label: "Bath - 5" },
      ],
      filtered_data: [],
      isLoading: false,
      requestCounter: useRequestCounter(),
      rowOptions: [
        { value: 50, text: "50 rows" },
        { value: 100, text: "100 rows" },
        { value: 200, text: "200 rows" },
        { value: 300, text: "300 rows" },
        { value: 500, text: "500 rows" },
      ],
      projectSelected: 0,
      arrayOfImages: [],
      loading: false,
      query: this.$route.query,
      unitHistoryData: {},
      mlsData: [],
      // Base filter query string (city/type/status/date/unittype …) shared by the
      // full load and the table's server-side infinite scroll. Ends with "&".
      baseQueryParam: "",
      // Progressive load cursor: getMLSDetail fetches this offset (200 rows per
      // page) and appends when it advances. mlsTotal caps how far we page.
      mlsOffset: 0,
      mlsTotal: 0,
      // Drives the full-page PageLoader — only true during the initial (offset
      // 0) load, so appended pages on scroll don't re-trigger it.
      initialLoading: false,
      sortBy: "sort-uname",
      orderAsc: true,
      mlsHistory: [],
      currentDate: new Date(),
      filterSearch: null,
      filter_rows: {
        filterText: "",
        filterArray: [],
      },
      filter_options: [
        {
          id: "cs",
          label: "Construction Status",
          children: [
            {
              id: "cs_Pre-Construction",
              label: "Pre-Construction",
            },
            {
              id: "cs_Excavation & Foundation",
              label: "Excavation & Foundation",
            },
            {
              id: "cs_Framing",
              label: "Framing",
            },
            {
              id: "cs_Interior Finishing",
              label: "Interior Finishing",
            },
            {
              id: "cs_Some Homes Complete",
              label: "Some Homes Complete",
            },
            {
              id: "cs_Complete",
              label: "Complete",
            },
          ],
        },
        {
          id: "pt",
          label: "Project Type",
          children: [
            {
              id: "pt_Apartment",
              label: "Apartment",
            },
            {
              id: "pt_Mixed-Use Apartment",
              label: "Mixed-Use Apartment",
            },
            {
              id: "pt_Townhome",
              label: "Townhome",
            },
            {
              id: "pt_Stacked Townhome",
              label: "Stacked Townhome",
            },
          ],
        },
        {
          id: "pdt",
          label: "Product Type",
          children: [
            {
              id: "pdt_Concrete",
              label: "Concrete",
            },
            {
              id: "pdt_Wood Frame",
              label: "Wood Frame",
            },
            {
              id: "pdt_Townhome",
              label: "Townhome",
            },
            {
              id: "pdt_Stacked Townhome",
              label: "Stacked Townhome",
            },
          ],
        },
      ],
      fields: [],
      list2: [
        { label: "Balcony", key: "balcony", sortable: true },
        { label: "Balcony Area", key: "balcony_area", sortable: true },
        { label: "View Orientation", key: "view_orientation", sortable: true },
        { label: "Parking", key: "parking" },
        { label: "Stalls", key: "stalls" },
        { label: "Sold Date", key: "sold_date", sortable: true },
        {
          label: "Sold Price",
          key: "sold_price",
          formatter: (value) => {
            return Number(value)
              ? `$${Math.round(value).toLocaleString()}`
              : "-";
          },
          sortable: true,
        },
        {
          label: "Construction Status",
          key: "construction_status",
          sortable: true,
        },
        { label: "Product Type", key: "product_type", sortable: true },
        { label: "Neighbourhood", key: "neighbour", sortable: true },
        { label: "Price Status", key: "price_backup", sortable: true },
        { label: "Occupancy Date", key: "occupancy_date", sortable: true },
      ],
      custom_header_field: false,
      cityOpen: false,
      moreFiltersOpen: false,
      typeOpen: false,
      bathsOpen: false,
      featuresOpen: false,
      shownFields: ALL_FIELDS.filter(f => !f.off).map(f => ({ ...f })),
      availableFields: ALL_FIELDS.filter(f => f.off).map(f => ({ ...f })),
      // ── Investor Rental tab ──────────────────────────────────────────────
      // Rows from api/get_rental_mls_list_mid, held separately from mlsData so
      // switching tabs doesn't discard the other report's fetched rows.
      investorData: [],
      investorLoaded: false,
      // Loading state for a tab switch. Distinct from `initialLoading`, which
      // drives the full-page PageLoader: switching tabs only swaps the table's
      // data source, so the indicator belongs in the table region rather than
      // over the whole page (mirrors MarketOverview.vue's .ms-state block).
      tableLoading: false,
      // The Investor tab spans the whole BC region and needs its own area
      // filter; the Unit Report is already scoped by the caller's city[] params.
      citySelected: [],
      mapView: false,
      selectedListingUid: null,
      listingSortField: "total_net_psf",
      listingSortDir: "desc",
      currentAvailability: null,
      allSelected: false,
      filteredReportDetail: [],
      selectedSummary: [],
      unitReportModal: false,
      // City-per-quarter breakdown chart popup (small multiples: one chart per metric)
      chartModal: false,
      chartType: "bar",
      chartCityLimit: 8,
      showCityControls: false,
      chartSelectedCities: [], // explicit city picks; empty = auto top-N
      chartCityColors: {}, // city name -> hex colour override
      chartInstances: [],
      chartThemeObserver: null,
      totalRows: 1,
      currentPage: 1,
      perPage: 50,
      pageOptions: [100, 200, 300, 400, 500],
      useUserDataStore: useUserDataStore(),
      projectsHeader: [
        {
          key: "name",
          label: "Projects",
          thClass: "text-left mw-400p",
          tdClass: "text-left",
          sortable: true,
        },
        { key: "units", label: "Units", sortable: true },
        {
          key: "size",
          label: "Size",
          tdClass: "bg-t-data-2",
          sortable: true,
        },
        {
          key: "list_price",
          label: "List Price",
          tdClass: "bg-t-data-1",
          sortable: true,
        },
        { key: "psf", label: "$PSF", tdClass: "bg-t-data-1", sortable: true },
        {
          key: "net_price",
          label: "Net Price",
          tdClass: "bg-t-data-1",
          sortable: true,
        },
        {
          key: "net_psf",
          label: "Net $PSF",
          tdClass: "bg-t-data-1",
          sortable: true,
        },
      ],
      previewHeader: [
        { key: "Type", label: "Type", sortable: true },
        { key: "Baths", label: "Baths", sortable: true },
        { key: "Pkg", label: "Pkg", sortable: true },
        { key: "# Units", label: "Units", sortable: true },
        { key: "Units %", label: "Units %", sortable: true },
        { key: "Conf Sold", label: "Conf Sold", sortable: true },
        { key: "Sold %", label: `${this.isMarketRental ? 'Leased %' : 'Sold %'}`, sortable: true },
        {
          key: "Avg Size",
          label: "Avg Size",
          tdClass: "bg-t-data-2",
          sortable: true,
        },
        {
          key: "List Price",
          label: "List Price",
          tdClass: "bg-t-data-1",
          sortable: true,
        },
        {
          key: "$PSF",
          label: "$PSF",
          tdClass: "bg-t-data-1",
          sortable: true,
        },
        {
          key: "Net Price",
          label: "Net Price",
          tdClass: "bg-t-data-1",
          sortable: true,
        },
        {
          key: "Net $PSF",
          label: "Net $PSF",
          tdClass: "bg-t-data-1",
          sortable: true,
        },
        { key: "view", label: "" },
      ],
    };
  },
  methods: {
    // ── Report tabs ────────────────────────────────────────────────────────
    // A switch changes the report and nothing else the caller set: the field
    // layout and filter vocabularies follow the target report (see the
    // isInvestorReport watcher), while the route keeps the scope params so
    // switching back lands on the same Unit Report.
    switchReport(target) {
      const wantInvestor = target === INVESTOR_REPORT_VALUE;
      if (wantInvestor === this.isInvestorReport) return;

      const query = { ...this.$route.query };
      if (wantInvestor) {
        query[REPORT_QUERY_KEY] = INVESTOR_REPORT_VALUE;
        // The Investor report's scope is the curated mid list, not the caller's
        // project selection — seed it when the URL doesn't already carry one.
        if (!query["mids[]"]) query["mids[]"] = INVESTOR_RENTAL_MIDS;
      } else {
        delete query[REPORT_QUERY_KEY];
      }
      this.$router.push({ path: this.$route.path, query }).catch((e) => {
        if (!e || e.name !== "NavigationDuplicated")
          console.error("Report switch navigation failed", e);
      });
    },

    // The field list for the active report — the Fields panel and the table's
    // columns both read from it.
    reportFieldSet() {
      return this.isInvestorReport ? INVESTOR_FIELDS : ALL_FIELDS;
    },
    applyReportFields() {
      const all = this.reportFieldSet();
      this.shownFields = all.filter(f => !f.off).map(f => ({ ...f }));
      this.availableFields = all.filter(f => f.off).map(f => ({ ...f }));
    },
    // Clears the selections whose vocabularies differ between the two reports,
    // so a switch can't leave e.g. "Presale" selected on the rental tab.
    resetReportFilters() {
      this.filter_rows.filterText = "";
      this.filter_rows.filterArray = [];
      this.custom_filter.status.status_selected = [];
      this.custom_filter.listingType.listingType_selected = [];
      this.custom_filter.projectStatus.projectStatus_selected = [];
      this.custom_filter.city.city_selected = [];
      this.custom_filter.beds.beds_selected = [];
      this.custom_filter.baths.baths_selected = [];
      this.custom_filter.fpSpecs.fpSpecs_selected = [];
      this.citySelected = [];
      this.enablePricedate = false;
      this.enableSoldDate = false;
    },

    // ── Investor Rental data ───────────────────────────────────────────────
    // Ported from the standalone rental report. The endpoint matches mids
    // flatly and caps how many it takes per call, so the mid list goes out in
    // batches of 5 and the responses are concatenated.
    // `inTable` keeps the indicator inside the table region instead of raising
    // the full-page loader — the shape a tab switch wants, since the rest of the
    // page (header, tabs, filters) stays put and only the rows change.
    async fetchInvestorData({ inTable = false } = {}) {
      const vm = this;
      if (this.isLoading) return;
      this.isLoading = true;
      if (inTable) this.tableLoading = true;
      else this.initialLoading = true;
      this.requestCounter.resetCounter();
      this.requestCounter.setRequestCounter(1);

      const MID_BATCH_SIZE = 5;
      const q = this.$route.query;
      const rawMids = q["mids[]"];
      const mids = Array.isArray(rawMids)
        ? rawMids
        : rawMids
          ? [rawMids]
          : INVESTOR_RENTAL_MIDS;
      const rawStatus = q["status[]"];
      const statusList = Array.isArray(rawStatus)
        ? rawStatus
        : rawStatus
          ? [rawStatus]
          : [];
      const statusQuery = statusList
        .map((s) => `status%5B%5D=${encodeURIComponent(s)}`)
        .join("&");

      const midBatches = [];
      for (let i = 0; i < mids.length; i += MID_BATCH_SIZE) {
        midBatches.push(mids.slice(i, i + MID_BATCH_SIZE));
      }

      const calcNetPrice = (d) => {
        const res = Number(d.field_mls_list_price_value - d.buyer_incentives);
        return res > 0 ? res : "-";
      };
      // The rental feed carries a per-unit history in mls_rental_data: the last
      // entry is the current state, the one before it the previous — which is
      // what the price-date fallback reads.
      const previousEntry = (d, key) => {
        const rental = d.mls_rental_data || [];
        return rental.length > 1 ? rental[rental.length - 2][key] : null;
      };

      try {
        const responses = await Promise.all(
          midBatches.map((batch) => {
            const midQuery = batch
              .map((mid) => `mids%5B%5D=${encodeURIComponent(mid)}`)
              .join("&");
            return axios.get(
              `${BACKEND_URL}api/get_rental_mls_list_mid?${midQuery}${statusQuery ? "&" + statusQuery : ""}`
            );
          })
        );
        const rawData = responses.flatMap((res) =>
          Array.isArray(res.data) ? res.data : []
        );

        this.investorData = rawData.map((mlsRow) => {
          const netPrice = calcNetPrice(mlsRow);

          let latest = {
            list_price: null,
            price_date: null,
            sold_date: null,
            sold_price: null,
            status: null,
          };
          const rentalData = mlsRow.mls_rental_data || [];
          if (rentalData.length) latest = rentalData[rentalData.length - 1];

          const pick = (v, fallback) => (vm.isRealValue(v) ? v : fallback);
          const listPrice = pick(latest.list_price, previousEntry(mlsRow, "list_price"));
          const priceDate = pick(latest.price_date, previousEntry(mlsRow, "price_date"));
          const mlsStatus = pick(latest.status, mlsRow.status);
          const bed = pick(latest.bed, mlsRow.field_mls_beds_value);
          const bath = pick(latest.bath, mlsRow.field_mls_baths_value);
          const unitSize = pick(latest.unitSize, mlsRow.field_mls_unit_size_value);

          const resolvedListPrice = vm.isRealValue(listPrice)
            ? listPrice
            : mlsRow.field_mls_list_price_value;
          const psf = Number(resolvedListPrice / unitSize);
          const incentives = Number(mlsRow.buyer_incentives) || 0;
          const netPsf = unitSize
            ? Number((resolvedListPrice - incentives) / unitSize)
            : NaN;

          return {
            ...mlsRow,
            // MlsReportTable indexes rows on entity_id; the rental feed keys
            // units by eid.
            entity_id: mlsRow.eid,
            field_mls_list_price_value: resolvedListPrice,
            field_mls_beds_value: bed,
            field_mls_baths_value: bath,
            field_mls_unit_size_value: unitSize,
            parking: pick(latest.parking, mlsRow.parking),
            view_orientation: pick(latest.orientation, mlsRow.view_orientation),
            fp: Array.isArray(latest.fp) ? latest.fp : mlsRow.fp,
            pr: pick(latest.pr, mlsRow.pr),
            backup_file: pick(latest.backup_file, mlsRow.backup_file),
            price_date: priceDate,
            sold_price: latest.sold_price || mlsRow.sold_price,
            sold_date: latest.sold_date || mlsRow.sold_date,
            // The shared table splits the two statuses across two columns:
            // status_name_name is the MLS listing status (it also drives the
            // Unit filter, the map pins and the list badges), `status` the
            // project lifecycle status.
            status_name_name: mlsStatus === "Sold" ? "Leased" : mlsStatus,
            status: vm.setProjectStatus(mlsRow.utc_status),
            customData: {
              project: mlsRow.developer,
              projectName: mlsRow.name,
              href_link: vm.investorUtcLink(mlsRow),
            },
            customLocationData: {
              address: mlsRow.address,
              municipality: mlsRow.city,
              neighbour: mlsRow.neighbour,
            },
            custom_field_mls_beds_value: {
              type:
                bed == "s" ? "Studio" : bed == "jr" ? "Jr 1-bed" : `${bed}-bed`,
              fp_specs:
                (mlsRow.fp_specs || "").toLocaleLowerCase() != "none"
                  ? mlsRow.fp_specs
                  : "",
            },
            custom_age: vm.getAge(mlsRow),
            // Rent is a small monthly figure, so both rates keep two decimals.
            // isMarketStrata is false on this tab, which is what the shared
            // column formatters key off.
            psf:
              vm.isRealValue(resolvedListPrice) && isFinite(psf)
                ? psf.toFixed(2)
                : "-",
            custom_net_psf:
              vm.isRealValue(resolvedListPrice) && isFinite(netPsf)
                ? netPsf.toFixed(2)
                : "-",
            net_price: netPrice,
            buyer_incentives: incentives
              ? Math.round(incentives).toLocaleString()
              : "-",
            custom_price_backup: mlsRow.price_backup,
            price_backup: mlsRow.price_backup,
            // Filled lazily per rendered page — one request each, see
            // fillInvestorListingTypes.
            custom_listing_type: null,
            rowSelected: false,
          };
        });

        this.investorLoaded = true;
        this._investorListingTypeDone = new Set();

        const priceDates = this.investorData
          .filter((d) => !["-", "", null].includes(d.price_date))
          .map((d) => new Date(d.price_date));
        const soldDates = this.investorData
          .filter((d) => !["-", "", null].includes(d.sold_date))
          .map((d) => new Date(d.sold_date));
        if (priceDates.length) {
          this.rangeValue.startDate = new Date(Math.min(...priceDates));
          this.rangeValue.endDate = new Date(Math.max(...priceDates));
        }
        if (soldDates.length) {
          this.rangeValueSoldDate.startDate = new Date(Math.min(...soldDates));
          this.rangeValueSoldDate.endDate = new Date(Math.max(...soldDates));
        }

        this.loadFilters();
      } catch (error) {
        console.error("fetchInvestorData: failed to fetch rental MLS list", error);
      } finally {
        this.isLoading = false;
        this.initialLoading = false;
        this.tableLoading = false;
        this.requestCounter.incrementCompletedRequest();
      }
    },

    // The rental feed exposes the UTC coordinates directly rather than under the
    // field_* names the strata rows use, so it needs its own link builder.
    investorUtcLink(row) {
      return `/type?project=${row.project}&fid=${row.fid}&uid=${row.uid}`;
    },

    // Project Status label from the stored taxonomy term id. On this tab the
    // sale-side terms read with lease wording.
    setProjectStatus(id) {
      const label = projectStatusLabel(id);
      if (!label) return "";
      const leaseWording = {
        2882: "Now Leasing",
        21135: "Now Leasing",
        2978: "Leased Out",
      };
      const key = String(id).trim();
      return Object.prototype.hasOwnProperty.call(leaseWording, key)
        ? leaseWording[key]
        : label;
    },

    // Listing type costs one api/get_mls_history call per unit, so it is filled
    // only for the rows on screen (watched via visibleRows) rather than for the
    // whole result set.
    async fillInvestorListingTypes(rows) {
      if (!this._investorListingTypeDone) this._investorListingTypeDone = new Set();
      const pending = (rows || []).filter(
        (r) => r && r.entity_id && !this._investorListingTypeDone.has(r.entity_id)
      );
      if (!pending.length) return;
      pending.forEach((r) => this._investorListingTypeDone.add(r.entity_id));

      const CONCURRENCY = 8;
      let idx = 0;
      const worker = async () => {
        while (idx < pending.length) {
          const row = pending[idx++];
          try {
            const type = await this.setListingType(row);
            this.$set(row, "custom_listing_type", type);
          } catch (error) {
            console.error(
              "fillInvestorListingTypes: get_mls_history failed for",
              row.entity_id,
              error
            );
          }
        }
      };
      await Promise.all(Array.from({ length: CONCURRENCY }, () => worker()));
    },

    // Average size / rent / $PSF across rows that actually carry a price —
    // drives the Investor tab's KPI cards.
    getInvestorAvailablePricing(data) {
      const priced = (data || []).filter(
        (item) =>
          item.field_mls_list_price_value &&
          !isNaN(Number(item.field_mls_list_price_value))
      );
      if (!priced.length) {
        this.currentAvailability = null;
        return;
      }
      const sums = priced.reduce(
        (acc, curr) => {
          acc.size += Number(curr.field_mls_unit_size_value) || 0;
          acc.list_price += Number(curr.field_mls_list_price_value) || 0;
          return acc;
        },
        { size: 0, list_price: 0 }
      );
      const size = sums.size / priced.length;
      const listPrice = sums.list_price / priced.length;
      this.currentAvailability = {
        units: priced.length,
        size,
        list_price: listPrice,
        psf: size > 0 ? Number(listPrice / size).toFixed(2) : null,
      };
    },

    // ── Investor map panel ─────────────────────────────────────────────────
    toggleMapView() {
      this.mapView = !this.mapView;
      // The table sits in a display:none wrapper while the map is up, so it
      // needs a redraw once it is measurable again.
      if (!this.mapView) {
        this.$nextTick(() => {
          const t = this.$refs.mlsTable;
          if (t && t.tabulator) t.tabulator.redraw(true);
        });
      }
    },
    toggleListingSort() {
      const order = ["total_net_psf", "field_mls_list_price_value", "name"];
      const idx = order.indexOf(this.listingSortField);
      this.listingSortField = order[(idx + 1) % order.length];
      this.listingSortDir = this.listingSortField === "name" ? "asc" : "desc";
    },
    selectListing(row) {
      // Toggle focus so re-clicking the same item still re-centres the marker.
      this.selectedListingUid =
        this.selectedListingUid === row.uid ? null : row.uid;
      this.$nextTick(() => (this.selectedListingUid = row.uid));
    },
    onMarkerSelect(uid) {
      this.selectedListingUid = uid;
    },
    openListingDetail(row) {
      this.popHistory(row);
    },
    statusColor(status) {
      const s = (status || "").toLowerCase();
      if (s.includes("now selling") || s.includes("now leasing")) return "#6C90B6";
      if (s.includes("selling soon") || s.includes("leasing soon")) return "#3C6289";
      if (s.includes("planning")) return "#244260";
      if (s.includes("sold out") || s.includes("leased out")) return "#A1BCD8";
      return "#9ca3af";
    },
    listingBadgeClass(status) {
      const s = (status || "").toLowerCase();
      let cls = "rpd-lp-badge";
      if (s.includes("now selling") || s.includes("now leasing")) cls += " rpd-lp-badge--active";
      else if (s.includes("selling soon") || s.includes("leasing soon")) cls += " rpd-lp-badge--soon";
      else if (s.includes("sold out") || s.includes("leased out")) cls += " rpd-lp-badge--out";
      else if (s.includes("planning")) cls += " rpd-lp-badge--planning";
      return cls;
    },
    formatPsf(v) {
      if (v == null || v === "" || v === "-" || isNaN(parseFloat(v))) return "—";
      return "$" + parseFloat(v).toLocaleString("en-CA", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    listingThumbUrl(row) {
      if (row.pic && row.pic.filename) {
        return `${BACKEND_URL}sites/default/files/${row.pic.filename}`;
      }
      return null;
    },
    listingThumbStyle(row) {
      const url = this.listingThumbUrl(row);
      if (url) return { backgroundImage: `url('${url}')` };
      const palette = {
        Apartment: ["#1B3553", "#0F2238"],
        "Mixed-Use Apartment": ["#26315F", "#181E3B"],
        "Stacked Townhome": ["#3F4B7A", "#222A4D"],
        Townhome: ["#4A5775", "#2C354D"],
        Rowhome: ["#4A5775", "#2C354D"],
        Multiplex: ["#5A4E76", "#332B4A"],
        Commercial: ["#163A3A", "#0B2222"],
        Industrial: ["#3A2E20", "#1F1810"],
      };
      const [a, b] = palette[row.project_type] || palette["Apartment"];
      return { background: `linear-gradient(160deg, ${a} 0%, ${b} 100%)` };
    },
    listingLocation(row) {
      return (
        [row.address, row.neighbour, row.city].filter(Boolean).join(" · ") || "—"
      );
    },
    listingBedType(row) {
      const bt = row.custom_field_mls_beds_value;
      return bt && bt.type ? bt.type : "";
    },
    // Resolves the Investor tab's area selection into the city / neighbourhood
    // labels the rows carry. A parent city node selects every city beneath it;
    // a sub-community node (subc_) matches on `neighbour` instead.
    resolveCityFilter(selectedIds) {
      const cities = new Set();
      const neighbours = new Set();
      const idSet = new Set(selectedIds);

      const walk = (nodes) => {
        for (const n of nodes) {
          if (idSet.has(n.id)) {
            if (n.id.startsWith("subc_")) {
              neighbours.add(n.label);
            } else {
              const collect = (node) => {
                if (node.id && node.id.startsWith("city_")) cities.add(node.label);
                if (node.children) node.children.forEach(collect);
              };
              collect(n);
            }
          }
          if (n.children) walk(n.children);
        }
      };

      walk(this.cityOptions);
      return { cities, neighbours };
    },

    getSegVal(group) {
      const map = {
        status: this.custom_filter.status.status_selected,
        listing: this.custom_filter.listingType.listingType_selected,
        projectStatus: this.custom_filter.projectStatus.projectStatus_selected,
      };
      const arr = map[group];
      return !arr || arr.length === 0 ? 'All' : arr[0];
    },
    setSegVal(group, val) {
      const map = {
        status: [this.custom_filter.status, 'status_selected'],
        listing: [this.custom_filter.listingType, 'listingType_selected'],
        projectStatus: [this.custom_filter.projectStatus, 'projectStatus_selected'],
      };
      const [filterGroup, key] = map[group];
      this.$set(filterGroup, key, val === 'All' ? [] : [val]);
    },
    setSingleBed(val) { this.$set(this.custom_filter.beds, 'beds_selected', val ? [val] : []); },
    setSingleBath(val) { this.$set(this.custom_filter.baths, 'baths_selected', val ? [val] : []); },
    // ── City-per-quarter breakdown chart ──────────────────────────────
    openCityChart() {
      this.chartModal = true;
      this.$nextTick(() => {
        this.renderAllCharts();
        this.startChartThemeObserver();
      });
    },
    closeCityChart() {
      this.chartModal = false;
      this.stopChartThemeObserver();
      this.destroyCityChart();
    },
    destroyCityChart() {
      if (this.chartInstances && this.chartInstances.length) {
        this.chartInstances.forEach((c) => c && c.destroy());
      }
      this.chartInstances = [];
    },
    // Colour for a city series: user override wins; otherwise fall back to the
    // palette, indexed by the city's position among the displayed cities (or,
    // if it isn't displayed, its position in the full city list).
    cityColor(city) {
      if (this.chartCityColors[city]) return this.chartCityColors[city];
      const { cities, allCities } = this.cityQuarterChartData;
      let idx = cities.indexOf(city);
      if (idx === -1) idx = (allCities || []).indexOf(city);
      if (idx < 0) idx = 0;
      return CHART_PALETTE[idx % CHART_PALETTE.length];
    },
    isCitySelected(city) {
      return this.cityQuarterChartData.cities.includes(city);
    },
    toggleChartCity(city) {
      // Seed from the currently displayed cities so unchecking one out of the
      // auto top-N keeps the rest visible instead of resetting to all.
      let sel = this.chartSelectedCities.length
        ? this.chartSelectedCities.slice()
        : this.cityQuarterChartData.cities.slice();
      const i = sel.indexOf(city);
      if (i === -1) sel.push(city);
      else sel.splice(i, 1);
      this.chartSelectedCities = sel;
    },
    setCityColor(city, color) {
      this.$set(this.chartCityColors, city, color);
    },
    resetChartCities() {
      this.chartSelectedCities = [];
      this.chartCityColors = {};
    },
    hexToRgba(hex, alpha) {
      const h = hex.replace("#", "");
      const r = parseInt(h.substring(0, 2), 16);
      const g = parseInt(h.substring(2, 4), 16);
      const b = parseInt(h.substring(4, 6), 16);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    },
    // Compact axis tick: size -> "1,234", psf -> "$X", price -> "$Xk/$X.XM".
    chartAxisTick(v, metric) {
      const n = Number(v);
      if (metric.key === "size") return Math.round(n).toLocaleString();
      if (metric.key === "psf" || metric.key === "netPsf") {
        return this.isMarketStrata
          ? `$${Math.round(n).toLocaleString()}`
          : `$${n.toFixed(0)}`;
      }
      if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
      if (n >= 1e3) return `$${Math.round(n / 1e3)}k`;
      return `$${Math.round(n)}`;
    },
    // Renders one chart per metric into the grid of canvases. The v-for that
    // outputs the canvases and cityQuarterChartData.metrics share the same order,
    // so canvas[idx] maps to metrics[idx].
    renderAllCharts() {
      this.destroyCityChart();

      const { quarters, metrics } = this.cityQuarterChartData;
      if (!quarters.length || !metrics.length) return;

      let canvases = this.$refs.cityChartCanvas;
      if (!canvases) return;
      const canvasArr = Array.isArray(canvases) ? canvases : [canvases];

      const isDark = document.documentElement.classList.contains("dark");
      const textColor = isDark ? "#C9CCD2" : "#545454";
      const gridColor = isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)";
      const strata = this.isMarketStrata;
      const isLine = this.chartType === "line";

      metrics.forEach((metricData, idx) => {
        const canvas = canvasArr[idx];
        if (!canvas) return;
        const metricDef =
          CHART_METRICS.find((m) => m.key === metricData.key) || CHART_METRICS[0];

        const datasets = metricData.series.map((s) => {
          const color = this.cityColor(s.city);
          return {
            label: s.city,
            data: s.values,
            backgroundColor: isLine ? color : this.hexToRgba(color, 0.85),
            borderColor: color,
            borderWidth: isLine ? 2 : 1,
            pointRadius: isLine ? 3 : 0,
            pointBackgroundColor: color,
            tension: 0.3,
            spanGaps: true,
            fill: false,
            maxBarThickness: 22,
          };
        });

        const chart = new ChartJS(canvas, {
          type: this.chartType,
          data: { labels: quarters, datasets },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            interaction: { mode: "index", intersect: false },
            plugins: {
              datalabels: { display: false },
              legend: { display: false },
              tooltip: {
                callbacks: {
                  label: (ctx) =>
                    ctx.parsed.y == null
                      ? `${ctx.dataset.label}: —`
                      : `${ctx.dataset.label}: ${metricDef.fmt(ctx.parsed.y, strata)}`,
                },
              },
            },
            scales: {
              x: { ticks: { color: textColor }, grid: { color: gridColor } },
              y: {
                beginAtZero: false,
                ticks: {
                  color: textColor,
                  callback: (val) => this.chartAxisTick(val, metricDef),
                },
                grid: { color: gridColor },
              },
            },
          },
        });
        this.chartInstances.push(chart);
      });
    },
    startChartThemeObserver() {
      this.stopChartThemeObserver();
      this.chartThemeObserver = new MutationObserver(() => {
        if (this.chartModal) this.renderAllCharts();
      });
      this.chartThemeObserver.observe(document.documentElement, {
        attributes: true,
        attributeFilter: ["class"],
      });
    },
    stopChartThemeObserver() {
      if (this.chartThemeObserver) {
        this.chartThemeObserver.disconnect();
        this.chartThemeObserver = null;
      }
    },
    getCitiesFromSelection(selectedIds, options) {
      const cities = [];
      const neighbours = [];
      const traverse = (nodes, forceCollect) => {
        nodes.forEach(node => {
          const pick = forceCollect || selectedIds.includes(node.id);
          if (node.id && node.id.startsWith('city_')) {
            if (pick) cities.push(node.label);
            if (node.children) traverse(node.children, false);
          } else if (node.id && node.id.startsWith('subc_')) {
            if (pick) neighbours.push(node.label);
          } else {
            if (node.children) traverse(node.children, pick);
          }
        });
      };
      traverse(options || [], false);
      return { cities, neighbours };
    },
    toggleCity(id) {
      const sel = this.custom_filter.city.city_selected;
      const idx = sel.indexOf(id);
      if (idx === -1) sel.push(id);
      else sel.splice(idx, 1);
    },
    toggleFilterChip(id) {
      const arr = this.filter_rows.filterArray;
      const idx = arr.indexOf(id);
      if (idx === -1) arr.push(id);
      else arr.splice(idx, 1);
    },
    toggleFpSpec(label) {
      const sel = this.custom_filter.fpSpecs.fpSpecs_selected;
      const idx = sel.indexOf(label);
      if (idx === -1) sel.push(label);
      else sel.splice(idx, 1);
    },
    toggleSoldDate() {
      this.enableSoldDate = !this.enableSoldDate;
      if (this.enableSoldDate && !this.custom_filter.soldDate.soldDate_selected) {
        this.$set(this.custom_filter.soldDate, 'soldDate_selected', '180');
      }
    },
    resetFields() {
      this.applyReportFields();
    },
    clearFields() {
      const required = this.shownFields.filter(f => f.required);
      const rest = [...this.shownFields.filter(f => !f.required), ...this.availableFields];
      this.shownFields = required;
      this.availableFields = rest;
    },
    removeField(f) {
      this.shownFields = this.shownFields.filter(x => x.id !== f.id);
      this.availableFields = [...this.availableFields, f];
    },
    addField(f) {
      this.availableFields = this.availableFields.filter(x => x.id !== f.id);
      this.shownFields = [...this.shownFields, f];
    },
    async processDataWithWorker(chunkData) {
      this.isBusy = true
      const worker = new Worker('src/workers/mlsProcessingWorker.js');

      await worker.postMessage({
        data: chunkData,
        BACKEND_URL: BACKEND_URL
      });

      worker.onmessage = (e) => {
        const processedResults = e.data;
        processedResults.forEach(processedRow => {
          const originalRow = this.mlsData.find(r => r.entity_id === processedRow.entity_id);
          if (originalRow) {
            this.$set(originalRow, 'custom_price_confidence', processedRow.custom_price_confidence);
            this.$set(originalRow, 'custom_listing_type', processedRow.custom_listing_type);
            this.$set(originalRow, 'priceGap', processedRow.priceGap);
          }
        });
        this.isBusy = false
        worker.terminate();
      };

      worker.onerror = (err) => {
        console.error("Worker Error:", err);
        worker.terminate();
      };
    },
    isRealValue(value) {
      return !["zero", "", "-", null, 0, undefined, "0", "null", NaN].includes(
        value
      );
    },
    getPriceConfidence(priceGap, mlsRow) {
      var priceConfidence = "Low";
      if (
        [mlsRow.field_mls_sold_date_value, mlsRow.field_mls_sold_price_value].every((data) =>
          this.isRealValue(data)
        )
      )
        return "Verified";
      if (priceGap <= 60 && priceGap > 0) {
        priceConfidence = "High";
      } else if (priceGap >= 61 && priceGap <= 120) {
        priceConfidence = "Medium";
      } else if (priceGap > 121) {
        priceConfidence = "Low";
      } else priceConfidence = "-";

      return priceConfidence;
    },
    getPriceGap(mlsRow) {
      const date1 = new Date(mlsRow.field_mls_price_date_value * 1000);
      const date2 = new Date(mlsRow.field_mls_sold_date_value * 100);
      const diffTime = Math.abs(date2 - date1);
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
      return ![NaN, 0].includes(diffDays) && mlsRow.mls_status == "Sold"
        ? diffDays
        : "-";
    },
    dataMapper(originalString) {
      return typeof originalString == 'string' ? originalString.replace(/[$,]/g, '') : originalString;
    },
    getDefaultValue(data) {
      let res = data.value;
      if (!this.haveValue(data.value)) return (res = "-");

      if (["zero"].includes(data.value)) return (res = 0);

      if (
        [
          "field_mls_list_price_value",
          "mls_list_price",
          "net_price",
          "buyer_incentives",
          "custom_net_sold_price",
          "list_price"
        ].includes(data.field.key)
      ) {
        res = `$${Math.round(parseInt(data.value)).toLocaleString("en-US")}`;
      }

      if (
        [
          "realtor_incentives",
          "total_avg_psf",
          "total_net_psf",
          "list_price_net",
          "psf",
          "custom_net_psf",
          "custom_net_sold_psf",
          "sold_price",
          "custom_net_sold_psf",
        ].includes(data.field.key)
      ) {
        if (!this.isMarketStrata) res = `$${Number(data.value).toFixed(2)}`;
        else
          res = `$${Math.round(parseInt(data.value)).toLocaleString("en-US")}`;
      }

      return res;
    },
    haveValue(data) {
      return data && !["", "-", null, 0, "0"].includes(data);
    },
    toUTCMidnight(date) {
      return Date.UTC(date.getFullYear(), date.getMonth(), date.getDate());
    },
    resetHeaders() {
      this.fields = JSON.parse(JSON.stringify(this.field_selected));
      this.list2 = JSON.parse(JSON.stringify(this.custom_fields));
    },
    setUnits(data, index) {
      const vm = this;
      const uids = [...new Set(data.map(unit => unit.field_unit_type_value))];
      sessionStorage.setItem(
        uids + index,
        JSON.stringify(data)
      );
      const STATUS = vm.isMarketStrata ? ["now_selling", "sold_out", "in_planning"] : ["now_leasing", "leased_out", "in_planning"]
      const routeData = this.$router.resolve({
        path: "/unit-summary",
        query: {
          unit: index,
          uids: uids,
          status: STATUS,
        },
      });
      window.open(routeData.href, "_blank");
    },
    sortTableByAvgSize(aRow, bRow, key, sortDesc) {
      let acc =
        key == "Avg Size" ? Number(aRow[key].replace(",", "")) : aRow[key];
      let curr =
        key == "Avg Size" ? Number(bRow[key].replace(",", "")) : bRow[key];
      if (sortDesc == true) {
        return acc > curr ? 1 : acc < curr ? -1 : 0;
      } else {
        return acc < curr ? -1 : acc > curr ? 1 : 0;
      }
    },
    isNotEmpty(response) {
      if (response == null) return false;
      if (typeof response === "string" && response.trim().length === 0) return false;
      if (Array.isArray(response) && response.length === 0) return false;
      if (typeof response === "object" && Object.keys(response).length === 0) return false;
      return true;
    },
    async loadFilters() {
      try {
        const custom_filter = await loadView(this.getViewCategory, "custom_filter");
        if (this.isNotEmpty(custom_filter)) {
          for (const key in custom_filter) {
            if (this.custom_filter[key]) {
              this.custom_filter[key][key + "_selected"] =
                custom_filter[key][key + "_selected"];
            }
          }
        }
      } catch (error) {
        console.log(error);
      }

      try {
        const main_filter = await loadView(this.getViewCategory, "main_filter");
        if (this.isNotEmpty(main_filter)) {
          if (Array.isArray(main_filter.filterArray))
            this.filter_rows.filterArray = main_filter.filterArray;
          if (typeof main_filter.filterText === "string")
            this.filter_rows.filterText = main_filter.filterText;
          if (main_filter.maxRow && main_filter.maxRow != null)
            this.perPage = main_filter.maxRow;
        }
      } catch (error) {
        console.log(error);
      }

      try {
        const savedShown = await loadView(this.getViewCategory, "field_selected");
        const savedHidden = await loadView(this.getViewCategory, "custom_headers");

        if (
          (Array.isArray(savedShown) && savedShown.length) ||
          (Array.isArray(savedHidden) && savedHidden.length)
        ) {
          const allPossibleFields = [...this.fields, ...this.list2];
          const savedKeys = new Set([
            ...(Array.isArray(savedShown) ? savedShown : []).map((c) => c.key),
            ...(Array.isArray(savedHidden) ? savedHidden : []).map((c) => c.key),
          ]);
          const localKeys = allPossibleFields.map((c) => c.key);
          const matches =
            savedKeys.size === localKeys.length &&
            localKeys.every((k) => savedKeys.has(k));

          if (matches) {
            const keyToDef = Object.fromEntries(
              allPossibleFields.map((c) => [c.key, c])
            );
            this.fields = (Array.isArray(savedShown) ? savedShown : [])
              .map((c) => keyToDef[c.key])
              .filter(Boolean);
            this.list2 = (Array.isArray(savedHidden) ? savedHidden : [])
              .map((c) => keyToDef[c.key])
              .filter(Boolean);
          }
        }
      } catch (error) {
        console.log(error);
      }
    },
    async saveFilters() {
      var selected_custom_filter = {
        baths: {
          baths_selected: this.custom_filter.baths.baths_selected,
        },
        beds: {
          beds_selected: this.custom_filter.beds.beds_selected,
        },
        fpSpecs: {
          fpSpecs_selected: this.custom_filter.fpSpecs.fpSpecs_selected,
        },
        listingType: {
          listingType_selected:
            this.custom_filter.listingType.listingType_selected,
        },
        status: {
          status_selected: this.custom_filter.status.status_selected,
        },
        city: {
          city_selected: this.custom_filter.city.city_selected,
        },
        projectStatus: {
          projectStatus_selected:
            this.custom_filter.projectStatus.projectStatus_selected,
        },
        priceDate: {
          priceDate_selected: this.custom_filter.priceDate.priceDate_selected,
        },
        soldDate: {
          soldDate_selected: this.custom_filter.soldDate.soldDate_selected,
        },
      };
      try {
        saveView(this.getViewCategory, "custom_filter", {
          custom_filter: selected_custom_filter,
        }).then((custom_filter) => {
          saveView(this.getViewCategory, "main_filter", {
            main_filter: {
              filterArray: this.filter_rows.filterArray,
              filterText: this.filter_rows.filterText,
              maxRow: this.perPage,
            },
          }).then((main_filter) => {
            saveView(this.getViewCategory, "field_selected", {
              field_selected: this.fields,
            }).then((field_selected) => {
              saveView(this.getViewCategory, "custom_headers", {
                custom_headers: this.list2,
              }).then((res) => {
                this.$bvToast.toast(`Filters saved successfully!`, {
                  title: "Great job!",
                  variant: "success",
                  autoHideDelay: 5000,
                });
              });
            });
          });
        });
      } catch (error) {
        this.$bvToast.toast(
          `Saving of filters failed. Please contact the administrator. Thank you`,
          {
            title: "Ooops!",
            autoHideDelay: 5000,
            variant: "warning",
          }
        );
        console.log(error);
      }
    },
    switchRangeValue(value) {
      if (value == "Price") this.enablePricedate = !this.enablePricedate;
      else if (value == "Sold") this.enableSoldDate = !this.enableSoldDate;
    },
    getDiffDays(date) {
      let date1 = this.currentDate;
      let date2 = new Date(date);

      let dateFormatter = new Intl.DateTimeFormat("en-US");

      if (date == "Invalid Date") return null;

      let Difference_In_Days = Math.floor(
        (date1 - date2) / (1000 * 60 * 60 * 24)
      );

      return Difference_In_Days;
    },
    getAvailablePricing(data) {
      const vm = this;

      function formatNumber(value) {
        return typeof value == 'string' ? value.replace(/,/g, '') : value
      }

      let unitsWithListPrice = data.filter(
        (item) =>
          item.field_mls_list_price_value &&
          !isNaN(Number(item.field_mls_list_price_value))
      ).length;

      let availability = data.reduce(
        (acc, curr) => {
          let netPrice = [curr.net_price, curr.field_mls_unit_size_value].every(val => vm.isRealValue(val)) ? formatNumber(curr.net_price) : 0

          acc.units++;
          if (
            curr.field_mls_list_price_value &&
            !isNaN(Number(curr.field_mls_list_price_value))
          ) {
            acc.size += Math.round(curr.field_mls_unit_size_value);
          }
          if (
            curr.field_mls_list_price_value &&
            !isNaN(Number(curr.field_mls_list_price_value))
          ) {
            acc.list_price += Math.round(
              Number(curr.field_mls_list_price_value)
            );
          }
          if (curr.mls_psf && !isNaN(Number(curr.mls_psf))) {
            acc.psf += Number(formatNumber(curr.field_mls_list_price_value) / curr.field_mls_unit_size_value);
          }
          if (curr.buyer_incentives && !isNaN(Number(curr.buyer_incentives))) {
            acc.buyer_incentives += Number(curr.buyer_incentives) / acc.units;
          }
          acc.net_price += !isNaN(Number(netPrice)) ? Number(netPrice) : 0;
          acc.net_psf += !isNaN(Number(netPrice)) ? Number(netPrice / curr.field_mls_unit_size_value) : 0;
          return acc;
        },
        {
          units: 0,
          size: 0,
          list_price: 0,
          psf: 0,
          buyer_incentives: 0,
          net_price: 0,
          net_psf: 0,
        }
      );

      availability.size = availability.size / unitsWithListPrice || 0;
      availability.list_price = availability.list_price / unitsWithListPrice;

      availability.net_price =
        Math.round(availability.net_price / unitsWithListPrice
        ) || 0;
      availability.psf = !vm.isMarketStrata
        ? Number(availability.list_price / availability.size).toFixed(2)
        : Math.round(availability.list_price / availability.size) || 0;
      availability.net_psf = !vm.isMarketStrata
        ? Number(availability.net_price / availability.size).toFixed(2)
        : Math.round(availability.net_price / availability.size) || 0;
      vm.currentAvailability = availability;
    },
    addToFilterOptions() {
      const vm = this;

      switch (vm.gutType) {
        case "strata":
          vm.custom_filter.listingType.listingType_options = [
            {
              id: "lt_Presale",
              label: "Presale",
              text: "Presale",
              value: "Presale",
            },
            {
              id: "lt_Assignment",
              label: "Assignment",
              text: "Assignment",
              value: "Assignment",
            },
            {
              id: "lt_Resale",
              label: "Resale",
              text: "Resale",
              value: "Resale",
            },
          ]
          vm.custom_filter.projectStatus.projectStatus_options = [
            { id: "ps_Now Selling", label: "Now Selling", text: "Now Selling", value: "Now Selling" },
            { id: "ps_Selling Soon", label: "Sold Out", text: "Sold Out", value: "Sold Out" },
          ]
          vm.custom_filter.status.status_options = [
            {
              id: "status_Active",
              label: "Active",
              text: "Active",
              value: "Active",
            },
            { id: "status_Sold", label: "Sold", text: "Sold", value: "Sold" },
          ]

          break;

        case "rental":
          vm.custom_filter.listingType.listingType_options = [
            {
              id: "lt_New Lease",
              label: "New Lease",
              text: "New Lease",
              value: "New Lease",
            },
            {
              id: "lt_Re-Lease",
              label: "Re-Lease",
              text: "Re-Lease",
              value: "Re-Lease",
            },
          ]
          vm.custom_filter.projectStatus.projectStatus_options = [
            { id: "ps_Now Leasing", label: "Now Leasing", text: "Now Leasing", value: "Now Leasing" },
            { id: "ps_Leased Out", label: "Leased Out", text: "Leased Out", value: "Leased Out" },
          ]
          vm.custom_filter.status.status_options = [
            {
              id: "status_Active",
              label: "Active",
              text: "Active",
              value: "Active",
            },
            {
              id: "status_Sold",
              label: "Leased",
              text: "Leased",
              value: "Leased",
            },
          ]
          break;
      }
      // Rebuilt from scratch: this runs again on every tab switch, and unshift
      // onto the existing array would stack duplicate region groups.
      vm.custom_filter.city.city_options = [];
      vm.custom_filter.city.city_options.unshift(
        {
          id: "city_N of Fraser",
          label: "N of Fraser",
          children: nFraserOptions.map(city => {
            return {
              id: `city_` + city.text,
              label: city.text,
              key: `city_` + city.text
            }
          }),
        },
        {
          id: "city_S of Fraser",
          label: "S of Fraser",
          children: sFraserOptions.map(city => {
            return {
              id: `city_` + city.text,
              label: city.text,
              key: `city_` + city.text
            }
          }),
        },
        {
          id: "city_Van Island",
          label: "Van Island",
          children: vm.vanCities.map(city => {
            return {
              id: `city_` + city.text,
              label: city.text,
              key: city.key
            }
          }),
        },
        {
          id: "city_Okanagan",
          label: "Okanagan",
          children: vm.okaCities.map(city => {
            return {
              id: `city_` + city.text,
              label: city.text,
              key: city.key
            }
          }),
        },
      );
      vm.custom_filter.city.city_selected = []
    },
    getFilteredData(data) {
      this.filtered_data = data;
    },
    exportData() {
      const vm = this;
      try {
        const csvContent = this.convertToCsv(vm.filteredMlsData);
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "exported-data.csv");
        link.click();
        URL.revokeObjectURL(url); // Clean up the URL after download
        // Show success toast once report is generated
        vm.$bvToast.toast(`You can now view the downloaded file`, {
          title: "Success!",
          variant: "success",
          autoHideDelay: 5000,
        });
      } catch (error) {
        console.error("Failed to fetch user data:", error);

        vm.$bvToast.toast(
          `${"Something went wrong. \n Please inform us to fix this for you."}`,
          {
            title: "Oops!",
            variant: "danger",
            autoHideDelay: 5000,
          }
        );
      }
    },
    exportSummary() {
      let data = {
        project: {
          header: this.projectsHeader,
          data: this.projects,
        },
        groupedData: {
          header: this.previewHeader,
          data: this.groupedBeds,
        },
      };
      var csvContent = "";
      for (const [key, value] of Object.entries(data)) {
        for (const [innerKey, innerValue] of Object.entries(value)) {
          if (innerKey == "header") {
            csvContent +=
              value[innerKey]
                .filter((header) => header.key != "view")
                .map((item) => item.label)
                .join(",") + "\n";
          }
          if (innerKey == "data" && key == "project") {
            const rows = value[innerKey].map((item) => {
              return this.projectsHeader.map((header) => {
                let cell = item[header.key];
                return typeof cell === "string" && cell.includes(",")
                  ? `"${cell.replace(/"/g, '""')}"` // Escape commas and quotes
                  : cell;
              });
            });
            rows.forEach((row, index) => {
              csvContent += row.join(",") + "\n";

              if (index == rows.length - 1) {
                csvContent += "\n"; // Add an extra newline between project entries
              }
            });
          }
          if (innerKey == "data" && key == "groupedData") {
            const rows = value[innerKey].map((item) => {
              return this.previewHeader.map((header) => {
                let cell = item[header.key];
                return typeof cell === "string" && cell.includes(",")
                  ? `"${cell.replace(/"/g, '""')}"` // Escape commas and quotes
                  : cell;
              });
            });
            rows.forEach((row) => {
              csvContent += row.join(",") + "\n";
            });
          }
        }
      }
      var pom = document.createElement("a");
      var blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      var url = URL.createObjectURL(blob);
      pom.href = url;
      pom.setAttribute("download", "exported-mls-report-summary.csv");
      pom.click();
    },
    convertToCsv(data) {
      const vm = this;
      let headers = [
        {
          key: "name",
          label: "Project",
        },
        {
          key: "developer",
          label: "Developer",
        },
        {
          key: "address",
          label: "Address",
        },
        {
          key: "city",
          label: "City",
        },
        {
          key: "neighbour",
          label: "Neighbour",
        },
        {
          key: "project_type",
          label: "Project Type",
        },
      ];
      headers.push(
        ...vm.fields.filter(
          (header) =>
            !["checkbox", "customData", "customLocationData"].includes(
              header.key
            )
        )
      );

      const rows = data.map((row) => {
        return headers.map((header) => {
          const cell = row[header.key];

          if (header.key == "custom_field_mls_beds_value") {
            return cell ? `${cell.type ? cell.type + " | " : ""}${cell.fp_specs}` : '';
          }
          return typeof cell === "string" && cell.includes(",")
            ? `"${cell.replace(/"/g, '""')}"` // Escape commas and quotes
            : cell;
        });
      });

      let forRowheaders = headers.map((item) => item.label);
      const headerRow = forRowheaders.join(",");
      const csvRows = [headerRow, ...rows.map((row) => row.join(","))];
      return csvRows.join("\n");
    },
    async fetchUserData() {
      try {
        await this.useUserDataStore.getData();
      } catch (error) {
        console.error("Failed to fetch user data:", error);
      }
    },
    async generateReport() {
      const vm = this;
      vm.$bvToast.toast(
        `Download is in progress. Please wait for a moment while we prepare everything for you.`,
        { title: "Downloading...", variant: "info", autoHideDelay: 5000 }
      );
      try {
        vm.loading = true;
        const summaryReport = vm.filteredMlsData;
        const now = new Date();
        const reportDate = now.toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' }).toUpperCase();
        const reportTime = now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });

        const doc = new jsPDF({ unit: 'mm', format: 'letter', orientation: 'landscape' });
        const PW = doc.internal.pageSize.width;
        const PH = doc.internal.pageSize.height;

        const C = {
          navy: [18, 40, 63],
          coral: [252, 88, 104],
          violet: [52, 51, 118],
          fg1: [18, 40, 63],
          fg2: [84, 84, 84],
          fg3: [138, 138, 138],
          success: [31, 157, 85],
          successBg: [232, 245, 238],
          danger: [212, 56, 71],
          divider: [238, 240, 244],
          borderStrong: [201, 204, 210],
          bgSubtle: [237, 242, 254],
          white: [255, 255, 255],
        };
        const M = { l: 14, r: 14 };

        const drawUtilBar = () => {
          doc.setFillColor(...C.navy);
          doc.rect(0, 0, PW, 9, 'F');
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(7);
          doc.setTextColor(255, 255, 255);
          const reportLabel = `REPORT · ${reportDate}`;
          doc.text(reportLabel, M.l, 5.8);
          doc.text(reportTime, M.l + doc.getTextWidth(reportLabel) + 8, 5.8);
          const brandTxt = 'REALPROPERTYDATA.CA';
          doc.text(brandTxt, PW - M.r, 5.8, { align: 'right' });
          doc.setFillColor(...C.coral);
          doc.circle(PW - M.r - doc.getTextWidth(brandTxt) - 2.5, 4.6, 0.9, 'F');
        };

        const drawPageFooter = (pageNum, total) => {
          const fy = PH - 10;
          doc.setDrawColor(...C.divider);
          doc.setLineWidth(0.2);
          doc.line(M.l, fy - 4, PW - M.r, fy - 4);
          doc.setFillColor(...C.coral);
          doc.circle(M.l + 1.2, fy - 1, 0.9, 'F');
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(6.5);
          doc.setTextColor(...C.fg3);
          doc.text('REAL PROPERTY DATA · CONFIDENTIAL', M.l + 3.5, fy);
          doc.text(`PAGE ${pageNum} OF ${total}`, PW - M.r, fy, { align: 'right' });
        };

        const hexToRgb = (hex) => {
          const h = String(hex || '').replace('#', '');
          if (h.length !== 6) return C.fg1;
          return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
        };

        // ====== Header ======
        let y = 14;
        doc.setFillColor(...C.coral);
        doc.rect(M.l, y, 10, 1.2, 'F');
        y += 4.5;

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(7);
        doc.setTextColor(...C.violet);
        doc.text('UNIT SUMMARY REPORT', M.l, y);

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(22);
        doc.setTextColor(...C.fg1);
        doc.text('Unit Summary', M.l, y + 8);

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(7);
        doc.setTextColor(...C.violet);
        doc.text(`${summaryReport.length} UNITS`, PW - M.r, y, { align: 'right' });
        doc.setFont('helvetica', 'italic');
        doc.setFontSize(7);
        doc.setTextColor(...C.fg3);
        doc.text(`Generated · ${reportDate}`, PW - M.r, y + 5, { align: 'right' });

        y += 13;
        doc.setDrawColor(...C.divider);
        doc.setLineWidth(0.2);
        doc.line(M.l, y, PW - M.r, y);
        y += 5;

        // Legend
        const legend = [
          { dot: C.navy, label: 'Verified' },
          { dot: C.coral, label: 'Semi-verified' },
          { dot: C.borderStrong, label: 'Estimated' },
        ];
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(7.5);
        const legendItemWidths = legend.map(it => doc.getTextWidth(it.label) + 5 + 6);
        const legendTotalW = legendItemWidths.reduce((a, b) => a + b, 0) - 6;
        let lx = PW - M.r - legendTotalW;
        legend.forEach((it, i) => {
          doc.setFillColor(...it.dot);
          doc.circle(lx + 1.2, y, 1.1, 'F');
          doc.setTextColor(...C.fg2);
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(7.5);
          doc.text(it.label, lx + 4, y + 1.3);
          lx += legendItemWidths[i];
        });
        y += 5;

        // ====== Data — columns synced from tabulator ======
        const unitBreakDownHeaders = vm.getPdfColumns(
          vm.$refs.mlsTable && vm.$refs.mlsTable.tabulator
        );
        const formattedSummary = summaryReport.map((item) => ({ ...item }));
        const tableData = formattedSummary.map((row) =>
          unitBreakDownHeaders.map((h) => vm.pdfFormatCell(row, h.key))
        );

        const list_price_index = unitBreakDownHeaders.findIndex(
          (h) => h.key === "field_mls_list_price_value"
        );
        const priceBackupByRow = formattedSummary.map(
          (r) => r.custom_price_backup
        );

        const headRow = unitBreakDownHeaders.map((h) => h.label);
        const availW = PW - M.l - M.r;
        const weights = headRow.map((label, i) => {
          if (i === 0) return 2.4;
          if (i === 1) return 1.6;
          const t = String(label || '').toLowerCase();
          if (/(price|incentive|date|status|confidence|address|plan name|listing type)/.test(t)) return 1.2;
          if (/(psf|baths|age|unit #|stalls|parking|beds)/.test(t)) return 0.7;
          return 1;
        });
        const weightSum = weights.reduce((a, b) => a + b, 0);
        const columnStyles = {};
        headRow.forEach((_, i) => {
          columnStyles[i] = { cellWidth: (weights[i] / weightSum) * availW };
          if (i === 0) { columnStyles[i].halign = 'left'; columnStyles[i].fontStyle = 'bold'; }
        });
        const colCount = headRow.length;
        const baseFontSize = colCount > 18 ? 5.6 : colCount > 14 ? 6.2 : colCount > 10 ? 7 : 7.6;
        const headFontSize = Math.max(5.4, baseFontSize - 0.8);

        autoTable(doc, {
          startY: y,
          margin: { left: M.l, right: M.r, top: 14, bottom: 18 },
          head: [headRow],
          body: tableData,
          theme: 'plain',
          rowPageBreak: 'avoid',
          tableWidth: availW,
          styles: {
            font: 'helvetica',
            fontSize: baseFontSize,
            cellPadding: { top: 1.8, right: 1.4, bottom: 1.8, left: 1.4 },
            textColor: C.fg1,
            overflow: 'linebreak',
          },
          headStyles: {
            fontStyle: 'bold', fontSize: headFontSize, textColor: C.fg2,
            halign: 'center', overflow: 'linebreak', valign: 'bottom',
            cellPadding: { top: 1.6, right: 1.4, bottom: 2, left: 1.4 },
          },
          bodyStyles: { halign: 'center', valign: 'middle' },
          columnStyles,
          didParseCell: (data) => {
            if (data.section === 'body'
              && data.column.index === list_price_index
              && list_price_index >= 0) {
              const backup = priceBackupByRow[data.row.index];
              if (backup) {
                data.cell.styles.textColor = hexToRgb(vm.setColor(backup));
                data.cell.styles.fontStyle = 'bold';
              }
            }
            if (data.section === 'body' && data.cell.text && data.cell.text[0] === '—') {
              data.cell.styles.textColor = C.fg3;
              data.cell.styles.fontStyle = 'normal';
            }
          },
          didDrawCell: (data) => {
            if (data.section === 'head') {
              doc.setDrawColor(...C.navy);
              doc.setLineWidth(0.5);
              doc.line(data.cell.x, data.cell.y + data.cell.height,
                data.cell.x + data.cell.width, data.cell.y + data.cell.height);
            }
            if (data.section === 'body') {
              doc.setDrawColor(...C.divider);
              doc.setLineWidth(0.2);
              doc.line(data.cell.x, data.cell.y + data.cell.height,
                data.cell.x + data.cell.width, data.cell.y + data.cell.height);
            }
          },
        });

        // ====== Image pages (one per floor plan) ======
        await new Promise((resolve) => {
          this.arrayOfImages.forEach((x) => {
            doc.addPage('letter', 'l');
            let yi = 14;
            doc.setFillColor(...C.coral);
            doc.rect(M.l, yi, 10, 1.2, 'F');
            yi += 4.5;
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(7);
            doc.setTextColor(...C.violet);
            doc.text('FLOOR PLAN', M.l, yi);
            doc.setFont('helvetica', 'bold');
            doc.setFontSize(18);
            doc.setTextColor(...C.fg1);
            doc.text(String(x.project.name || ''), M.l, yi + 8);
            yi += 12;
            doc.setDrawColor(...C.divider);
            doc.setLineWidth(0.2);
            doc.line(M.l, yi, PW - M.r, yi);
            yi += 5;

            const facts = [
              { l: 'UNIT #', v: x.project.field_mls_address_value || '—' },
              { l: 'PRICE', v: x.project.price || '—' },
              { l: '$PSF', v: x.project.psf || '—' },
              { l: 'PRICE DATE', v: x.project.priceDate || '—' },
            ];
            const stripH = 15;
            doc.setFillColor(...C.bgSubtle);
            doc.roundedRect(M.l, yi, PW - M.l - M.r, stripH, 2, 2, 'F');
            const cw = (PW - M.l - M.r) / facts.length;
            facts.forEach((f, i) => {
              const fx = M.l + i * cw;
              doc.setFont('helvetica', 'bold');
              doc.setFontSize(6.2);
              doc.setTextColor(...C.violet);
              doc.text(f.l, fx + 4, yi + 4.5);
              doc.setFont('helvetica', 'bold');
              doc.setFontSize(11);
              doc.setTextColor(...C.fg1);
              doc.text(String(f.v), fx + 4, yi + 11);
            });
            yi += stripH + 6;

            const availH = PH - yi - 16;
            const availW = PW - M.l - M.r;
            let iw = x.image.width;
            let ih = x.image.height;
            const sc = Math.min(availW / iw, availH / ih);
            iw *= sc; ih *= sc;
            const ix = M.l + (availW - iw) / 2;
            doc.addImage(x.image.src, ix, yi, iw, ih);
          });
          resolve();
        });

        // Apply util bar + footer to every page
        const total = doc.internal.getNumberOfPages();
        for (let i = 1; i <= total; i++) {
          doc.setPage(i);
          drawUtilBar();
          drawPageFooter(i, total);
        }

        this.loading = false;
        vm.$bvToast.toast(`You can now view the downloaded file`, {
          title: "Success!", variant: "success", autoHideDelay: 5000,
        });
        window.open(
          doc.output("bloburl"),
          "_blank",
          "toolbar=no,status=no,menubar=no,scrollbars=no,resizable=no,modal=yes,top=100,left=500,width=1000,height=800"
        );
      } catch (error) {
        console.error("Failed to generate report:", error);
        vm.$bvToast.toast(
          `Something went wrong. \n Please inform us to fix this for you.`,
          { title: "Oops!", variant: "danger", autoHideDelay: 5000 }
        );
      }
    },
    enhanceWordBreak(doc, cell, column) {
      if (cell === undefined) {
        return;
      }

      const hasCustomWidth = typeof cell.styles.cellWidth === "number";

      if (hasCustomWidth || cell.raw == null || cell.colSpan > 1) {
        return;
      }

      let text;

      if (cell.raw instanceof Node) {
        text = cell.raw.innerText;
      } else {
        if (typeof cell.raw == "object") {
          // not implemented yet
          // when a cell contains other cells (colSpan)
          return;
        } else {
          text = "" + cell.raw;
        }
      }

      // split cell string by spaces
      const words = text.split(/\s+/);

      // calculate longest word width
      const maxWordUnitWidth = words
        .map((s) => Math.floor(doc.getStringUnitWidth(s) * 100) / 100)
        .reduce((a, b) => Math.max(a, b), 0);
      const maxWordWidth =
        maxWordUnitWidth * (cell.styles.fontSize / doc.internal.scaleFactor);

      const minWidth = cell.padding("horizontal") + maxWordWidth;

      // update minWidth for cell & column

      if (minWidth > cell.minWidth) {
        cell.minWidth = minWidth;
      }

      if (cell.minWidth > cell.wrappedWidth) {
        cell.wrappedWidth = cell.minWidth;
      }

      if (cell.minWidth > column.minWidth) {
        column.minWidth = cell.minWidth;
      }

      if (column.minWidth > column.wrappedWidth) {
        column.wrappedWidth = column.minWidth;
      }
    },
    async getImageRatio(imgUrl) {
      const max = { height: 190, width: 133 };

      return new Promise(async (resolve) => {
        let image = new Image();
        image.src = imgUrl;

        image.onload = async function () {
          const ratio = image.height / image.width;

          if (image.height > max.height || image.width > max.width) {
            if (image.height > image.width) {
              image.height = max.height;
              image.width = image.height * (1 / ratio);
              // Making reciprocal of ratio because ration of height as width is no valid here needs width as height
            } else if (image.width > image.height) {
              image.width = max.width;
              image.height = image.width * ratio;
              // Ratio is valid here
            } else {
              image.width = max.width;
              image.height = image.width * ratio;
            }
          }
          resolve({
            status: "success",
            image: image,
          });
        };
      });
    },
    reportFormatDate(dateVal) {
      let vm = this;
      var newDate = new Date(dateVal);

      var sMonth = vm.padValue(newDate.getMonth() + 1);
      var sDay = vm.padValue(newDate.getDate());
      var sYear = newDate.getFullYear();
      var sHour = newDate.getHours();
      var sMinute = vm.padValue(newDate.getMinutes());
      var sAMPM = "AM";

      var iHourCheck = parseInt(sHour);

      if (iHourCheck > 12) {
        sAMPM = "PM";
        sHour = iHourCheck - 12;
      } else if (iHourCheck === 0) {
        sHour = "12";
      }

      sHour = vm.padValue(sHour);

      return (
        sMonth +
        "/" +
        sDay +
        "/" +
        sYear +
        " " +
        sHour +
        ":" +
        sMinute +
        " " +
        sAMPM
      );
    },

    padValue(value) {
      return value < 10 ? "0" + value : value;
    },
    formatDate(date) {
      if (!date || date.length <= 0) return "-";

      let mlsDate = new Date(date * 1000);

      return mlsDate.toISOString().slice(0, 10).replace(/-/g, "-");
    },
    convertCurrency(value) {
      return !["", "-", "0", null].includes(value) ? "$" + value : "-";
    },
    setRpdHeaderFields() {
      const vm = this;
      var commonFields;
      var commonCustomHeaderFields;
      switch (vm.gutType) {
        case 'strata':
          commonFields = [
            { label: "Selected", key: "checkbox", thClass: "w-small" },
            {
              label: "Project & Developer",
              key: "customData",
              thClass: "text-left pl-3 w-25",
              tdClass: "border-right",
              sortable: true,
            },
            { label: "Location", key: "customLocationData", sortable: true },
            { label: "Unit #", key: "field_mls_address_value", sortable: true },
            {
              label: "Plan Name",
              key: "field_mls_plan_name_value",
              sortable: true,
            },
            {
              label: "Type",
              key: "custom_field_mls_beds_value",
              sortable: true,
            },
            { label: "Baths", key: "field_mls_baths_value", sortable: true },
            {
              label: "Unit Size",
              key: "field_mls_unit_size_value",
              formatter: (value) => {
                return Number(value) ? Math.round(value).toLocaleString() : "-";
              },
              sortable: true,
            },
            {
              label: "List Price",
              key: "field_mls_list_price_value",
              tdClass: "_w-350",
              thClass: "_w-350",
              sortable: true,
            },
            {
              label: "$PSF",
              key: "psf",
              formatter: this.convertCurrency,
              sortable: true,
            },
            {
              label: "Incentives",
              key: "buyer_incentives",
              formatter: this.convertCurrency,
              sortable: true,
            },
            {
              label: "Net Price",
              key: "net_price",
              sortable: true,
            },
            {
              label: "Net $PSF",
              key: "custom_net_psf",
              formatter: this.convertCurrency,
              sortable: true,
              sortByFormatted: true,
            },
            { label: "Price Date", key: "price_date", sortable: true },
            { label: "Unit Status", key: "status_name_name", sortable: true },
            { label: "Project Status", key: "status", sortable: true },
            { label: "Age", key: "custom_age", sortable: true },
            {
              label: "Listing Type",
              key: "custom_listing_type",
              sortable: true,
            },
          ];

          commonCustomHeaderFields = [
            { label: "Unit Status", key: "status_name_name", sortable: true },
            {
              label: "Sold Price Confidence",
              key: "custom_price_confidence",
              sortable: true,
            },
          ];
          break;
        case 'rental':
          commonFields = [
            { label: "Selected", key: "checkbox", thClass: "w-small" },
            {
              label: "Project & Developer",
              key: "customData",
              thClass: "text-left pl-3 w-25",
              tdClass: "border-right",
              sortable: true,
            },
            { label: "Location", key: "customLocationData", sortable: true },
            { label: "Unit #", key: "field_mls_address_value", sortable: true },
            {
              label: "Plan Name",
              key: "field_mls_plan_name_value",
              sortable: true,
            },
            {
              label: "Type",
              key: "custom_field_mls_beds_value",
              sortable: true,
            },
            { label: "Baths", key: "field_mls_baths_value", sortable: true },
            {
              label: "Unit Size",
              key: "field_mls_unit_size_value",
              formatter: (value) => {
                return Number(value) ? Math.round(value).toLocaleString() : "-";
              },
              sortable: true,
            },
            {
              label: "List Price",
              key: "field_mls_list_price_value",
              tdClass: "_w-350",
              thClass: "_w-350",
              sortable: true,
            },
            {
              label: "$PSF",
              key: "psf",
              formatter: this.convertCurrency,
              sortable: true,
            },
            {
              label: "Incentives",
              key: "buyer_incentives",
              formatter: this.convertCurrency,
              sortable: true,
            },
            {
              label: "Net Price",
              key: "net_price",
              sortable: true,
            },
            {
              label: "Net $PSF",
              key: "custom_net_psf",
              formatter: this.convertCurrency,
              sortable: true,
              sortByFormatted: true,
            },
            { label: "Price Date", key: "price_date", sortable: true },
            { label: "Unit Status", key: "status_name_name", sortable: true },
            { label: "Project Status", key: "status", sortable: true },
            { label: "Age", key: "custom_age", sortable: true },
            {
              label: "Listing Type",
              key: "custom_listing_type",
              sortable: true,
            },
          ];

          commonCustomHeaderFields = [
            { label: "Unit Status", key: "status_name_name", sortable: true },
            {
              label: "Leased Price Confidence",
              key: "custom_price_confidence",
              sortable: true,
            },
          ];
          break;

        default:
          commonFields = [
            { label: "Selected", key: "checkbox", thClass: "w-small" },
            {
              label: "Project & Developer",
              key: "customData",
              thClass: "text-left pl-3 w-25",
              tdClass: "border-right",
              sortable: true,
            },
            { label: "Location", key: "customLocationData", sortable: true },
            { label: "Unit #", key: "field_mls_address_value", sortable: true },
            {
              label: "Plan Name",
              key: "field_mls_plan_name_value",
              sortable: true,
            },
            {
              label: "Type",
              key: "custom_field_mls_beds_value",
              sortable: true,
            },
            { label: "Baths", key: "field_mls_baths_value", sortable: true },
            {
              label: "Unit Size",
              key: "field_mls_unit_size_value",
              formatter: (value) => {
                return Number(value) ? Math.round(value).toLocaleString() : "-";
              },
              sortable: true,
            },
            {
              label: "Sold Price Confidence",
              key: "custom_price_confidence",
              sortable: true,
            },
            {
              label: "List Price",
              key: "field_mls_list_price_value",
              tdClass: "_w-350",
              thClass: "_w-350",
              sortable: true,
            },
            {
              label: "$PSF",
              key: "psf",
              formatter: this.convertCurrency,
              sortable: true,
            },
            {
              label: "Incentives",
              key: "buyer_incentives",
              formatter: this.convertCurrency,
              sortable: true,
            },
            {
              label: "Net Price",
              key: "net_price",
              sortable: true,
            },
            {
              label: "Net $PSF",
              key: "custom_net_psf",
              formatter: this.convertCurrency,
              sortable: true,
              sortByFormatted: true,
            },
            { label: "Price Date", key: "price_date", sortable: true },
            { label: "Unit Status", key: "status_name_name", sortable: true },
            { label: "Project Status", key: "status", sortable: true },
            { label: "Age", key: "custom_age", sortable: true },
            {
              label: "Price Confidence",
              key: "custom_price_confidence",
              sortable: true,
            },
          ];

          commonCustomHeaderFields = [
            { label: "Unit Status", key: "status_name_name", sortable: true },
            {
              label: "Listing Type",
              key: "custom_listing_type",
              sortable: true,
            },
          ];
          break;
      }

      vm.fields = commonFields;
      vm.field_selected = JSON.parse(JSON.stringify(commonFields));
      vm.list2 = commonCustomHeaderFields;
      vm.custom_fields = commonCustomHeaderFields;
    },
    selectAllRows({ selectAll, tableRef }) {
      this.filtered_data.forEach((filteredItem, index) => {
        this.mlsData.forEach((item) => {
          if (item.entity_id === filteredItem.entity_id) {
            item.rowSelected = selectAll;
          }
        });
      });
    },
    updateTotalRows(value) {
      this.totalRows = value;
    },
    roundToWholeNumber(value) {
      return Number(value) ? "$" + Math.round(value).toLocaleString() : "-";
    },
    showModal(data) {
      this.$root.$emit("bv::show::modal", "mls-fp-" + data);
    },
    setColor(data) {
      let color = "";
      if (data) {
        if (data == "Verified") color = "#0b36dc";

        if (data == "Educated Estimated") color = "#dc3545";

        if (data == "Semi-Verified") color = "#FF6F16";
      }
      return color;
    },

    getPdfColumns(tab) {
      // Tabulator field ids in MlsReportTable differ from row data keys.
      // Map back to the row-data field key that pdfFormatCell understands.
      const FIELD_REMAP = {
        name: "customData",
        field_mls_baths_value: "field_mls_baths_value",
        field_mls_unit_size_value: "field_mls_unit_size_value",
      };
      if (tab && typeof tab.getColumns === "function") {
        const cols = tab
          .getColumns()
          .filter((c) => c.isVisible && c.isVisible() && c.getField())
          .map((c) => ({
            label: (c.getDefinition && c.getDefinition().title) || c.getField(),
            key: FIELD_REMAP[c.getField()] || c.getField(),
          }))
          .filter((c) => c.key !== "entity_id");
        if (cols.length) return cols;
      }
      // Fallback: use vm.fields
      return (this.fields || [])
        .filter((x) => x.key !== "checkbox")
        .map((x) => ({ label: x.label, key: x.key }));
    },

    pdfFormatCell(row, key) {
      const DASH = "—";
      const v = row[key];
      const isEmpty = (x) =>
        x === null || x === undefined || x === "" || x === "-" || x === "0" || x === 0;

      if (key === "customData") {
        const projectName =
          (v && typeof v === "object" && v.projectName) || row.name || "";
        const dev =
          (v && typeof v === "object" && v.project) || row.developer || "";
        return projectName || dev ? `${projectName}\n${dev}` : DASH;
      }
      if (key === "customLocationData") {
        if (v && typeof v === "object") {
          const addr = v.address || "";
          const city = v.municipality || v.neighbour || "";
          return addr || city ? `${addr}\n${city}` : DASH;
        }
        return isEmpty(v) ? DASH : String(v);
      }
      if (key === "custom_field_mls_beds_value") {
        if (v && typeof v === "object") {
          const type = v.type || "";
          const fp = v.fp_specs && v.fp_specs !== "None" ? v.fp_specs : "";
          return type ? (fp ? `${type}\n${fp}` : type) : DASH;
        }
        return isEmpty(v) ? DASH : String(v);
      }
      if (["field_mls_list_price_value", "net_price", "buyer_incentives"].includes(key)) {
        if (isEmpty(v)) return DASH;
        const n = Number(typeof v === "string" ? v.replace(/[$,]/g, "") : v);
        return isNaN(n) || n === 0 ? DASH : `$${Math.round(n).toLocaleString()}`;
      }
      if (key === "psf" || key === "custom_net_psf") {
        if (isEmpty(v)) return DASH;
        const n = Number(typeof v === "string" ? v.replace(/[$,]/g, "") : v);
        if (isNaN(n) || n === 0) return DASH;
        return this.isMarketStrata
          ? `$${Math.round(n).toLocaleString()}`
          : `$${n.toFixed(2)}`;
      }
      if (key === "field_mls_unit_size_value") {
        const n = Number(v);
        return n && !isNaN(n) ? Math.round(n).toLocaleString() : DASH;
      }
      if (key === "custom_age") {
        if (v === 0 || v === "0") return "New";
        const n = Number(v);
        return !n || isNaN(n) ? DASH : `${n}d`;
      }
      return isEmpty(v) ? DASH : String(v);
    },
    refreshResults() {
      const vm = this;
      if (this.mlsData.filter((row) => row.rowSelected === true).length > 0)
        this.mlsData = this.mlsData.filter((row) => row.rowSelected === true);
    },
    async generateArrayOfImages() {
      const vm = this;
      vm.arrayOfImages = new Array();
      let promises = [];
      if (confirm("Include Floor Plans in the report?")) {
        vm.filteredMlsData.forEach((x) => {
          if (!x.floor_plan || !x.floor_plan.preview) return;

          promises.push(vm.getImageRatio(x.floor_plan.preview));

          vm.getImageRatio(x.floor_plan.preview).then((resolve) => {
            if (resolve.status == "success") {
              vm.arrayOfImages.push({
                project: {
                  name: x.name,
                  field_mls_address_value: x.field_mls_address_value,
                  price: this.roundToWholeNumber(
                    x.field_mls_list_price_value
                  ).toString(),
                  psf:
                    x.psf > 0 ? this.roundToWholeNumber(x.psf).toString() : "-",
                  priceDate: x.price_date,
                },
                image: resolve.image,
              });
            }
          });
        });
      }
      try {
        // Wait for all promises to complete
        await Promise.all(promises);

        // After all images are processed, generate the report
        await this.generateReport();

        // Show success toast once report is generated
      } catch (error) {
        // Handle any errors that occurred during the process
        console.error(
          "Error during image generation or report generation:",
          error
        );
        vm.$bvToast.toast(
          `${"Something went wrong. \n Please inform us to fix this for you."}`,
          {
            title: "Oops!",
            variant: "danger",
            autoHideDelay: 5000,
          }
        );
      }
    },
    selectAll: function () {
      if (!this.allSelected) {
        for (let report in this.mlsSort) {
          this.selectedSummary.push(this.mlsSort[report].entity_id);
        }
      } else {
        this.selectedSummary = [];
      }
    },
    fieldsOn: function () {
      // this.reportFields = [];
      // if (this.fields.length >= 10) {
      //     let poped = this.fields.pop();
      //     this.list2.push(poped);
      // } else {
      //     this.fields.forEach((val, key) => {
      //         this.reportFields.push(val.field);
      //     });
      // }
    },
    project_sort(column) {
      if (this.sortBy == column) {
        this.orderAsc = !this.orderAsc;
      }
      this.sortBy = column;
    },
    utcLink(utc) {
      let link =
        "/type?project=" +
        utc.city_entity_id +
        "&fid=" +
        utc.field_project_type_collection_value +
        "&sid=" +
        utc.field_utc_status_tid +
        "&uid=" +
        utc.field_unit_type_value;
      return link;
    },
    getAge(row) {
      let d2 = new Date();
      let d1 = new Date(row.occupancy_date);

      if (d1 == "Invalid Date") return "-";

      if (d1 > d2) return 0;
      // Calculate the total difference in months

      let totalMonths =
        (d2.getFullYear() - d1.getFullYear()) * 12 +
        (d2.getMonth() - d1.getMonth());

      // If the day of the second date is before the first, adjust the month count
      if (d2.getDate() < d1.getDate()) {
        totalMonths -= 1;
      }

      // If the difference is 0-12 months, round up to 1 year
      if (totalMonths <= 12) {
        return 1;
      }

      // Otherwise, round to the nearest whole number in years
      return Math.round(totalMonths / 12);
    },
    async setListingType(row) {
      const vm = this;
      let res = "Presale";
      let priceDate = new Date(row.price_date);
      let soldDate = new Date(row.sold_date);
      let constructionCompletionDate = new Date(row.occupancy_date);
      let soldOutDate = new Date(row.sold_out_date);
      try {
        let resHistory = await axios.get(
          BACKEND_URL + `api/get_mls_history?eid=${row.entity_id}`
        );
        let hasSoldStatusBeforeEntry;
        if (resHistory.data) {
          hasSoldStatusBeforeEntry = resHistory.data.some(
            (val, index) =>
              val["mls_status"] == "Sold" && index < resHistory.data.length - 1
          );
        }
        if (hasSoldStatusBeforeEntry) {
          if (
            [priceDate, soldDate].some(
              (date) => date < constructionCompletionDate
            )
          )
            res = "Assignment";

          if (
            [priceDate, soldDate].some(
              (date) => date > constructionCompletionDate
            )
          )
            res = "Resale";
        } else {
          if (
            [priceDate, soldDate].some(
              (date) =>
                date > soldOutDate && date < constructionCompletionDate
            )
          )
            res = "Assignment";

          if (
            [priceDate, soldDate].some(
              (date) =>
                date > soldOutDate && date > constructionCompletionDate
            )
          )
            res = "Resale";
        }

        if (
          ["now leasing", "leased out"].includes(row.status.toLowerCase())
        ) {
          switch (res) {
            case "Presale":
              res = "New Lease";
              break;

            case "Resale":
              res = "Re-Lease";
              break;
          }
        }

      } catch (error) {
        console.log(error);
      }
      return res;
    },


    // Enriches a raw MLS row (as returned by api/get_mls_list[_paged]) with the
    // derived fields the table formatters, KPIs and map rely on. Shared by the
    // full in-memory load (getMLSDetail) and the table's server-side infinite
    // scroll so both paths produce identical row shapes (DRY).
    mapMlsRow(mlsRow) {
      const vm = this;
      return {
        ...mlsRow,
        psfToString: this.roundToWholeNumber(mlsRow.psf).toString(),
        customData: {
          project: mlsRow.developer,
          projectName: mlsRow.name,
          href_link: this.utcLink(mlsRow),
        },
        customLocationData: {
          address: mlsRow.address,
          municipality: mlsRow.city,
          neighbour: mlsRow.neighbour,
        },
        custom_field_mls_beds_value: {
          type: mlsRow.field_mls_beds_value != null ?
            mlsRow.field_mls_beds_value == "s"
              ? "Studio"
              : mlsRow.field_mls_beds_value == "jr"
                ? "Jr 1" + "-bed"
                : mlsRow.field_mls_beds_value + "-bed" : '',
          fp_specs:
            mlsRow.fp_specs.toLocaleLowerCase() != "none"
              ? mlsRow.fp_specs
              : "",
        },
        custom_age: this.getAge(mlsRow),
        rowSelected: false,
        psf: !vm.isMarketStrata
          ? Number(mlsRow.psf).toFixed(2).toLocaleString()
          : Math.round(mlsRow.psf).toLocaleString(),
        buyer_incentives: mlsRow.buyer_incentives
          ? Math.round(mlsRow.buyer_incentives).toLocaleString()
          : "-",
        net_price: mlsRow.net_price
          ? Math.round(mlsRow.net_price)
          : "-",
        custom_net_psf: !vm.isMarketStrata
          ? Number(mlsRow.net_price / mlsRow.field_mls_unit_size_value)
            .toFixed(2)
            .toLocaleString()
          : Math.round(
            mlsRow.net_price / mlsRow.field_mls_unit_size_value
          ).toLocaleString(),
      };
    },

    // Beds label used by the type column, mirroring mapMlsRow's derivation but
    // for a raw history record's mls_beds value.
    historyBedsType(v) {
      if (v == null || v === "") return "";
      const s = String(v);
      if (s === "s") return "Studio";
      if (s === "jr") return "Jr 1-bed";
      return `${s}-bed`;
    },
    // Net price for a history snapshot: list price minus buyer incentives
    // (same rule as SummaryPopUp.getNetPrice).
    historyNetPrice(h) {
      const list = Math.round(Number(h.mls_list_price)) || 0;
      const inc = Math.round(Number(h.buyer_incentives)) || 0;
      const res = list - inc;
      return res ? res : "-";
    },
    // Maps one raw history record (api/get_mls_history) into a table row that
    // shares the parent unit's columns. Project/developer/location/project-status
    // are inherited from the parent for context; price/psf/size/status/date come
    // from the snapshot. `entity_id` is synthesized to stay unique (Tabulator's
    // row index) and `_isHistory` flags it for styling/exclusion.
    mapHistoryRow(h, parent, i) {
      const netPrice = this.historyNetPrice(h);
      const size = Number(h.mls_unit_size);
      let netPsf = "-";
      if (netPrice !== "-" && size) {
        const val = netPrice / size;
        netPsf = this.isMarketStrata
          ? Math.round(val).toLocaleString()
          : val.toFixed(2);
      }
      return {
        ...parent,
        entity_id: `${parent.entity_id}::h${i}`,
        _isHistory: true,
        _children: undefined,
        rowSelected: false,
        price_backup: h.price_back_up || "",
        field_mls_address_value: h.mls_address || parent.field_mls_address_value,
        field_mls_plan_name_value: h.mls_plan_name || "",
        custom_field_mls_beds_value: {
          type: this.historyBedsType(h.mls_beds),
          fp_specs: "",
        },
        field_mls_baths_value: h.mls_baths || "",
        field_mls_unit_size_value: h.mls_unit_size || "",
        field_mls_list_price_value: h.mls_list_price || "",
        psf: h.mls_psf || "",
        buyer_incentives: h.buyer_incentives
          ? Math.round(Number(h.buyer_incentives)).toLocaleString()
          : "-",
        net_price: netPrice,
        custom_net_psf: netPsf,
        price_date: h.price_date || h.mls_price_date || "-",
        sold_date: h.mls_sold_date || "-",
        sold_price: h.mls_sold_price || "-",
        status_name_name: h.mls_status || "",
        custom_age: "-",
        custom_listing_type: "",
      };
    },
    // Fetches api/get_mls_history for each supplied row (concurrency-limited so
    // a page of ~200 rows doesn't flood the browser/backend). When a row's
    // history is a non-empty array, the snapshots are mapped to child rows,
    // stored on row._children (so filter/sort re-renders keep them) and pushed
    // into the live table as expandable tree children. Fire-and-forget: the
    // page renders immediately and history fills in as it resolves.
    async fetchHistoriesForRows(rows) {
      const CONCURRENCY = 8;
      let idx = 0;
      const worker = async () => {
        while (idx < rows.length) {
          const row = rows[idx++];
          if (!row || !row.entity_id || row._historyLoaded) continue;
          row._historyLoaded = true;
          try {
            const res = await axios.get(
              BACKEND_URL + `api/get_mls_history?eid=${row.entity_id}`
            );
            const hist = Array.isArray(res.data) ? res.data : [];
            if (hist.length) {
              const children = hist.map((h, i) => this.mapHistoryRow(h, row, i));
              // addRowChildren writes the children onto this row's data object
              // (Tabulator holds it by reference), so we don't pre-assign here —
              // doing both would duplicate. Only when the row isn't rendered
              // (returned false) do we persist _children ourselves so a later
              // re-render picks it up.
              const table = this.$refs.mlsTable;
              const attached =
                table && table.addRowChildren
                  ? table.addRowChildren(row.entity_id, children)
                  : false;
              if (!attached) row._children = children;
            }
          } catch (error) {
            console.error(
              "fetchHistoriesForRows: get_mls_history failed for",
              row.entity_id,
              error
            );
          }
        }
      };
      await Promise.all(Array.from({ length: CONCURRENCY }, () => worker()));
    },

    // Resolves a raw row from the paged endpoint to its fully-enriched twin in
    // mlsData (so the worker-computed custom_listing_type and any KPI-side
    // mutations are reused). Falls back to a fresh enrichment when the full
    // load hasn't produced the row yet. Passed to the table as :row-mapper.
    enrichServerRow(raw) {
      const existing = this.mlsById[raw.entity_id];
      return existing || this.mapMlsRow(raw);
    },

    // Fired by MlsReportTable when the user scrolls onto the last row. Advance
    // the offset by one page (200) and load the next page via getMLSDetail.
    onReachedLastRow() {
      // Infinite scroll (paged fetch) is admin-only; non-admins load once.
      if (!this.isAdmin) return;
      if (this.isLoading) return;
      const nextOffset = this.mlsOffset + 100;
      // Stop once we've paged past the end of the result set.
      if (this.mlsTotal && nextOffset >= this.mlsTotal) return;
      this.mlsOffset = nextOffset;
      this.getMLSDetail(this.baseQueryParam);
    },

    // `inTable` keeps the indicator inside the table region instead of raising
    // the full-page loader — see fetchInvestorData.
    async getMLSDetail(params, { inTable = false } = {}) {
      if (this.isLoading) return; // guard against overlapping page loads
      const isInitial = this.mlsOffset === 0;
      let allData = [];
      if (isInitial) {
        if (inTable) this.tableLoading = true;
        else this.initialLoading = true;
      }
      this.isLoading = true;
      this.requestCounter.resetCounter();
      this.requestCounter.setRequestCounter(1);

      if (this.isAdmin) {
        // Admin only: paged infinite scroll. Fetch one page (200 rows) at the
        // current offset. mlsOffset === 0 is the initial load (replaces data);
        // when advanced via onReachedLastRow the page is appended. mlsTotal is
        // captured so paging can stop at the end of the result set.
        const limit = 100;
        const endPoint = `api/get_mls_list_paged?${params}limit=${limit}&offset=${this.mlsOffset}`;
        try {
          const res = await axios.get(BACKEND_URL + endPoint);
          if (res && res.data && Array.isArray(res.data.data)) {
            allData = res.data.data;
            if (res.data.pagination) this.mlsTotal = Number(res.data.pagination.total) || 0;
          }
        } catch (error) {
          console.error("getMLSDetail: failed to fetch MLS page", error);
        }
      } else {
        // Non-admin: legacy single unpaged call to get_mls_list (returns a bare
        // array). No infinite scroll — the whole result set loads at once.
        const endPoint = `api/get_mls_list?${params}`;
        try {
          const res = await axios.get(BACKEND_URL + endPoint);
          if (res && res.data && Array.isArray(res.data)) {
            allData = res.data;
          }
        } catch (error) {
          console.error("getMLSDetail: failed to fetch MLS list", error);
        }
      }
      if (allData.length > 0) {
        var processedCount = 0;
        let mappedData = allData
          .filter(
            (mlsRow) =>
              mlsRow.price_backup != "Educated Estimated" &&
              mlsRow.price_date.length > 0 && !mlsRow.ignore_mls
          )
          .map((mlsRow) => this.mapMlsRow(mlsRow));

        await Promise.resolve().then(result => {
          const processed = mappedData.map(mlsRow => {
            let priceGap = this.getPriceGap(mlsRow);
            return {
              ...mlsRow,
              custom_price_confidence:
                this.getPriceConfidence(priceGap, mlsRow)
            }
          });
          // Replace on the initial load, append on subsequent pages.
          this.mlsData = isInitial ? processed : this.mlsData.concat(processed);
          // Each row pulls its own price history (api/get_mls_history); non-empty
          // results are attached as expandable child rows. Deferred to nextTick
          // so the table has rendered the new rows (addRowChildren needs them
          // present); runs in the background so the page isn't blocked.
          this.$nextTick(() => this.fetchHistoriesForRows(processed));
          this.filtered_data = isInitial
            ? cloneDeep(mappedData)
            : this.filtered_data.concat(cloneDeep(mappedData));
          this.isLoading = false;
          this.initialLoading = false;
          this.tableLoading = false;
          this.requestCounter.incrementCompletedRequest();
          var priceDates = mappedData
            .filter((data) => data.price_date != "-")
            .map((item) => new Date(item.price_date));
          var soldDates = mappedData
            .filter((data) => data.sold_date != "-")
            .map((item) => new Date(item.sold_date));

          var priceDates = this.mlsData
            .filter((data) => !["-", "", null].includes(data.price_date))
            .map((item) => new Date(item.price_date));

          var soldDates = this.mlsData
            .filter((data) => data.sold_date != "-")
            .map((item) => new Date(item.sold_date));

          let maxDays = this.mlsData
            .filter((data) => data.price_date != "-")
            .reduce((max, item) => {
              let diffDays = this.getDiffDays(item.price_date);
              return diffDays > max ? diffDays : max;
            }, 0);

          let maxSoldDays = this.mlsData
            .filter((data) => data.sold_date != "-")
            .reduce((max, item) => {
              let diffDays = this.getDiffDays(item.sold_date);
              return diffDays > max ? diffDays : max;
            }, 0);
          this.rangeValue.max = maxDays;
          this.rangeValue.startDate = new Date(Math.min(...priceDates));
          this.rangeValue.endDate = new Date(Math.max(...priceDates));

          this.rangeValueSoldDate.max = maxSoldDays;
          this.rangeValueSoldDate.startDate = new Date(
            Math.min(...soldDates)
          );
          this.rangeValueSoldDate.endDate = new Date(
            Math.max(...soldDates)
          );

          this.loadFilters();
        });
      } else {
        this.isLoading = false;
        this.initialLoading = false;
        this.tableLoading = false;
        this.requestCounter.incrementCompletedRequest();
      }
    },
    popHistory(data) {
      const vm = this;
      vm.unitHistoryData = data;
      vm.mlsHistory = [];
      this.$bvModal.show('mlspop');

      let entry = "eid=" + data.entity_id;
      const endPoint = `api/get_mls_history?${entry}`;
      $.get(
        {
          url: BACKEND_URL + endPoint,
        },
        (data, status) => {
          if (status !== "success") return;
          this.mlsHistory = data;
        }
      );
      console.log("mlsHistory", this.mlsHistory);
    },
  },
  computed: {
    ...mapState(useUserStore, ["current_user"]),
    ...mapState(useUserDataStore, ["user_data"]),
    ...mapState(useRequestCounter, ["reqCounter", "getCounter"]),
    shownFieldIds() {
      return this.shownFields.map(f => f.id);
    },
    sortedGroupedBeds() {
      const parseSize = (v) =>
        typeof v === "string" ? Number(v.replace(/,/g, "")) || 0 : Number(v) || 0;
      return [...(this.groupedBeds || [])].sort(
        (a, b) => parseSize(a["Avg Size"]) - parseSize(b["Avg Size"])
      );
    },
    priceDateDays: {
      get() {
        return this.enablePricedate ? (Number(this.custom_filter.priceDate.priceDate_selected) || 0) : 0;
      },
      set(v) {
        if (v > 0) {
          this.enablePricedate = true;
          this.custom_filter.priceDate.priceDate_selected = String(v);
        } else {
          this.enablePricedate = false;
        }
      },
    },
    soldDateDays: {
      get() {
        return this.enableSoldDate ? (Number(this.custom_filter.soldDate.soldDate_selected) || 0) : 0;
      },
      set(v) {
        if (v > 0) {
          this.enableSoldDate = true;
          this.$set(this.custom_filter.soldDate, 'soldDate_selected', String(v));
        } else {
          this.enableSoldDate = false;
        }
      },
    },
    statusOpts() {
      return this.custom_filter.status.status_options.map(o => o.value || o.text || o.label);
    },
    listingOpts() {
      return this.custom_filter.listingType.listingType_options.map(o => o.value || o.text || o.label);
    },
    projectStatusOpts() {
      return this.custom_filter.projectStatus.projectStatus_options.map(o => o.value || o.text || o.label);
    },
    // entity_id -> enriched row lookup, used to resolve server-fetched rows
    // back to their fully-processed twin in mlsData (see enrichServerRow).
    mlsById() {
      const map = {};
      for (const row of this.mlsData) map[row.entity_id] = row;
      return map;
    },
    // True when any interactive filter or the free-text search is active. The
    // paged endpoint can't express these refinements (it even strips `q`), so
    // in that case the table falls back to the in-memory filtered dataset.
    hasActiveClientFilters() {
      const f = this.filter_rows || {};
      const searchActive = !!(f.filterText && String(f.filterText).trim().length);
      const arrActive = !!(f.filterArray && f.filterArray.length);
      const cf = this.custom_filter;
      const customActive = [
        cf.baths.baths_selected,
        cf.beds.beds_selected,
        cf.fpSpecs.fpSpecs_selected,
        cf.listingType.listingType_selected,
        cf.city.city_selected,
        cf.status.status_selected,
        cf.priceDate.priceDate_selected,
        cf.soldDate.soldDate_selected,
        cf.projectStatus.projectStatus_selected,
      ].some((x) => x && x.length > 0);
      return searchActive || arrActive || customActive || this.enablePricedate || this.enableSoldDate;
    },
    // Default (unfiltered) view -> table streams pages from the server. Once a
    // client-side filter/search is applied -> table renders in-memory data.
    // The Investor tab has no paged endpoint, so it always renders in memory.
    serverMode() {
      if (this.isInvestorReport) return false;
      return !this.hasActiveClientFilters;
    },
    serverUrl() {
      return BACKEND_URL + "api/get_mls_list_paged";
    },
    // True while a scroll-triggered page (offset > 0) is being fetched — drives
    // the inline "loading more" indicator, distinct from the initial PageLoader.
    isFetchingMore() {
      return this.isLoading && !this.initialLoading;
    },
    activeCount() {
      return this.filteredMlsData.filter(r => r.status_name_name === 'Active').length;
    },
    kpiAvgList() {
      const rows = this.filteredMlsData.filter(r => r.field_mls_list_price_value && !isNaN(Number(r.field_mls_list_price_value)));
      if (!rows.length) return '-';
      const avg = rows.reduce((s, r) => s + Number(r.field_mls_list_price_value), 0) / rows.length;
      return avg >= 1e6 ? `$${(avg / 1e6).toFixed(2)}M` : `$${Math.round(avg).toLocaleString()}`;
    },
    kpiAvgPsf() {
      const rows = this.filteredMlsData.filter(r => r.psf && !isNaN(Number(String(r.psf).replace(/,/g, ''))));
      if (!rows.length) return '-';
      const avg = rows.reduce((s, r) => s + Number(String(r.psf).replace(/,/g, '')), 0) / rows.length;
      return this.isMarketStrata
        ? `$${Math.round(avg).toLocaleString()}`
        : `$${avg.toFixed(2)}`;
    },
    kpiAvgNetPsf() {
      const rows = this.filteredMlsData.filter(r => r.custom_net_psf && !isNaN(Number(String(r.custom_net_psf).replace(/,/g, ''))));
      if (!rows.length) return '-';
      const avg = rows.reduce((s, r) => s + Number(String(r.custom_net_psf).replace(/,/g, '')), 0) / rows.length;
      return this.isMarketStrata
        ? `$${Math.round(avg).toLocaleString()}`
        : `$${avg.toFixed(2)}`;
    },
    kpiAvgNet() {
      const rows = this.filteredMlsData.filter(r => r.net_price && r.net_price !== '-' && !isNaN(Number(r.net_price)));
      if (!rows.length) return '-';
      const avg = rows.reduce((s, r) => s + Number(r.net_price), 0) / rows.length;
      return avg >= 1e6 ? `$${(avg / 1e6).toFixed(2)}M` : `$${Math.round(avg).toLocaleString()}`;
    },
    // Aggregates the currently filtered rows into per-quarter averages, grouped
    // by city (series) and quarter (x-axis), for EVERY metric at once. Quarters
    // come from each row's price_date; cities are capped to the top N by
    // valid-row count so the small-multiple charts stay readable. Returns one
    // entry in `metrics` per CHART_METRICS definition, each with its own series.
    cityQuarterChartData() {
      const rows = this.filteredMlsData || [];
      const cityMap = {}; // city -> { total, quarters: { qkey -> { metricKey -> {sum,count} } } }
      const quarterSort = {}; // qkey -> sortable value (year*4 + quarter)

      rows.forEach((r) => {
        if (r._isHistory) return;
        const city =
          r.city ||
          (r.customLocationData && r.customLocationData.municipality) ||
          "Unknown";
        const d = new Date(r.price_date);
        if (isNaN(d.getTime())) return;
        const q = Math.floor(d.getMonth() / 3) + 1;
        const year = d.getFullYear();
        const qkey = `Q${q} ${year}`;
        quarterSort[qkey] = year * 4 + q;

        if (!cityMap[city]) cityMap[city] = { total: 0, quarters: {} };
        cityMap[city].total += 1;
        const qCell =
          cityMap[city].quarters[qkey] || (cityMap[city].quarters[qkey] = {});
        CHART_METRICS.forEach((metric) => {
          const val = metric.get(r);
          if (!isFinite(val) || val <= 0) return;
          const mc = qCell[metric.key] || (qCell[metric.key] = { sum: 0, count: 0 });
          mc.sum += val;
          mc.count += 1;
        });
      });

      const quarters = Object.keys(quarterSort).sort(
        (a, b) => quarterSort[a] - quarterSort[b]
      );
      const allCities = Object.keys(cityMap).sort(
        (a, b) => cityMap[b].total - cityMap[a].total
      );
      // Explicit picks take precedence (keep only cities present in the data);
      // otherwise fall back to the top-N by unit count.
      const picked = this.chartSelectedCities.filter((c) => cityMap[c]);
      const cities = picked.length
        ? picked
        : allCities.slice(0, this.chartCityLimit);

      const metrics = CHART_METRICS.map((metric) => ({
        key: metric.key,
        label: metric.label,
        series: cities.map((city) => ({
          city,
          values: quarters.map((qk) => {
            const q = cityMap[city].quarters[qk];
            const cell = q && q[metric.key];
            return cell && cell.count ? cell.sum / cell.count : null;
          }),
        })),
      }));

      return {
        quarters,
        cities,
        metrics,
        allCities,
        totalCities: allCities.length,
      };
    },
    // Shared legend (city -> colour) rendered once above the chart grid, since
    // per-chart legends would be repeated five times.
    chartLegend() {
      return this.cityQuarterChartData.cities.map((city) => ({
        city,
        color: this.cityColor(city),
      }));
    },
    singleBedsVal() {
      const s = this.custom_filter.beds.beds_selected;
      return s.length === 1 ? s[0] : '';
    },
    singleBathsVal() {
      const s = this.custom_filter.baths.baths_selected;
      return s.length === 1 ? s[0] : '';
    },
    singleBedsLabel() {
      const v = this.singleBedsVal;
      if (!v) return 'All Types';
      const opt = this.custom_filter.beds.beds_options.find(o => o.id === v);
      return opt ? opt.label : 'All Types';
    },
    singleBathsLabel() {
      const v = this.singleBathsVal;
      if (!v) return 'Any Baths';
      const opt = this.custom_filter.baths.baths_options.find(o => o.id === v);
      return opt ? opt.label : 'Any Baths';
    },
    citiesDisplay() {
      const sel = this.custom_filter.city.city_selected;
      if (!sel || sel.length === 0) return 'All Cities';
      if (sel.length === 1) {
        for (const group of this.custom_filter.city.city_options) {
          if (group.id === sel[0]) return group.label;
          const child = group.children && group.children.find(c => c.id === sel[0]);
          if (child) return child.label;
        }
      }
      return `${sel.length} selected`;
    },
    activeFilterCount() {
      return this.filter_rows.filterArray.length;
    },
    featuresDisplay() {
      const sel = this.custom_filter.fpSpecs.fpSpecs_selected;
      if (!sel || sel.length === 0) return 'All Features';
      if (sel.length === 1) return sel[0];
      return `${sel.length} features`;
    },
    cityOptions() {
      const region = this.$route.query.region;
      if (region === 'alberta') return getAlbertaCities();
      if (region === 'eastern-canada') return EAST_COAST_CITIES;
      return [{ id: 'bc', label: 'British Columbia', children: BC_REGION_CITIES }];
    },
    visibleRows() {
      const items = this.filteredMlsData;

      const start = (this.currentPage - 1) * this.perPage;
      const end = start + this.perPage;

      return items.slice(start, end);
    },
    getUnitReportSummaryAverage() {
      const units = this.projects
      const totalUnits = this.totalRows;
      function calculateWeightedAverage(total, unitCount) {
        return total === 0 ? 0 : Number((unitCount / totalUnits) * total);
      }
      var result = units.map(unit => {
        return {
          units: unit.units,
          size: this.haveValue(unit.size) ? calculateWeightedAverage(unit.size, unit.units) : 0,
          list_price: this.haveValue(unit.list_price) ? calculateWeightedAverage(unit.list_price, unit.units) : 0,
          net_price: this.haveValue(unit.net_price) ? calculateWeightedAverage(unit.net_price, unit.units) : 0,
          psf: this.haveValue(unit.psf) ? calculateWeightedAverage(unit.psf, unit.units) : 0,
          net_psf: this.haveValue(unit.net_psf) ? calculateWeightedAverage(unit.net_psf, unit.units) : 0,
        }
      }).reduce((acc, curr) => {
        acc.units += curr.units;
        acc.size += Math.round(curr.size);
        acc.list_price += Math.round(curr.list_price);
        acc.net_price += Math.round(curr.net_price);
        acc.psf += curr.psf;
        acc.net_psf += curr.net_psf;
        return acc;
      }, {
        units: 0,
        size: 0,
        list_price: 0,
        net_price: 0,
        psf: 0,
        net_psf: 0,
      })

      return result
    },
    getGroupedBedsAverage() {
      const vm = this;
      const units = vm.groupedBeds
      const totalUnits = units.reduce((sum, unit) => sum + unit['# Units'], 0);
      function calculateWeightedAverage(total, unitCount) {
        let mappedData = vm.dataMapper(total);
        return mappedData === 0 ? 0 : Number((unitCount / totalUnits) * mappedData);
      }
      var result = units.map(unit => {
        return {
          units: unit['# Units'],
          size: this.haveValue(unit['Avg Size']) && unit['Avg Size'] != 'N/A' ? calculateWeightedAverage(unit['Avg Size'], unit['# Units']) : 0,
          conf_sold: this.haveValue(unit['Conf Sold']) ? unit['Conf Sold'] : 0,
          list_price: this.haveValue(unit['List Price']) && unit['List Price'] != 'N/A' ? calculateWeightedAverage(unit['List Price'], unit['# Units']) : 0,
          units_percentage: this.haveValue(unit['Units %']) ? unit['Units %'] : 0,
          sold_percentage: this.haveValue(unit['Sold %']) ? unit['Sold %'] : 0,
          net_price: this.haveValue(unit['Net Price']) && unit['Net Price'] != 'N/A' ? calculateWeightedAverage(unit['Net Price'], unit['# Units']) : 0,
          psf: this.haveValue(unit['$PSF']) && unit['$PSF'] != 'N/A' ? calculateWeightedAverage(unit['$PSF'], unit['# Units']) : 0,
          net_psf: this.haveValue(unit['Net $PSF']) && unit['Net $PSF'] != 'N/A' ? calculateWeightedAverage(unit['Net $PSF'], unit['# Units']) : 0,
        }
      }).reduce((acc, curr) => {
        acc.units += curr.units;
        acc.size += Math.round(curr.size);
        acc.list_price += Math.round(curr.list_price);
        acc.net_price += Math.round(curr.net_price);
        acc.psf += curr.psf;
        acc.net_psf += curr.net_psf;
        acc.conf_sold += curr.conf_sold;
        acc.units_percentage += curr.units_percentage;
        acc.sold_percentage += curr.sold_percentage;
        return acc;
      }, {
        units: 0,
        size: 0,
        list_price: 0,
        net_price: 0,
        psf: 0,
        net_psf: 0,
        conf_sold: 0,
        units_percentage: 0,
        sold_percentage: 0
      })

      return result
    },
    projects() {
      const vm = this;
      var data = vm.filteredMlsData;
      var groups = Object.groupBy(data, ({ name }) => name);
      var result = [];
      for (const group in groups) {
        var unitsWithListPrice = groups[group].filter(
          (item) =>
            item.field_mls_list_price_value &&
            !isNaN(Number(item.field_mls_list_price_value))
        ).length;
        var rowData = Array.isArray(groups[group])
          ? groups[group].reduce(
            (acc, curr) => {
              acc.units++;
              acc.size += this.haveValue(curr.field_mls_unit_size_value)
                ? Number(curr.field_mls_unit_size_value || 0)
                : 0;
              acc.filteredSize += this.haveValue(
                curr.field_mls_list_price_value
              )
                ? Number(curr.field_mls_unit_size_value || 0)
                : 0;
              acc.list_price += this.haveValue(
                curr.field_mls_list_price_value
              )
                ? Number(curr.field_mls_list_price_value || 0)
                : 0;
              acc.buyer_incentives += this.haveValue(curr.buyer_incentives)
                ? Number(curr.buyer_incentives || 0)
                : 0;
              acc.psf += this.haveValue(curr.psf) ? Number(curr.psf || 0) : 0;
              return acc;
            },
            {
              units: 0,
              size: 0,
              list_price: 0,
              psf: 0,
              buyer_incentives: 0,
              filteredSize: 0,
            }
          )
          : {
            units: 0,
            size: 0,
            list_price: 0,
            psf: 0,
            buyer_incentives: 0,
            filteredSize: 0,
          };

        let netPrice = Math.round(
          (rowData.list_price - rowData.buyer_incentives) / unitsWithListPrice
        );
        let result_psf = !this.isMarketStrata
          ? `${Number(rowData.list_price / rowData.filteredSize).toFixed(2)}`
          : `${Math.round(rowData.list_price / rowData.filteredSize)}`;
        let result_netpsf = !this.isMarketStrata
          ? `${Number(
            netPrice / (rowData.filteredSize / unitsWithListPrice)
          ).toFixed(2)}`
          : `${Math.round(
            netPrice / (rowData.filteredSize / unitsWithListPrice)
          )}`;
        result.push({
          name: group,
          units: rowData.units,
          size: Math.round(rowData.size / rowData.units),
          list_price: rowData.list_price
            ? rowData.list_price / unitsWithListPrice
            : `-`,
          psf: rowData.list_price ? result_psf : "-",
          net_price: this.haveValue(netPrice) && netPrice ? netPrice : "-",
          net_psf: this.haveValue(netPrice) && netPrice ? result_netpsf : "-",
        });
      }
      return result;
    },
    selectedCity() {
      const vm = this;
      if (
        vm.$route.query["city[]"].every((city) =>
          this.vanCities.some((vanCity) => city == vanCity.key)
        )
      )
        return "Van Island";
      if (
        vm.$route.query["city[]"].every((city) =>
          this.okaCities.some((vanCity) => city == vanCity.key)
        )
      )
        return "Okanagan";
      if (
        vm.$route.query["city[]"].every((key) =>
          vm.nFraserOptions.some((city) => city.value == key)
        )
      )
        return "North Fraser";
      if (
        vm.$route.query["city[]"].every((key) =>
          vm.sFraserOptions.some((city) => city.value == key)
        )
      )
        return "South Fraser";
    },
    showFlag() {
      return [
        "darcysrai@gmail.com",
        "mark.wang+urban@devradius.com",
        "erloucabilan94@gmail.com",
        "Erl@devbc.com",
        "darcy@gmail.com",
        "markdeveloperbc@test.com",
      ].includes(this.current_user.mail);
    },
    getViewCategory() {
      // The Investor tab carries its own saved-view slot so its field layout and
      // filters never overwrite the Unit Report's.
      if (this.isInvestorReport) return 24;
      switch (this.isMarketStrata) {
        case true:
          if (this.selectedCity == "Van Island") return 15;
          if (this.selectedCity == "Okanagan") return 16;
          if (this.selectedCity == "North Fraser") return 17;
          if (this.selectedCity == "South Fraser") return 18;
          break;

        case false:
          if (this.selectedCity == "Vancouver") return 19;
          if (this.selectedCity == "Okanagan") return 20;
          if (this.selectedCity == "North Fraser") return 21;
          if (this.selectedCity == "South Fraser") return 22;
          break;

        default:
          return 23;
      }
    },
    // Which of the two reports this page is showing. Route-driven so the tab is
    // linkable and Back walks the tabs (see switchReport).
    isInvestorReport() {
      return this.$route.query[REPORT_QUERY_KEY] === INVESTOR_REPORT_VALUE;
    },
    reportTitle() {
      return this.isInvestorReport ? "Investor Owned Rental Report" : "Unit Report";
    },
    // Investor rows are rental by definition, so the rate columns keep their two
    // decimals regardless of the unittype the caller arrived with.
    isMarketStrata() {
      if (this.isInvestorReport) return false;
      return this.$route.query["unittype[]"] === "2952";
    },
    gutType() {
      if (this.isInvestorReport) return 'rental';
      return this.$route.query["unittype[]"] === "2952" ? 'strata' : 'rental';
    },
    // The rows the filters, KPIs, table and exports all read from. Each tab
    // keeps its own fetched set so switching back doesn't refetch.
    sourceRows() {
      return this.isInvestorReport ? this.investorData : this.mlsData;
    },
    filteredMlsData: {
      get: function () {
        const vm = this;
        let data = vm.sourceRows;

        let res = data.filter((item) => {
          return Object.values(item).some((value) => {
            return String(value)
              .toLowerCase()
              .includes(vm.filter_rows.filterText.toLowerCase());
          });
        });

        const hasFilterArray = vm.filter_rows.filterArray.length > 0;
        const hasCustomFilters = [
          vm.custom_filter.baths.baths_selected,
          vm.custom_filter.beds.beds_selected,
          vm.custom_filter.fpSpecs.fpSpecs_selected,
          vm.custom_filter.listingType.listingType_selected,
          vm.custom_filter.city.city_selected,
          vm.custom_filter.status.status_selected,
          vm.custom_filter.priceDate.priceDate_selected,
          vm.custom_filter.soldDate.soldDate_selected,
          vm.custom_filter.projectStatus.projectStatus_selected
        ].some((x) => x && x.length > 0);

        if (hasFilterArray || hasCustomFilters || vm.enablePricedate || vm.enableSoldDate) {

          let bath = [];
          let type = [];
          let fp = [];
          let status = []; // Unit Status
          let cs = [];
          let projectType = [];
          let productType = [];
          let city = [];
          let subCity = [];

          // Parse General Filters
          vm.filter_rows.filterArray.forEach((val) => {
            if (val.startsWith("st_")) status.push(val.replace("st_", ""));
            if (val.startsWith("cs_")) cs.push(val.replace("cs_", ""));
            if (val.startsWith("pt_")) projectType.push(val.replace("pt_", ""));
            if (val.startsWith("pdt_")) productType.push(val.replace("pdt_", ""));
          });

          if (status.length > 0)
            res = res.filter((item) => status.includes(item.status_name_name));
          if (cs.length > 0)
            res = res.filter((item) => cs.includes(item.construction_status));
          if (projectType.length > 0)
            res = res.filter((item) => projectType.includes(item.project_type));
          if (productType.length > 0)
            res = res.filter((item) => productType.includes(item.product_type));

          // Iterate through custom_filter keys
          for (var item in vm.custom_filter) {
            const filterGroup = vm.custom_filter[item];

            // City Filtering (using BC_REGION_CITIES tree)
            if (filterGroup["city_selected"] && filterGroup["city_selected"].length > 0) {
              const { cities: citiesArr, neighbours: neighboursArr } = vm.getCitiesFromSelection(
                filterGroup["city_selected"], vm.cityOptions
              );
              city.push(...citiesArr);
              subCity.push(...neighboursArr);
              if (city.length > 0 || subCity.length > 0) {
                res = res.filter((row) => city.includes(row.city) || subCity.includes(row.neighbour));
              }
            }

            // Beds Filtering
            if (filterGroup["beds_selected"] && filterGroup["beds_selected"].length > 0) {
              filterGroup["beds_selected"].forEach((val) => {
                if (val.startsWith("bed_")) type.push(val.replace("bed_", ""));
              });
              res = res.filter((row) =>
                type.some((t) => {
                  if (t === row.custom_field_mls_beds_value.type && row.custom_field_mls_beds_value.fp_specs != "Jr Bed")
                    return true;
                  if (row.custom_field_mls_beds_value.fp_specs == "Jr Bed") {
                    return (
                      t.search(row.custom_field_mls_beds_value.type) > -1 &&
                      t.search(row.custom_field_mls_beds_value.fp_specs) > -1
                    );
                  }
                })
              );
            }

            // Baths Filtering
            if (filterGroup["baths_selected"] && filterGroup["baths_selected"].length > 0) {
              filterGroup["baths_selected"].forEach((val) => {
                if (val.startsWith("bath_")) bath.push(val.replace("bath_", ""));
              });
              res = res.filter((row) => bath.includes(row.field_mls_baths_value));
            }

            // FP Specs Filtering
            if (filterGroup["fpSpecs_selected"] && filterGroup["fpSpecs_selected"].length > 0) {
              filterGroup["fpSpecs_selected"].forEach((val) => fp.push(val));
              res = res.filter((row) =>
                fp.some((spec) => {
                  let split = row.fp_specs.split(",").map((s) => s.trim());
                  return split.includes(spec);
                })
              );
            }

            // Listing Type Filtering
            if (filterGroup["listingType_selected"] && filterGroup["listingType_selected"].length > 0) {
              res = res.filter((row) =>
                vm.custom_filter["listingType"]["listingType_selected"].some(
                  (val) => val == row.custom_listing_type
                )
              );
            }

            // Project Status Filtering
            if (filterGroup["projectStatus_selected"] && filterGroup["projectStatus_selected"].length > 0) {
              res = res.filter((row) =>
                vm.custom_filter["projectStatus"]["projectStatus_selected"].some(
                  (val) => val == row.status
                )
              );
            }

            // Status Filtering
            if (filterGroup["status_selected"] && filterGroup["status_selected"].length > 0) {
              res = res.filter((row) =>
                vm.custom_filter["status"]["status_selected"].some(
                  (val) => val == row.status_name_name
                )
              );
            }

            // Date Filtering Logic
            if (vm.isAdmin) {
              if (vm.enablePricedate) {
                res = res.filter((row) => {
                  return (
                    !["-", "", null].includes(row.price_date) &&
                    vm.toUTCMidnight(new Date(row.price_date)) >= vm.toUTCMidnight(new Date(vm.rangeValue.startDate)) &&
                    vm.toUTCMidnight(new Date(row.price_date)) <= vm.toUTCMidnight(new Date(vm.rangeValue.endDate))
                  );
                });
              }
              if (vm.enableSoldDate) {
                res = res.filter((row) => {
                  return (
                    !["-", "", null].includes(row.sold_date) &&
                    vm.toUTCMidnight(new Date(row.sold_date)) >= vm.toUTCMidnight(new Date(vm.rangeValueSoldDate.startDate)) &&
                    vm.toUTCMidnight(new Date(row.sold_date)) <= vm.toUTCMidnight(new Date(vm.rangeValueSoldDate.endDate))
                  );
                });
              }
            } else {
              if (vm.enablePricedate) {
                if (filterGroup["priceDate_selected"] && filterGroup["priceDate_selected"].length > 0) {
                  res = res.filter(
                    (row) =>
                      !["-", "", null].includes(row.price_date) &&
                      vm.getDiffDays(row.price_date) <= Number(vm.custom_filter.priceDate["priceDate_selected"])
                  );
                }
              }
              if (vm.enableSoldDate) {
                if (filterGroup["soldDate_selected"] && filterGroup["soldDate_selected"].length > 0) {
                  res = res.filter(
                    (row) =>
                      !["-", "", null].includes(row.sold_date) &&
                      vm.getDiffDays(row.sold_date) <= vm.custom_filter.soldDate["soldDate_selected"]
                  );
                }
              }
            }
          } // End loop
        }

        // The Investor tab's own area filter — its own control rather than
        // custom_filter.city, because it resolves against the BC region tree and
        // matches sub-communities on `neighbour`.
        if (vm.isInvestorReport && vm.citySelected.length > 0) {
          const { cities, neighbours } = vm.resolveCityFilter(vm.citySelected);
          const hasCity = cities.size > 0;
          const hasNeigh = neighbours.size > 0;
          if (hasCity || hasNeigh) {
            res = res.filter(
              (row) =>
                (hasCity && cities.has(row.city)) ||
                (hasNeigh && neighbours.has(row.neighbour))
            );
          }
        }

        vm.totalRows = res.length;
        if (vm.isInvestorReport) vm.getInvestorAvailablePricing(res);
        else vm.getAvailablePricing(res);

        return res;
      },

      set: function (newValue) {
        if (this.isInvestorReport) this.investorData = newValue;
        else this.mlsData = newValue;
      }
    },

    // ── Investor map panel ─────────────────────────────────────────────────
    // Rows shaped for <project-map>, which reads row.geo = { lat, lng }. Only
    // rows carrying valid coordinates are plotted.
    mapRows() {
      const vm = this;
      if (!this.isInvestorReport) return [];
      return (this.filteredMlsData || [])
        .map((row) => {
          const g = row.geo_location;
          if (!g) return null;
          const lat = parseFloat(g.lat);
          const lng = parseFloat(g.lng);
          if (isNaN(lat) || isNaN(lng)) return null;
          const cd = row.customData || {};
          const netPsf = vm.isRealValue(row.custom_net_psf)
            ? row.custom_net_psf
            : row.psf;
          return {
            ...row,
            uid: row.entity_id,
            geo: { lat, lng },
            name: row.name || cd.projectName || "",
            developer: row.developer || cd.project || "",
            // `status` is the project status here, which is what ProjectMap keys
            // its icon colour and badge off.
            status: row.status,
            project_type: row.project_type,
            total_net_psf: vm.isRealValue(netPsf) ? netPsf : null,
            total_units: null,
            ref_link: cd.href_link || null,
          };
        })
        .filter(Boolean);
    },
    listingSortLabel() {
      const labels = {
        total_net_psf: "PSF",
        field_mls_list_price_value: "Rent",
        name: "Name",
      };
      return labels[this.listingSortField] || "PSF";
    },
    sortedMapRows() {
      const field = this.listingSortField;
      const dir = this.listingSortDir === "desc" ? -1 : 1;
      const toNum = (v) =>
        v == null || v === "" || v === "-" || isNaN(parseFloat(v))
          ? null
          : parseFloat(v);
      return this.mapRows.slice().sort((a, b) => {
        if (field === "name") {
          return dir * String(a.name || "").localeCompare(String(b.name || ""));
        }
        const av = toNum(a[field]);
        const bv = toNum(b[field]);
        // Blanks to the bottom regardless of direction.
        if (av == null && bv == null) return 0;
        if (av == null) return 1;
        if (bv == null) return -1;
        return dir * (av - bv);
      });
    },
    allListingData: function () {
      const vm = this;
      let allData = new Array(...vm.mlsData);
      return allData.map((row, index) => {
        let clonedRow = {
          ...row,
          customData:
            "[" + row.customData.projectName + "] " + row.customData.project,
          customLocationData:
            "[" +
            row.customLocationData.address +
            "] " +
            row.customLocationData.municipality,
        };

        Object.keys(clonedRow).forEach((key) => {
          clonedRow[key] = !["-", 0, null, NaN, "0"].includes(clonedRow[key])
            ? clonedRow[key]
            : "-";
        });
        if (clonedRow["field_mls_beds_value"] && !vm.isAdmin) {
          let bed = "";
          let fpSpecs = "";
          bed = `${clonedRow["field_mls_beds_value"]}-bed`;

          if (!["None", ""].includes(clonedRow["fp_specs"]))
            fpSpecs = `${clonedRow["fp_specs"]}`;

          if (clonedRow["field_mls_beds_value"] == "s") bed = "Studio";

          clonedRow["test_sample"] = fpSpecs ? bed + " / " + fpSpecs : bed;
        }

        return clonedRow;
      });
    },
    isAdmin: function () {
      return this.current_user.role == "admin";
    },
    filteredFloorPlan: function () {
      return this.mlsData.filter((x) => x.floor_plan.length != 0);
    },
    mappedRow: {
      get: function () {
        return this.mlsData.map((mlsRow) => {
          return {
            ...mlsRow,
            customData: {
              project: mlsRow.developer,
              projectName: mlsRow.name,
              href_link: this.utcLink(mlsRow),
            },
            rowSelected: false,
          };
        });
      },
      set: function (newVal) {
        return newVal;
      },
    },
    mlsSort: {
      get: function () {
        let alphSorting = {
          "sort-uname": "name",
          location: "address",
          "sort-status": "status_name_name",
          "sort-unit": "field_mls_address_value",
          "sort-beds": "field_mls_beds_value",
          "sort-baths": "field_mls_baths_value",
          "sort-usize": "field_mls_unit_size_value",
          "sort-price": "price",
          "sort-psf": "psf",
          "sort-date": "price_date",
        };
        var vm = this;
        var ob = this.mlsData;
        Object.keys(alphSorting).forEach(function (k) {
          if (k == vm.sortBy) {
            if (vm.orderAsc) {
              ob = vm.mlsData.sort((a, b) =>
                a[alphSorting[k]] > b[alphSorting[k]]
                  ? 1
                  : b[alphSorting[k]] > a[alphSorting[k]]
                    ? -1
                    : 0
              );
            } else {
              ob = vm.mlsData.sort((a, b) =>
                a[alphSorting[k]] < b[alphSorting[k]]
                  ? 1
                  : b[alphSorting[k]] < a[alphSorting[k]]
                    ? -1
                    : 0
              );
            }
          }
        });
        return ob;
      },
      set: function (newValue) {
        this.mlsData = newValue;
      },
    },

    groupedBeds() {
      const vm = this;
      const data = this.filteredMlsData.map((value) => {
        return {
          ...value,
          custom_field_mls_beds_value: value.custom_field_mls_beds_value.type,
        };
      });
      const groups = [
        "custom_field_mls_beds_value",
        "field_mls_baths_value",
        "parking",
      ];
      const grouped = {};
      data.forEach((item) => {
        groups
          .reduce((acc, group, index) => {
            const key = item[group];

            acc[key] = acc[key] || (index + 1 === groups.length ? [] : {});
            return acc[key];
          }, grouped)
          .push(item);
      });
      function calculateAverage(arr) {
        var filteredArr = arr.filter(
          (value) => !["", 0, null, "-"].includes(value)
        );
        return !vm.isMarketStrata
          ? Number(
            filteredArr.reduce((sum, value) => sum + value, 0) / arr.length
          )
            .toFixed(2)
            .toLocaleString()
          : Math.round(
            filteredArr.reduce((sum, value) => sum + value, 0) / arr.length
          );
      }
      const mappedData = Object.keys(grouped).map((bedType) => {
        // Collect both 'None' and 'U/G' parking types for this bed type
        const units = Object.values(grouped[bedType]).flatMap((floor) => {
          return [
            "None",
            "U/G",
            "Pad",
            "Double",
            "Single",
            "Tandem",
            "Triple",
          ].flatMap((parking) => {
            return floor[parking] || []; // Collect units from 'None' and 'U/G'
          });
        });

        // If there are no units after collecting, just return an empty row.
        if (units.length === 0) {
          return {
            Type: `${bedType}`,
            Baths: "N/A",
            Pkg: "N/A",
            "# Units": 0,
            "Units %": 0,
            "Avg Size": "N/A",
            "Gross Price": "N/A",
            "Gross $PSF": "N/A",
            "Net Price": "N/A",
            "Net $PSF": "N/A",
          };
        }
        let unitsWithListPrice = units.filter(
          (item) =>
            !["", null, 0].includes(item.field_mls_list_price_value) &&
            item.field_mls_list_price_value
        );

        // Calculate averages for the collected units
        const avgSize = calculateAverage(
          unitsWithListPrice.map(
            (unit) => Number(unit.field_mls_unit_size_value) || 0
          )
        );
        const soldPrice = calculateAverage(
          unitsWithListPrice.map((unit) => Number(unit.sold_price) || 0)
        );
        const buyer_incentives = calculateAverage(
          unitsWithListPrice.map((unit) => Number(unit.buyer_incentives) || 0)
        );
        const soldPsf = !vm.isMarketStrata
          ? Number(soldPrice / avgSize).toFixed(2)
          : Math.round(soldPrice / avgSize);
        const avgPrice = calculateAverage(
          unitsWithListPrice.map(
            (unit) => Number(unit.field_mls_list_price_value) || 0
          )
        );
        const avgPSF = calculateAverage(
          unitsWithListPrice.map((unit) => vm.haveValue(unit.psf) ? Number(vm.dataMapper(unit.psf)) : 0)
        );
        const avgNetPrice = calculateAverage(
          unitsWithListPrice.map((unit) => unit.net_price || 0)
        );
        const confSold = unitsWithListPrice.reduce((acc, unit) => {
          return ["Sold", "Leased"].includes(unit.status) ? (acc += 1) : acc;
        }, 0);
        // Use the first unit's details for Baths and Parking
        const firstUnit = unitsWithListPrice[0] || {};
        const baths = firstUnit.field_mls_baths_value || "N/A"; // Default to 'N/A' if not available
        const parking = firstUnit.parking || "N/A"; // Default to 'N/A' if not available
        const net_sold_price = Math.round(soldPrice - buyer_incentives);
        const avgNetPSF = !vm.isMarketStrata
          ? Number(avgNetPrice / avgSize).toFixed(2)
          : Math.round(avgNetPrice / avgSize);
        const units_percentage = Math.round(Number(unitsWithListPrice.length / data.length) * 100)
        return {
          Type: `${bedType}`,
          Baths: baths,
          Pkg: parking,
          "# Units": vm.isAdmin ? units.length : unitsWithListPrice.length,
          "Units %": vm.haveValue(units_percentage) ? units_percentage + '%' : '-',
          "Avg Size":
            unitsWithListPrice.length > 0
              ? Math.round(avgSize).toLocaleString()
              : "N/A",
          "List Price":
            unitsWithListPrice.length > 0
              ? `$${Math.round(avgPrice).toLocaleString()}`
              : "N/A",
          $PSF: unitsWithListPrice.length > 0 ? `$${this.isMarketStrata ? Math.round(avgPSF).toLocaleString() : avgPSF}` : "N/A",
          "Net Price":
            unitsWithListPrice.length > 0
              ? `$${Math.round(avgNetPrice).toLocaleString()}`
              : "N/A",
          "Net $PSF":
            unitsWithListPrice.length > 0 && avgNetPSF != "NaN"
              ? `$${this.isMarketStrata ? Math.round(avgNetPSF).toLocaleString() : avgNetPSF}`
              : "N/A",
          Units: [...unitsWithListPrice],
          "Conf Sold": this.haveValue(confSold) ? confSold : '-',
          "Sold %": this.haveValue(confSold) ? Math.round(Number(confSold / units.length) * 100) + '%' : '-',
          "Sold Price":
            soldPrice > 0 ? `$${Math.round(soldPrice).toLocaleString()}` : "-",
          "Sold $PSF": soldPsf > 0 ? `$${soldPsf}` : "-",
          "Net Sold Price":
            net_sold_price > 0 ? `$${net_sold_price.toLocaleString()}` : "-",
          "Net Sold $PSF":
            Math.round(net_sold_price / avgSize) > 0
              ? `$${Math.round(net_sold_price / avgSize)}`
              : "-",
        };
      });
      return mappedData;
    },
  },
  watch: {
    mlsData: function (newVal) {
      this.totalRows = newVal.length;
    },
    // Tab switch: take the target report's field layout and filter vocabulary,
    // then load its rows the first time it is opened.
    isInvestorReport: {
      handler(isInvestor) {
        this.applyReportFields();
        this.addToFilterOptions();
        // Filters are per-report (different status / listing vocabularies), so a
        // switch starts from the target report's defaults rather than carrying
        // selections that may not exist on the other side.
        this.resetReportFilters();
        this.mapView = false;
        this.selectedListingUid = null;
        // A switch only swaps the table's rows, so it loads with the in-table
        // indicator rather than the full-page loader.
        if (isInvestor) {
          if (!this.investorLoaded) this.fetchInvestorData({ inTable: true });
        } else if (!this.mlsData.length) {
          this.mlsOffset = 0;
          this.getMLSDetail(this.baseQueryParam, { inTable: true });
        }
      },
    },
    visibleRows: {
      handler(newRows) {
        if (this.isInvestorReport) {
          // Listing type costs one request per unit, so fill only what is on
          // screen (the strata tab gets the same field from its worker).
          this.fillInvestorListingTypes(newRows);
          return;
        }
        const rowsNeedingProcessing = newRows.filter(row => !row.custom_price_confidence);
        this.processDataWithWorker(newRows);
      },
      // immediate: true Run on initial load
    },
    chartType() {
      if (this.chartModal) this.$nextTick(this.renderAllCharts);
    },
    chartSelectedCities: {
      deep: true,
      handler() {
        if (this.chartModal) this.$nextTick(this.renderAllCharts);
      },
    },
    chartCityColors: {
      deep: true,
      handler() {
        if (this.chartModal) this.$nextTick(this.renderAllCharts);
      },
    },
  },
  beforeDestroy() {
    this.stopChartThemeObserver();
    this.destroyCityChart();
  },
  mounted: function () {
    let param = "";
    var vm = this;
    vm.fetchUserData();
    let rangList = [
      "mlsbedsmax",
      "mlsbedsmin",
      "mlsbathsmax",
      "mlsbathsmin",
      "mlspricemax",
      "mlspricemin",
      "mlspricemax",
      "mlsunitmax",
      "mlsunitmin",
    ];
    vm.setRpdHeaderFields();
    rangList.forEach((d) => {
      if (vm.query[d]) {
        param += d + "=" + vm.query[d] + "&";
      }
    });
    let dateList = ["listmlsdate", "soldmlsdate"];
    dateList.forEach((d) => {
      if ($.isArray(vm.query[d + "[]"])) {
        vm.query[d + "[]"].forEach((a) => {
          param += d + "[]=" + a + "&";
        });
      } else if (vm.query[d + "[]"]) {
        param += d + "[]=" + vm.query[d + "[]"] + "&";
      }
    });

    if ($.isArray(vm.query["fp[]"])) {
      vm.query["fp[]"].forEach((a) => {
        param += "fp[]=" + a + "&";
      });
    } else {
      if (vm.query["fp[]"]) {
        param += "fp[]=" + vm.query["fp[]"] + "&";
      }
    }

    if ($.isArray(vm.query["city[]"])) {
      vm.query["city[]"].forEach((a) => {
        param += "city[]=" + a + "&";
      });
    } else {
      if (vm.query["city[]"]) {
        param += "city[]=" + vm.query["city[]"] + "&";
      }
    }

    if ($.isArray(vm.query["type[]"])) {
      vm.query["type[]"].forEach((a) => {
        param += "type[]=" + a + "&";
      });
    } else {
      if (vm.query["type[]"]) {
        param += "type[]=" + vm.query["type[]"] + "&";
      }
    }

    if ($.isArray(vm.query["status[]"])) {
      vm.query["status[]"].forEach((a) => {
        param += "status[]=" + a + "&";
      });
    } else {
      if (vm.query["status[]"]) {
        param += "status[]=" + vm.query["status[]"] + "&";
      }
    }
    if (!vm.isAdmin) {
      if ($.isArray(vm.query["mlsdate[]"])) {
        vm.query["mlsdate[]"].forEach((a) => {
          param += "mlsdate[]=" + a + "&";
        });
      } else {
        if (vm.query["mlsdate[]"]) {
          param += "mlsdate[]=" + vm.query["mlsdate[]"] + "&";
        }
      }
    }
    param += "unittype[]=" + vm.query["unittype[]"] + "&";
    vm.baseQueryParam = param;

    // The Fields panel and the table follow the active report; ALL_FIELDS is
    // only the right default for the Unit Report tab.
    this.applyReportFields();
    this.addToFilterOptions();

    // Each tab loads its own rows. Landing straight on ?rpt=investor must not
    // fire the strata request, and vice versa.
    if (this.isInvestorReport) this.fetchInvestorData();
    else this.getMLSDetail(param);

    this.filteredReportDetail = this.mlsData;
  },
};
</script>
<style scoped>
/* ── Page shell ── */
.rpd-mls-page {
  background: #F4F5F7;
  min-height: 100vh;
  font-family: 'Helvetica Light', Helvetica, Arial, sans-serif;
}

/* ── Content area ── */
.rpd-content {
  padding: 20px 28px 24px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

/* ── Page Header bar ── */
.rpd-ph {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  background: #fff;
  border-bottom: 1px solid #E9E8E9;
  padding: 14px 28px 16px;
}

.rpd-ph__left {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.rpd-ph__breadcrumb {
  font-family: 'Helvetica Light', Helvetica, Arial, sans-serif;
  font-size: 11px;
  color: #8A8A8A;
  display: flex;
  align-items: center;
  gap: 4px;
}

.rpd-ph__sep {
  font-size: 9px;
  color: #C9CCD2;
}

.rpd-ph__breadcrumb-cur {
  color: #12283F;
  font-weight: 500;
}

.rpd-ph__title-row {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.rpd-ph__title {
  font-family: Poppins, sans-serif;
  font-weight: 700;
  font-size: 22px;
  color: #12283F;
  margin: 0;
  line-height: 1.1;
}

.rpd-ph__summary-btn {
  font-family: 'Helvetica Light', Helvetica, Arial, sans-serif;
  font-size: 11px;
  font-weight: 500;
  background: #EDF2FE;
  color: #343376;
  border: none;
  padding: 3px 10px;
  border-radius: 999px;
  cursor: pointer;
  white-space: nowrap;
  transition: background 120ms cubic-bezier(0.2, 0, 0, 1);
}

.rpd-ph__summary-btn:hover {
  background: #dce8fd;
}

.rpd-ph__updated {
  font-family: 'Helvetica Light', Helvetica, Arial, sans-serif;
  font-size: 11px;
  color: #8A8A8A;
}

.rpd-ph__updated strong {
  color: #545454;
  font-weight: 500;
}

.rpd-ph__right {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

/* ── Report tabs (Unit Report / Investor Rental) ──
   The row sits at the bottom edge of the header and its underline lands on the
   header rule, so the active tab is joined to the table it loads. Each report
   owns a colour, exposed as --rpd-mode for the tab's underline and icon.
   Mirrors MarketOverview.vue's .ms-report-tabs. ── */
.rpd-report-tabs {
  display: flex;
  align-items: flex-end;
  gap: 2px;
  flex: 1 1 100%;
  min-width: 0;
  flex-wrap: wrap;
  row-gap: 4px;
  /* Bleeds to the header's padding edges so the underline meets the rule. */
  margin: 0 -28px -16px;
  padding: 0 28px;
}

.rpd-report-tabs--units {
  --rpd-mode: #12283f;
}

.rpd-report-tabs--investor {
  --rpd-mode: #343376;
}

html.dark .rpd-report-tabs--units {
  --rpd-mode: #4db7fe;
}

html.dark .rpd-report-tabs--investor {
  --rpd-mode: #a5a4e8;
}

.rpd-report-tab {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  padding: 0 15px 11px;
  border: none;
  border-bottom: 3px solid transparent;
  background: transparent;
  color: #6b7787;
  font-family: "Poppins", sans-serif;
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
  transition: color 0.12s cubic-bezier(0.2, 0, 0, 1),
    border-color 0.12s cubic-bezier(0.2, 0, 0, 1);
}

.rpd-report-tab:hover {
  color: #12283f;
}

.rpd-report-tab--active {
  color: #12283f;
  border-bottom-color: var(--rpd-mode);
}

.rpd-report-tab-icon {
  font-size: 13px;
  color: #8a97a8;
}

.rpd-report-tab--active .rpd-report-tab-icon {
  color: var(--rpd-mode);
}

html.dark .rpd-report-tab {
  color: #8ba3bd;
}

html.dark .rpd-report-tab:hover,
html.dark .rpd-report-tab--active {
  color: #e6edf6;
}

.rpd-ph__divider {
  width: 1px;
  height: 20px;
  background: #E9E8E9;
  flex-shrink: 0;
}

/* ── Icon button ── */
.rpd-icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 30px;
  background: #fff;
  border: 1px solid #E2E4EA;
  border-radius: 6px;
  color: #545454;
  cursor: pointer;
  transition: all 120ms cubic-bezier(0.2, 0, 0, 1);
  font-size: 12px;
  padding: 0;
}

.rpd-icon-btn:hover {
  background: #F4F5F7;
  border-color: #C9CCD2;
}

/* ── Ghost pill button ── */
.rpd-pill-ghost {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 11px;
  background: #fff;
  border: 1px solid #E2E4EA;
  border-radius: 6px;
  color: #545454;
  font-family: 'Helvetica Light', Helvetica, Arial, sans-serif;
  font-size: 11px;
  font-weight: 400;
  cursor: pointer;
  white-space: nowrap;
  transition: all 120ms cubic-bezier(0.2, 0, 0, 1);
}

.rpd-pill-ghost:hover {
  background: #F4F5F7;
  border-color: #C9CCD2;
  color: #12283F;
}

/* ── KPI accent stripes ── */
.rpd-kpi--navy {
  border-left: 3px solid #12283F;
}

.rpd-kpi--violet {
  border-left: 3px solid #343376;
}

/* ── Treeselect sizing ── */
.rpd-ts-city {
  width: 180px;
  flex-shrink: 0;
}

.rpd-ts-filter {
  width: 220px;
  flex-shrink: 0;
}

/* ── FpSpecsFilter sizing ── */
.rpd-fp-filter {
  flex-shrink: 0;
}

/* ── Native select dropdown ── */
.rpd-dropdown {
  position: relative;
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
}

.rpd-dropdown__select {
  appearance: none;
  -webkit-appearance: none;
  padding: 5px 28px 5px 10px;
  border: 1px solid #E2E4EA;
  border-radius: 6px;
  background: #fff;
  color: #545454;
  font-family: 'Helvetica Light', Helvetica, Arial, sans-serif;
  font-size: 11px;
  cursor: pointer;
  outline: none;
  transition: border-color 120ms cubic-bezier(0.2, 0, 0, 1);
  min-width: 100px;
}

.rpd-dropdown__select:hover {
  border-color: #C9CCD2;
}

.rpd-dropdown__caret {
  position: absolute;
  right: 8px;
  font-size: 9px;
  color: #8A8A8A;
  pointer-events: none;
}

/* ── Date slider wrapper ── */
.rpd-date-toggle-wrap {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  min-width: 0;
}

/* ── KPI Row ── */
.rpd-kpi-row {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 0;
}

.rpd-kpi {
  background: #fff;
  border: 1px solid #E9E8E9;
  border-radius: 10px;
  padding: 12px 20px;
  min-width: 140px;
  flex: 1;
}

.rpd-kpi__label {
  font-family: 'Helvetica Light', Helvetica, Arial, sans-serif;
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: #8A8A8A;
  margin-bottom: 4px;
}

.rpd-kpi__value {
  font-family: Poppins, sans-serif;
  font-weight: 700;
  font-size: 20px;
  color: #12283F;
  line-height: 1.1;
  font-variant-numeric: tabular-nums;
}

.rpd-kpi__sub {
  font-size: 11px;
  color: #8A8A8A;
  margin-top: 2px;
}

/* ── Toolbar ── */
.rpd-toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  background: #fff;
  border: 1px solid #E9E8E9;
  border-radius: 10px;
  flex-wrap: wrap;
}

.rpd-toolbar--r2 {
  padding: 8px 14px;
}

/* ── Search (matches MarketStrata design) ── */
.ms-search-wrap {
  position: relative;
  display: flex;
  align-items: center;
  width: 240px;
  padding: 6px 10px;
  border: 1px solid #E2E4EA;
  border-radius: 6px;
  background: #FAFBFD;
  gap: 8px;
  flex-shrink: 0;
}

.ms-search {
  flex: 1;
  border: none;
  outline: none;
  background: transparent;
  font-size: 12px;
  color: #12283F;
  font-family: inherit;
  min-width: 0;
}

.ms-search-icon {
  color: #8A8A8A;
  font-size: 12px;
  flex-shrink: 0;
}

/* ── Treeselect width ── */
.ms-treeselect {
  width: 200px;
  flex-shrink: 0;
}

/* ── Segmented Controls ── */
.rpd-seg {
  display: flex;
  align-items: center;
  gap: 6px;
}

.rpd-seg__label {
  font-family: 'Helvetica Light', Helvetica, Arial, sans-serif;
  font-size: 10px;
  color: #8A8A8A;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  white-space: nowrap;
}

.rpd-seg__btns {
  display: flex;
  gap: 0;
  border: 1px solid #E2E4EA;
  border-radius: 6px;
  padding: 2px;
  background: #FAFBFD;
}

.rpd-seg__btn {
  padding: 4px 10px;
  border: none;
  background: transparent;
  color: #545454;
  font-family: 'Helvetica Light', Helvetica, Arial, sans-serif;
  font-weight: 400;
  font-size: 11px;
  border-radius: 4px;
  cursor: pointer;
  white-space: nowrap;
  transition: all 120ms cubic-bezier(0.2, 0, 0, 1);
}

.rpd-seg__btn--on {
  background: #12283F;
  color: #fff;
  font-weight: 600;
}

/* ── Treeselect sizing helpers ── */
.rpd-ts-sm {
  width: 130px;
}

.rpd-ts-md {
  width: 220px;
}

.rpd-ts-lg {
  flex: 1;
  min-width: 200px;
}

/* ── Spacer ── */
.rpd-spacer {
  flex: 1;
}

/* ── Fields Button ── */
.rpd-fields-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 6px;
  background: #fff;
  color: #12283F;
  border: 1px solid #E2E4EA;
  font-family: Poppins, sans-serif;
  font-weight: 600;
  font-size: 11px;
  cursor: pointer;
  transition: all 120ms cubic-bezier(0.2, 0, 0, 1);
  white-space: nowrap;
}

.rpd-fields-btn--open {
  background: #12283F;
  color: #fff;
  border-color: #12283F;
}

/* ── Fields Panel ── */
.rpd-fields-panel {
  display: flex;
  gap: 0;
  background: #fff;
  border: 1px solid #E9E8E9;
  border-radius: 10px;
  padding: 16px;
  margin-bottom: 8px;
  position: relative;
  flex-wrap: wrap;
  gap: 16px;
}

.rpd-fp-col {
  flex: 1;
  min-width: 200px;
}

.rpd-fp-head {
  font-family: Poppins, sans-serif;
  font-weight: 600;
  font-size: 11px;
  color: #12283F;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.rpd-fp-reset {
  background: none;
  border: none;
  font-family: inherit;
  font-size: 11px;
  color: #343376;
  cursor: pointer;
  padding: 0;
}

.rpd-fp-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  min-height: 40px;
}

.rpd-fp-chip {
  padding: 4px 10px;
  border: 1px solid #E2E4EA;
  border-radius: 6px;
  font-size: 11px;
  color: #12283F;
  background: #FAFBFD;
  cursor: grab;
  user-select: none;
}

.rpd-fp-chip--shown {
  background: #EDF2FE;
  border-color: #343376;
  color: #343376;
}

.rpd-fp-bar {
  width: 1px;
  background: #E9E8E9;
  align-self: stretch;
  flex-shrink: 0;
}

.rpd-fp-close {
  position: absolute;
  top: 12px;
  right: 12px;
  background: none;
  border: none;
  cursor: pointer;
  color: #A8323F;
  font-size: 18px;
  padding: 0;
}

/* ── Legacy treeselect multi-value ── */
.vue-treeselect__multi-value-item {
  background: #426586 !important;
  border-radius: 7px;
  color: #fff !important;
  font-size: 0.8rem;
  font-weight: bold;
}

th>div {
  position: relative;
}

th>div>span {
  white-space: nowrap;
}

th>div>div {
  position: absolute;
}

fa {
  font-size: 18px;
}

th ul {
  border: 1px solid #eee;
  border-radius: 4px;
  position: absolute;
  left: 50%;
  top: 24px;
  transform: translateX(-50%);
  overflow: hidden;
}

.visible {
  max-height: 100px;
  transition: max-height 0.3s;
}

.invisible {
  max-height: 0px;
  transition: max-height 0.3s;
}

.rotate-180 {
  transform: rotate(180deg);
  transition: transform 0.3s;
}

.rotate-360 {
  transform: rotate(360deg);
  transition: transform 0.3s;
}

th ul li {
  background: #fff;
  padding: 8px 16px;
  border-bottom: 1px solid #dee2e6;
  color: #212529;
  text-transform: none;
  font-size: 12px;
  white-space: nowrap;
  height: 32px;
  text-align: left;
  cursor: pointer;
}

th ul li:hover {
  background: #4db7fe;
  color: #fff;
}

th ul li:last-child {
  border-bottom: none;
}

.selected {
  background: #4db7fe;
  color: #fff;
}

a {
  color: #007bff;
}

.status-btn {
  background: #eef4fb;
  border-radius: 25px;
  font-size: 20px;
  line-height: 18px;
  color: #415a8f;
  padding: 10px 30px;
  margin-right: 30px;
  display: inline-block;
}

.status-row {
  margin-top: 20px;
  margin-bottom: 50px;
  text-align: left;
}

.status-line {
  line-height: 5;
}

.summary-table-header-row {
  cursor: pointer;
}

.list-table thead.summary-table-header tr.summary-table-header-row th {
  top: 0;
  padding: 2.5rem 1.5rem;
  font-family: Poppins;
  font-weight: 500;
  font-size: 1.2rem;
  z-index: 98;
  vertical-align: middle;
  position: sticky;
  background: #476e93;
  color: #fff;
  line-height: 24px;
}

.table.list-table tbody tr td {
  font-family: Poppins;
  font-weight: 300;
  font-size: 1.2rem;
  line-height: 30px;
  padding: 1.5rem;
  vertical-align: middle;
  border-bottom: 2px solid #545454;
  border-top: none;
}

.print-none {
  cursor: pointer;
  height: fit-content;
  max-width: 50px;
}

.fields-toggle-title {
  color: #13293e;
  font-size: 1.4rem;
  text-align: left;
  font-weight: bold;
  margin-bottom: 10px;
}

.fields-toggle-wrapper {
  zoom: 1.4;
  border: 3px #13293e solid;
  position: absolute;
  right: 70px;
  width: 450px;
  z-index: 999;
  background: #f8f7f7;
  margin-top: 56px;
  padding: 30px;
}

.selected-fields .list-group-item {
  background: #fc5868;
  color: #fff;
}

.fields-toggle-bar {
  border-bottom: 3px solid #13293e;
  margin-left: -30px;
  margin-right: -30px;
  margin-top: 20px;
  margin-bottom: 20px;
}

.list-group-item {
  background: #d9d9d9;
  color: #13293e;
  font-size: 0.9rem;
  border: none;
}

.list-group {
  flex-direction: inherit;
}

.list-group-item {
  width: calc(50% - 20px);
  margin-left: 10px;
  margin-right: 10px;
  margin-bottom: 10px;
}

input._red-checkbox {
  accent-color: #ff033e;
  height: 30px !important;
  width: 30px !important;
}

h1 {
  font-size: 3rem;
  font-family: Poppins;
  font-weight: 600;
}

/* MOBILE STYLING */
@media only screen and (max-width: 976px) {
  .print-none {
    width: 100%;
    max-width: 35px;
    height: 40px;
  }
}

.modal-body {
  justify-content: center;
}

.mls-floorplan {
  color: #4d7ce1;
  text-decoration: underline;
}

.close {
  color: white !important;
}

/* END OF MOBILE STYLING */

/* ── Fields Button ── */
.ur-fields-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: #fff;
  border: 1px solid #E2E4EA;
  border-radius: 6px;
  font-family: 'Poppins', sans-serif;
  font-weight: 600;
  font-size: 11px;
  color: #12283F;
  cursor: pointer;
  transition: all 120ms ease;
}

.ur-fields-btn.active {
  background: #12283F;
  color: #fff;
  border-color: #12283F;
}

/* ── Fields Panel ── */
.ur-fields-panel {
  background: #fff;
  border: 1px solid #E9E8E9;
  border-radius: 10px;
  padding: 16px 18px 18px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  animation: ur-fade-in 180ms ease;
}

@keyframes ur-fade-in {
  from {
    opacity: 0;
    transform: translateY(-4px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.ur-fp-hd {
  display: flex;
  align-items: center;
  gap: 10px;
}

.ur-spacer {
  flex: 1;
}

.ur-fp-title {
  font-family: 'Poppins', sans-serif;
  font-weight: 700;
  font-size: 13px;
  color: #12283F;
}

.ur-fp-hint {
  font-size: 11px;
  color: #8A8A8A;
}

.ur-fp-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  background: transparent;
  border: none;
  font-size: 11px;
  color: #343376;
  cursor: pointer;
  border-radius: 4px;
  font-family: inherit;
  transition: background 80ms;
}

.ur-fp-link:hover {
  background: #F4F5F7;
}

.ur-icon-btn-sm {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: #fff;
  border: 1px solid #E2E4EA;
  border-radius: 6px;
  color: #545454;
  cursor: pointer;
}

.ur-fp-cols {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
}

.ur-fp-col-wrap {
  background: #FAFBFD;
  border: 1px dashed #E2E4EA;
  border-radius: 10px;
  padding: 12px 14px 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-height: 100px;
}

.ur-fp-col-hd {
  display: flex;
  align-items: center;
  gap: 8px;
}

.ur-fp-col-bar {
  width: 4px;
  height: 14px;
  border-radius: 1px;
  flex-shrink: 0;
}

.ur-fp-col-title {
  font-family: 'Poppins', sans-serif;
  font-weight: 700;
  font-size: 10px;
  color: #12283F;
  text-transform: uppercase;
  letter-spacing: 0.12em;
}

.ur-fp-col-meta {
  font-size: 10px;
  color: #8A8A8A;
}

.ur-fp-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  min-height: 40px;
}

.ur-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 8px 5px 6px;
  border-radius: 6px;
  font-family: 'Poppins', sans-serif;
  font-weight: 500;
  font-size: 11px;
  cursor: grab;
  user-select: none;
  border: 1px solid transparent;
}

.ur-chip--filled {
  background: #12283F;
  color: #fff;
  border-color: #12283F;
}

.ur-chip--outline {
  background: #fff;
  color: #12283F;
  border-color: #E2E4EA;
  cursor: pointer;
}

.ur-chip-num {
  font-size: 10px;
  color: rgba(255, 255, 255, 0.65);
  font-variant-numeric: tabular-nums;
  min-width: 14px;
  text-align: right;
}

.ur-chip-x {
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.65);
  cursor: pointer;
  font-size: 14px;
  line-height: 1;
  padding: 0 0 0 2px;
}

.ur-chip-plus {
  background: transparent;
  border: none;
  color: #8A8A8A;
  cursor: pointer;
  font-size: 14px;
  line-height: 1;
  padding: 0 0 0 2px;
}

.ur-chip-pin {
  padding: 1px 5px;
  background: rgba(255, 255, 255, 0.18);
  border-radius: 3px;
  font-size: 8px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.8);
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

/* ── Cities MultiSelect ── */
.rpd-ms {
  position: relative;
  flex-shrink: 0;
}

.rpd-ms__btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  background: #fff;
  border: 1px solid #E2E4EA;
  border-radius: 6px;
  font-family: 'Helvetica Light', Helvetica, Arial, sans-serif;
  font-size: 12px;
  color: #12283F;
  cursor: pointer;
  white-space: nowrap;
  transition: border-color 120ms;
}

.rpd-ms__btn--active {
  border-color: #343376;
  color: #343376;
}

.rpd-ms__backdrop {
  position: fixed;
  inset: 0;
  z-index: 10;
}

.rpd-ms__menu {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  z-index: 11;
  background: #fff;
  border: 1px solid #E2E4EA;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(18, 40, 63, 0.10);
  padding: 4px;
  min-width: 180px;
  max-height: 320px;
  overflow-y: auto;
}

.rpd-ms__group-hd {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px 5px;
  cursor: pointer;
  border-radius: 4px;
}

.rpd-ms__group-hd:hover {
  background: #F4F5F7;
}

.rpd-ms__group-label {
  font-family: Poppins, sans-serif;
  font-weight: 600;
  font-size: 11px;
  color: #12283F;
}

.rpd-ms__item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 5px 10px 5px 24px;
  cursor: pointer;
  border-radius: 4px;
  font-family: 'Helvetica Light', Helvetica, Arial, sans-serif;
  font-size: 12px;
  color: #12283F;
}

.rpd-ms__item:hover {
  background: #F4F5F7;
}

.rpd-ms__check {
  width: 14px;
  height: 14px;
  border: 1px solid #C9CCD2;
  border-radius: 3px;
  background: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.rpd-ms__check--on {
  background: #12283F;
  border-color: #12283F;
}

/* ── More Filters Panel ── */
.rpd-mf-panel {
  background: #fff;
  border: 1px solid #E9E8E9;
  border-radius: 10px;
  padding: 12px 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.rpd-mf-groups {
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
}

.rpd-mf-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.rpd-mf-label {
  font-family: 'Helvetica Light', Helvetica, Arial, sans-serif;
  font-size: 10px;
  color: #8A8A8A;
  text-transform: uppercase;
  letter-spacing: 0.1em;
}

.rpd-mf-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.rpd-tag-chip {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 4px;
  background: #fff;
  border: 1px solid #C9CCD2;
  color: #12283F;
  font-family: 'Helvetica Light', Helvetica, Arial, sans-serif;
  font-size: 11px;
  cursor: pointer;
  transition: all 120ms;
}

.rpd-tag-chip--on {
  background: #12283F;
  border-color: #12283F;
  color: #fff;
}

.rpd-mf-clear {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 8px;
  background: transparent;
  border: none;
  color: #D43847;
  font-family: 'Helvetica Light', Helvetica, Arial, sans-serif;
  font-size: 11px;
  cursor: pointer;
  align-self: flex-start;
  border-radius: 4px;
}

.rpd-mf-clear:hover {
  background: #FEF2F2;
}

/* ── Ghost pill active state + count badge ── */
.rpd-pill-ghost--active {
  background: #EDF2FE;
  border-color: #343376;
  color: #343376;
}

.rpd-pill-ghost__count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 16px;
  height: 16px;
  background: #343376;
  color: #fff;
  border-radius: 999px;
  font-size: 9px;
  font-weight: 600;
  padding: 0 4px;
}

/* ── Styled Dropdown (UnitDropdown-style) ── */
.rpd-udrop {
  position: relative;
  flex-shrink: 0;
}

.rpd-udrop__btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  background: #fff;
  border: 1px solid #E2E4EA;
  border-radius: 6px;
  font-family: 'Helvetica Light', Helvetica, Arial, sans-serif;
  font-size: 12px;
  color: #12283F;
  cursor: pointer;
  white-space: nowrap;
  transition: border-color 120ms;
}

.rpd-udrop__btn--active {
  border-color: #343376;
  color: #343376;
}

.rpd-udrop__backdrop {
  position: fixed;
  inset: 0;
  z-index: 10;
}

.rpd-udrop__menu {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  z-index: 11;
  background: #fff;
  border: 1px solid #E2E4EA;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(18, 40, 63, 0.10);
  padding: 4px;
  min-width: 140px;
  max-height: 280px;
  overflow-y: auto;
}

.rpd-udrop__opt {
  display: block;
  width: 100%;
  text-align: left;
  padding: 7px 10px;
  background: transparent;
  border: none;
  border-radius: 4px;
  font-family: 'Helvetica Light', Helvetica, Arial, sans-serif;
  font-size: 12px;
  color: #12283F;
  cursor: pointer;
  transition: background 80ms;
}

.rpd-udrop__opt:hover {
  background: #F4F5F7;
}

.rpd-udrop__opt--on {
  background: #EDF2FE;
  color: #343376;
  font-weight: 500;
}

/* ── Price Date Slider (non-admin) ── */
.rpd-pd {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 5px 10px;
  background: #fff;
  border: 1px solid #E2E4EA;
  border-radius: 6px;
  min-width: 120px;
  transition: all 120ms;
  flex-shrink: 0;
}

.rpd-pd--on {
  background: #EDF2FE;
  border-color: #343376;
  min-width: 220px;
}

.rpd-pd__toggle-btn {
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
}

.rpd-pd__label {
  font-family: 'Helvetica Light', Helvetica, Arial, sans-serif;
  font-size: 10px;
  color: #8A8A8A;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  white-space: nowrap;
}

.rpd-pd__days {
  font-family: Poppins, sans-serif;
  font-weight: 600;
  font-size: 11px;
  color: #343376;
  white-space: nowrap;
}

.rpd-pd__range {
  flex: 1;
  min-width: 60px;
  accent-color: #343376;
  height: 3px;
  cursor: pointer;
}

/* ── Date Toggle button (non-admin sold date) ── */
.rpd-dt-toggle {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 5px 10px;
  background: #fff;
  border: 1px solid #E2E4EA;
  border-radius: 6px;
  font-family: 'Helvetica Light', Helvetica, Arial, sans-serif;
  font-size: 11px;
  color: #12283F;
  cursor: pointer;
  white-space: nowrap;
  transition: all 120ms;
}

.rpd-dt-toggle--on {
  background: #EDF2FE;
  border-color: #343376;
}

/* ── Toggle track + thumb (shared) ── */
.rpd-toggle-track {
  display: inline-block;
  width: 22px;
  height: 12px;
  border-radius: 999px;
  background: #D0D4DC;
  position: relative;
  transition: background 120ms;
  flex-shrink: 0;
}

.rpd-toggle-track--on {
  background: #1F9D55;
}

.rpd-toggle-thumb {
  position: absolute;
  top: 1px;
  left: 1px;
  width: 10px;
  height: 10px;
  background: #fff;
  border-radius: 50%;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.15);
  transition: left 140ms cubic-bezier(0.2, 0, 0, 1);
}

.rpd-toggle-thumb--on {
  left: 11px;
}

/* ── Dark mode ── */
html.dark .rpd-mls-page {
  background: #0b1625;
}

html.dark .rpd-ph {
  background: #0e1b2c;
  border-bottom-color: rgba(255, 255, 255, 0.07);
}

html.dark .rpd-ph__breadcrumb {
  color: rgba(255, 255, 255, 0.35);
}

html.dark .rpd-ph__sep {
  color: rgba(255, 255, 255, 0.2);
}

html.dark .rpd-ph__breadcrumb-cur {
  color: rgba(255, 255, 255, 0.7);
}

html.dark .rpd-ph__title {
  color: #d6e4f0;
}

html.dark .rpd-ph__updated {
  color: rgba(255, 255, 255, 0.35);
}

html.dark .rpd-ph__updated strong {
  color: rgba(255, 255, 255, 0.65);
}

html.dark .rpd-ph__divider {
  background: rgba(255, 255, 255, 0.08);
}

html.dark .rpd-ph__summary-btn {
  background: rgba(52, 51, 118, 0.25);
  color: #93c5fd;
}

html.dark .rpd-icon-btn {
  background: #152030;
  border-color: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.6);
}

html.dark .rpd-icon-btn:hover {
  background: #1d3048;
  border-color: rgba(255, 255, 255, 0.2);
}

html.dark .rpd-pill-ghost {
  background: #152030;
  border-color: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.6);
}

html.dark .rpd-pill-ghost:hover {
  background: #1d3048;
  color: #d6e4f0;
}

html.dark .rpd-pill-ghost--active {
  background: rgba(52, 51, 118, 0.3);
  border-color: rgba(52, 51, 118, 0.6);
  color: #93c5fd;
}

html.dark .rpd-pill-ghost__count {
  background: #343376;
}

html.dark .rpd-kpi {
  background: #0e1b2c;
  border-color: rgba(255, 255, 255, 0.07);
}

html.dark .rpd-kpi__label {
  color: rgba(255, 255, 255, 0.4);
}

html.dark .rpd-kpi__value {
  color: #d6e4f0;
}

html.dark .rpd-kpi__sub {
  color: rgba(255, 255, 255, 0.3);
}

html.dark .rpd-toolbar {
  background: #0e1b2c;
  border-color: rgba(255, 255, 255, 0.07);
}

html.dark .ms-search-wrap {
  background: #152030;
  border-color: rgba(255, 255, 255, 0.1);
}

html.dark .ms-search {
  color: #d6e4f0;
}

html.dark .ms-search::placeholder {
  color: rgba(255, 255, 255, 0.25);
}

html.dark .ms-search-icon {
  color: rgba(255, 255, 255, 0.3);
}

html.dark .rpd-seg__label {
  color: rgba(255, 255, 255, 0.35);
}

html.dark .rpd-seg__btns {
  background: #152030;
  border-color: rgba(255, 255, 255, 0.08);
}

html.dark .rpd-seg__btn {
  color: rgba(255, 255, 255, 0.45);
}

html.dark .rpd-seg__btn--on {
  background: #d6e4f0;
  color: #0e1b2c;
}

html.dark .rpd-udrop__btn {
  background: #152030;
  border-color: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.65);
}

html.dark .rpd-udrop__btn--active {
  border-color: rgba(52, 51, 118, 0.6);
  color: #93c5fd;
}

html.dark .rpd-udrop__menu {
  background: #0e1b2c;
  border-color: rgba(255, 255, 255, 0.1);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.4);
}

html.dark .rpd-udrop__opt {
  color: rgba(255, 255, 255, 0.65);
}

html.dark .rpd-udrop__opt:hover {
  background: rgba(255, 255, 255, 0.05);
}

html.dark .rpd-udrop__opt--on {
  background: rgba(52, 51, 118, 0.25);
  color: #93c5fd;
}

html.dark .rpd-mf-panel {
  background: #0e1b2c;
  border-color: rgba(255, 255, 255, 0.07);
}

html.dark .rpd-mf-label {
  color: rgba(255, 255, 255, 0.35);
}

html.dark .rpd-tag-chip {
  background: #152030;
  border-color: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.65);
}

html.dark .rpd-tag-chip--on {
  background: #d6e4f0;
  border-color: #d6e4f0;
  color: #0e1b2c;
}

html.dark .rpd-mf-clear {
  color: #f87171;
}

html.dark .rpd-mf-clear:hover {
  background: rgba(248, 113, 113, 0.1);
}

html.dark .rpd-dropdown__select {
  background: #152030;
  border-color: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.65);
}

/* ── Investor Rental map view ─────────────────────────────────────────────
   Two columns: the listing list keeps its own scroll beside a map that fills
   the rest. Stacks under 1100px so the map keeps a usable height on narrow
   viewports rather than being squeezed into a strip. ── */
.rpd-map-layout {
  display: flex;
  gap: 14px;
  align-items: stretch;
  height: calc(100vh - 320px);
  min-height: 480px;
  margin-bottom: 12px;
}

.rpd-map-host {
  flex: 1 1 0;
  min-width: 0;
  min-height: 0;
  position: relative;
  display: flex;
  border: 1px solid #e9e8e9;
  border-radius: 10px;
  overflow: hidden;
}

.rpd-lp-list {
  flex: 0 0 32%;
  min-width: 300px;
  max-width: 460px;
  min-height: 0;
  background: #fff;
  border: 1px solid #e9e8e9;
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.rpd-lp-list-header {
  flex: 0 0 auto;
  padding: 10px 14px;
  border-bottom: 1px solid #eef0f4;
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
}

.rpd-lp-list-title {
  font-family: "Poppins", sans-serif;
  font-weight: 600;
  font-size: 13px;
  color: #12283f;
  letter-spacing: -0.01em;
}

.rpd-lp-list-count {
  font-size: 11px;
  color: #8a8a8a;
  margin-top: 1px;
}

.rpd-lp-list-count strong {
  color: #12283f;
  font-weight: 500;
}

.rpd-lp-sort {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 26px;
  padding: 0 9px;
  background: #fff;
  border: 1px solid #e2e4ea;
  border-radius: 6px;
  color: #545454;
  font-size: 11px;
  font-family: inherit;
  cursor: pointer;
  white-space: nowrap;
  transition: all 120ms cubic-bezier(0.2, 0, 0, 1);
}

.rpd-lp-sort:hover {
  background: #f7f8fb;
  border-color: #c9ccd2;
  color: #12283f;
}

.rpd-lp-list-body {
  flex: 1;
  overflow-y: auto;
}

.rpd-lp-item {
  display: flex;
  gap: 12px;
  padding: 11px 14px;
  border-bottom: 1px solid #eef0f4;
  border-left: 3px solid transparent;
  background: #fff;
  cursor: pointer;
  transition: background 120ms cubic-bezier(0.2, 0, 0, 1);
}

.rpd-lp-item:hover {
  background: #fafbfd;
}

.rpd-lp-item--selected {
  background: #eef3f8;
  border-left-color: #6c90b6;
}

.rpd-lp-thumb {
  flex: 0 0 auto;
  width: 46px;
  height: 46px;
  border-radius: 8px;
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.rpd-lp-thumb-fallback {
  color: rgba(255, 255, 255, 0.7);
  font-size: 16px;
}

.rpd-lp-meta {
  flex: 1;
  min-width: 0;
}

.rpd-lp-name-row {
  display: flex;
  align-items: center;
  gap: 6px;
}

.rpd-lp-status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.rpd-lp-name {
  font-family: "Poppins", sans-serif;
  font-weight: 600;
  font-size: 12.5px;
  color: #12283f;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.rpd-lp-dev {
  font-size: 11px;
  color: #8a8a8a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-top: 1px;
}

.rpd-lp-location {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 10.5px;
  color: #b6bac2;
  margin-top: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.rpd-lp-location-icon {
  font-size: 9px;
  flex-shrink: 0;
}

.rpd-lp-types {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 5px;
}

.rpd-lp-chip {
  display: inline-block;
  font-size: 9.5px;
  font-weight: 600;
  line-height: 1;
  padding: 3px 6px;
  border-radius: 999px;
  background: #eef3f8;
  color: #12283f;
  white-space: nowrap;
}

.rpd-lp-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 2px 8px 2px 7px;
  border-radius: 999px;
  font-size: 9.5px;
  font-weight: 500;
  background: rgba(84, 84, 84, 0.1);
  color: #545454;
  white-space: nowrap;
}

.rpd-lp-badge::before {
  content: "";
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: #8a8a8a;
  flex-shrink: 0;
}

.rpd-lp-badge--active {
  background: rgba(31, 157, 85, 0.1);
  color: #1f7a45;
}

.rpd-lp-badge--active::before {
  background: #1f9d55;
}

.rpd-lp-badge--soon {
  background: rgba(255, 184, 28, 0.14);
  color: #8a6a00;
}

.rpd-lp-badge--soon::before {
  background: #ffb81c;
}

.rpd-lp-badge--planning {
  background: rgba(52, 51, 118, 0.1);
  color: #343376;
}

.rpd-lp-badge--planning::before {
  background: #343376;
}

.rpd-lp-stats {
  flex: 0 0 auto;
  text-align: right;
  align-self: center;
}

.rpd-lp-psf {
  font-family: "Poppins", sans-serif;
  font-weight: 600;
  font-size: 13px;
  color: #12283f;
}

.rpd-lp-psf-unit {
  font-size: 9.5px;
  color: #b6bac2;
  margin-top: -1px;
}

.rpd-lp-empty {
  padding: 24px 14px;
  text-align: center;
  font-size: 12px;
  color: #8a8a8a;
}

@media (max-width: 1100px) {
  .rpd-map-layout {
    flex-direction: column;
    height: auto;
  }

  .rpd-lp-list {
    flex: 0 0 auto;
    max-width: none;
    max-height: 360px;
  }

  .rpd-map-host {
    height: 60vh;
    min-height: 380px;
  }
}

html.dark .rpd-map-host,
html.dark .rpd-lp-list {
  border-color: rgba(255, 255, 255, 0.08);
}

html.dark .rpd-lp-list,
html.dark .rpd-lp-item {
  background: #12283f;
}

html.dark .rpd-lp-list-header,
html.dark .rpd-lp-item {
  border-bottom-color: rgba(255, 255, 255, 0.08);
}

html.dark .rpd-lp-list-title,
html.dark .rpd-lp-list-count strong,
html.dark .rpd-lp-name,
html.dark .rpd-lp-psf {
  color: #e6edf6;
}

html.dark .rpd-lp-item:hover {
  background: #16314c;
}

html.dark .rpd-lp-item--selected {
  background: #1b3a5a;
}

html.dark .rpd-lp-chip {
  background: rgba(255, 255, 255, 0.08);
  color: #cfe0f2;
}

html.dark .rpd-lp-sort {
  background: transparent;
  border-color: rgba(255, 255, 255, 0.15);
  color: #8ba3bd;
}

html.dark .rpd-lp-sort:hover {
  background: rgba(255, 255, 255, 0.06);
  color: #e6edf6;
}

/* ── Feature tour (vue-tour) — themed to RPD ──
   The same card ProjectDetail.vue's feature tours use, so a reader who has met
   one of those recognises this as the same kind of thing. Navy in both themes:
   the step floats over the page rather than sitting in it, and a card that
   flipped to light on a dark page would read as a different component. Scoped
   still reaches .v-step — it is the root of the VStep child, so it carries this
   component's scope attribute. ── */
.v-tour .v-step {
  background: #12283F !important;
  color: #fff !important;
  border-radius: 12px !important;
  box-shadow: 0 24px 56px rgba(18, 40, 63, 0.32), 0 8px 16px rgba(18, 40, 63, 0.12) !important;
  padding: 18px 20px !important;
  max-width: 340px !important;
  font-family: 'Poppins', 'Helvetica Light', Arial, sans-serif !important;
}

.v-tour .v-step__header {
  background: transparent !important;
  padding: 0 0 6px !important;
  border: none !important;
  font-weight: 700 !important;
  font-size: 14px !important;
  letter-spacing: -0.01em !important;
  color: #fff !important;
}

.v-tour .v-step__content {
  font-size: 13px !important;
  line-height: 1.5 !important;
  color: rgba(255, 255, 255, 0.85) !important;
}

.v-tour .v-step__arrow,
.v-tour .v-step__arrow::before {
  background: #12283F !important;
  border: none !important;
}

.v-tour .v-step__buttons {
  padding-top: 12px !important;
}

.v-tour .v-step__button {
  background: #FC5868 !important;
  border: 1px solid #FC5868 !important;
  color: #fff !important;
  font-family: inherit !important;
  font-size: 12px !important;
  font-weight: 600 !important;
  padding: 6px 12px !important;
  border-radius: 8px !important;
}

.v-tour .v-step__button-skip,
.v-tour .v-step__button-previous {
  background: transparent !important;
  border: 1px solid rgba(255, 255, 255, 0.18) !important;
  color: rgba(255, 255, 255, 0.75) !important;
}

.v-tour .v-step__button:hover {
  filter: brightness(1.05);
}

/* The header wraps its pills below the card's comfortable width, and a step
   anchored to a button that has just moved under the others needs the room. */
@media (max-width: 640px) {
  .v-tour .v-step {
    max-width: 280px !important;
  }
}
</style>

<style>
/* ── Treeselect overrides (matches MarketStrata) ── */
.ms-filter-bar .vue-treeselect__control {
  min-height: 32px !important;
  height: 32px !important;
  border: 1px solid #e8eaed !important;
  border-radius: 6px !important;
  background: #ffffff !important;
  font-size: 12px !important;
  font-family: -apple-system, BlinkMacSystemFont, "Inter", "Segoe UI", sans-serif !important;
}

.ms-filter-bar .vue-treeselect__control:hover {
  border-color: #d1d5db !important;
}

.ms-filter-bar .vue-treeselect--focused .vue-treeselect__control {
  border-color: #0e1b2c !important;
  box-shadow: none !important;
}

.ms-filter-bar .vue-treeselect__placeholder,
.ms-filter-bar .vue-treeselect__single-value {
  line-height: 30px !important;
  font-size: 12px !important;
  color: #9ca3af !important;
}

.ms-filter-bar .vue-treeselect__multi-value {
  padding: 2px 4px !important;
  margin: 0 !important;
}

.ms-filter-bar .vue-treeselect__multi-value-item {
  background: #0e1b2c !important;
  border: none !important;
  border-radius: 4px !important;
  color: #ffffff !important;
  font-size: 11px !important;
  padding: 1px 5px !important;
  margin: 1px 2px !important;
  height: 20px !important;
  line-height: 20px !important;
}

.ms-filter-bar .vue-treeselect__value-remove {
  color: rgba(255, 255, 255, 0.6) !important;
  border-left-color: rgba(255, 255, 255, 0.2) !important;
}

.ms-filter-bar .vue-treeselect__value-remove:hover {
  color: #ffffff !important;
  background: rgba(255, 255, 255, 0.15) !important;
}

.ms-filter-bar .vue-treeselect__menu {
  font-size: 12px !important;
  border-radius: 8px !important;
  border: 1px solid #e8eaed !important;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1) !important;
}

.ms-filter-bar .vue-treeselect__option--selected {
  background: #eff6ff !important;
  color: #1d4ed8 !important;
}

.ms-filter-bar .vue-treeselect__option:hover {
  background: #f9fafb !important;
}

.ms-filter-bar .vue-treeselect__label {
  font-size: 12px !important;
  color: #374151 !important;
}

.ms-filter-bar .vue-treeselect__option--selected .vue-treeselect__label {
  color: #1d4ed8 !important;
}

.ms-filter-bar .vue-treeselect__indent-level-0>.vue-treeselect__list-item>.vue-treeselect__item-container>.vue-treeselect__label-container>.vue-treeselect__label {
  font-weight: 600 !important;
  color: #111827 !important;
}

/* ── Table wrap (matches MarketStrata) ── */
.mls-ms-table-wrap {
  overflow: hidden;
  background: #fff;
  border: 1px solid #E9E8E9;
  border-radius: 10px;
}

/* ── Loading-more indicator (infinite scroll append) ── */
.rpd-loading-more {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px;
  font-size: 12px;
  color: #545454;
  background: #fff;
  border-top: 1px solid #E2E4EA;
}

.rpd-loading-more__spinner {
  width: 14px;
  height: 14px;
  border: 2px solid #C9CCD2;
  border-top-color: #12283F;
  border-radius: 50%;
  animation: rpd-lm-spin 0.6s linear infinite;
}

@keyframes rpd-lm-spin {
  to {
    transform: rotate(360deg);
  }
}

/* Tab-switch loading, in the table's own region. Reserves roughly a table's
   worth of height so the page doesn't collapse and snap back as rows arrive. */
.rpd-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  min-height: 320px;
  color: #9ca3af;
  font-size: 13px;
}

.rpd-spinner {
  width: 24px;
  height: 24px;
  border: 2px solid #e5e7eb;
  border-top-color: #0e1b2c;
  border-radius: 50%;
  animation: rpd-lm-spin 0.7s linear infinite;
}

html.dark .rpd-state {
  color: #8ba3bd;
}

html.dark .rpd-spinner {
  border-color: rgba(255, 255, 255, 0.15);
  border-top-color: #4db7fe;
}

html.dark .rpd-loading-more {
  color: rgba(255, 255, 255, 0.5);
  background: #0b1625;
  border-top-color: rgba(255, 255, 255, 0.07);
}

html.dark .rpd-loading-more__spinner {
  border-color: rgba(255, 255, 255, 0.18);
  border-top-color: #d6e4f0;
}

/* ── Tabulator overrides (matches MarketStrata ms-table-wrap) ── */
.ms-table-wrap .tabulator {
  border: none !important;
  background: #ffffff !important;
  font-family: -apple-system, BlinkMacSystemFont, "Inter", "Segoe UI", sans-serif !important;
  font-size: 11px !important;
}

.ms-table-wrap .tabulator-header {
  background: #f8f9fa !important;
  border-bottom: 1px solid #e8eaed !important;
  border-top: none !important;
}

.ms-table-wrap .tabulator-col {
  background: transparent !important;
  border-right: none !important;
  padding: 0 !important;
}

.ms-table-wrap .tabulator-col-content {
  padding: 10px 14px !important;
}

.ms-table-wrap .tabulator-col-title {
  font-size: 10px !important;
  font-weight: 600 !important;
  color: #6b7280 !important;
  text-transform: uppercase !important;
  letter-spacing: 0.06em !important;
  white-space: nowrap !important;
}

.ms-table-wrap .tabulator-col.tabulator-sortable:hover .tabulator-col-title {
  color: #0e1b2c !important;
}

.ms-table-wrap .tabulator-col.tabulator-col-sorter-element:hover {
  background: #f0f1f3 !important;
}

.ms-table-wrap .tabulator-col .tabulator-col-sorter {
  color: #d1d5db !important;
}

.ms-table-wrap .tabulator-col.tabulator-col-sorter-element .tabulator-col-sorter {
  color: #0e1b2c !important;
}

.ms-table-wrap .tabulator-tableHolder {
  background: #ffffff !important;
}

.ms-table-wrap .tabulator-row {
  background: #ffffff !important;
  border-bottom: 1px solid #f3f4f6 !important;
  transition: background 0.1s !important;
}

.ms-table-wrap .tabulator-row:hover {
  background: #f9fafb !important;
}

.ms-table-wrap .tabulator-row.tabulator-row-even {
  background: #ffffff !important;
}

.ms-table-wrap .tabulator-row.tabulator-row-even:hover {
  background: #f9fafb !important;
}

.ms-table-wrap .tabulator-cell {
  border-right: none !important;
  padding: 0 14px !important;
  color: #374151 !important;
  overflow: hidden !important;
  box-sizing: border-box !important;
}

.ms-table-wrap .tabulator-tableHolder::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

.ms-table-wrap .tabulator-tableHolder::-webkit-scrollbar-track {
  background: transparent;
}

.ms-table-wrap .tabulator-tableHolder::-webkit-scrollbar-thumb {
  background: #e0e0e0;
  border-radius: 3px;
}

.ms-table-wrap .tabulator-row.tabulator-selected {
  background: #eff6ff !important;
}

.ms-table-wrap .tabulator-row.tabulator-selected:hover {
  background: #dbeafe !important;
}

.ms-table-wrap .tabulator-footer {
  border-top: 1px solid #e8eaed !important;
  background: #fff !important;
}

.ms-table-wrap .tabulator-page {
  font-size: 12px !important;
  border: 1px solid #E2E4EA !important;
  border-radius: 6px !important;
  color: #545454 !important;
  margin: 0 2px !important;
}

.ms-table-wrap .tabulator-page.active {
  background: #12283F !important;
  color: #fff !important;
  border-color: #12283F !important;
}

/* ── Dark mode: treeselect ── */
html.dark .ms-filter-bar .vue-treeselect__control {
  background: #152030 !important;
  border-color: rgba(255, 255, 255, 0.1) !important;
  color: #d6e4f0 !important;
}

html.dark .ms-filter-bar .vue-treeselect__placeholder,
html.dark .ms-filter-bar .vue-treeselect__single-value {
  color: rgba(255, 255, 255, 0.3) !important;
}

html.dark .ms-filter-bar .vue-treeselect__menu {
  background: #0e1b2c !important;
  border-color: rgba(255, 255, 255, 0.1) !important;
}

html.dark .ms-filter-bar .vue-treeselect__label {
  color: rgba(255, 255, 255, 0.65) !important;
}

html.dark .ms-filter-bar .vue-treeselect__option:hover {
  background: rgba(255, 255, 255, 0.05) !important;
}

html.dark .ms-filter-bar .vue-treeselect__option--selected {
  background: rgba(52, 51, 118, 0.25) !important;
}

html.dark .ms-filter-bar .vue-treeselect__option--selected .vue-treeselect__label {
  color: #93c5fd !important;
}

html.dark .ms-filter-bar .vue-treeselect__indent-level-0>.vue-treeselect__list-item>.vue-treeselect__item-container>.vue-treeselect__label-container>.vue-treeselect__label {
  color: #d6e4f0 !important;
}

/* ── Dark mode: table wrap + tabulator ── */
html.dark .mls-ms-table-wrap {
  background: #0e1b2c;
  border-color: rgba(255, 255, 255, 0.07);
}

html.dark .ms-table-wrap .tabulator,
html.dark .ms-table-wrap .tabulator-tableHolder {
  background: #0b1625 !important;
}

html.dark .ms-table-wrap .tabulator-header {
  background: #0e1b2c !important;
  border-bottom-color: rgba(255, 255, 255, 0.07) !important;
}

html.dark .ms-table-wrap .tabulator-col {
  background: transparent !important;
}

html.dark .ms-table-wrap .tabulator-col-title {
  color: rgba(255, 255, 255, 0.4) !important;
}

html.dark .ms-table-wrap .tabulator-col.tabulator-sortable:hover .tabulator-col-title {
  color: #d6e4f0 !important;
}

html.dark .ms-table-wrap .tabulator-col.tabulator-col-sorter-element:hover {
  background: rgba(255, 255, 255, 0.05) !important;
}

html.dark .ms-table-wrap .tabulator-row {
  background: #0b1625 !important;
  border-bottom-color: rgba(255, 255, 255, 0.04) !important;
}

html.dark .ms-table-wrap .tabulator-row:hover {
  background: #16263d !important;
}

html.dark .ms-table-wrap .tabulator-row.tabulator-row-even {
  background: #0b1625 !important;
}

html.dark .ms-table-wrap .tabulator-row.tabulator-row-even:hover {
  background: #16263d !important;
}

html.dark .ms-table-wrap .tabulator-row.tabulator-selected {
  background: #1e3a5f !important;
}

html.dark .ms-table-wrap .tabulator-row.tabulator-selected:hover {
  background: #1e3a5f !important;
}

html.dark .ms-table-wrap .tabulator-cell {
  color: rgba(255, 255, 255, 0.7) !important;
}

html.dark .ms-table-wrap .tabulator-tableHolder::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.1);
}

html.dark .ms-table-wrap .tabulator-footer {
  background: #0e1b2c !important;
  border-top-color: rgba(255, 255, 255, 0.07) !important;
}

html.dark .ms-table-wrap .tabulator-page {
  background: #0b1625 !important;
  border-color: rgba(255, 255, 255, 0.08) !important;
  color: rgba(255, 255, 255, 0.7) !important;
}

html.dark .ms-table-wrap .tabulator-page:hover:not([disabled]) {
  background: #16263d !important;
  border-color: rgba(255, 255, 255, 0.15) !important;
}

html.dark .ms-table-wrap .tabulator-page.active {
  background: #d6e4f0 !important;
  border-color: #d6e4f0 !important;
  color: #0b1625 !important;
}

/* ── Unit Report Summary Modal (matches City Preview Modal) ── */
.cp-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(3px);
  z-index: 1060;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}

.cp-dialog {
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.18), 0 4px 16px rgba(0, 0, 0, 0.08);
  width: 100%;
  max-width: 780px;
  max-height: 82vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.cp-dialog--wide {
  max-width: 1200px;
}

.cp-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px 16px;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
  gap: 12px;
}

.cp-header-left {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}

.cp-header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.cp-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: #0e1b2c;
  border-radius: 8px;
  color: #ffffff;
  font-size: 14px;
  flex-shrink: 0;
}

.cp-title {
  font-size: 17px;
  font-weight: 600;
  color: #111827;
  letter-spacing: -0.01em;
}

.cp-action-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 12px;
  border: 1px solid #e5e7eb;
  background: #ffffff;
  border-radius: 8px;
  color: #111827;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s, color 0.15s;
}

.cp-action-btn:hover {
  background: #0e1b2c;
  border-color: #0e1b2c;
  color: #ffffff;
}

.cp-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border: none;
  background: #f3f4f6;
  border-radius: 8px;
  color: #6b7280;
  cursor: pointer;
  font-size: 14px;
  transition: background 0.15s, color 0.15s;
}

.cp-close:hover {
  background: #e5e7eb;
  color: #111827;
}

.cp-body {
  overflow-y: auto;
  padding: 16px 24px 24px;
  flex: 1;
  min-height: 0;
}

.cp-section-title {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #6b7280;
  margin: 4px 0 10px;
}

.cp-section-title--spaced {
  margin-top: 24px;
}

.cp-table-wrap {
  width: 100%;
  overflow-x: auto;
  border: 1px solid #f3f4f6;
  border-radius: 10px;
}

.cp-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.cp-table thead tr {
  border-bottom: 2px solid #e5e7eb;
}

.cp-table th {
  padding: 10px 12px;
  color: #6b7280;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  white-space: nowrap;
  background: #fafbfc;
  position: sticky;
  top: 0;
  z-index: 1;
}

.cp-table td {
  padding: 10px 12px;
  color: #1f2937;
  border-bottom: 1px solid #f3f4f6;
  white-space: nowrap;
}

.cp-table tbody tr:last-child td {
  border-bottom: none;
}

.cp-table tbody tr:hover td {
  background: #f9fafb;
}

.cp-num {
  text-align: right;
}

.cp-city-name {
  font-weight: 500;
  color: #111827;
  text-align: left;
}

.cp-empty-row td {
  text-align: center;
  color: #9ca3af;
  padding: 32px;
}

.cp-total-row td {
  background: #f9fafb;
  font-weight: 700;
  color: #111827;
  border-top: 2px solid #e5e7eb;
  border-bottom: none;
}

.cp-view-btn {
  display: inline-flex;
  align-items: center;
  height: 28px;
  padding: 0 12px;
  border: none;
  background: #0e1b2c;
  border-radius: 6px;
  color: #ffffff;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;
}

.cp-view-btn:hover {
  background: #1f2f48;
}

/* ── City Breakdown Chart ── */
.rpd-chart-typetoggle {
  display: inline-flex;
  background: #f3f4f6;
  border-radius: 8px;
  padding: 2px;
  gap: 2px;
}

.rpd-chart-typebtn {
  border: none;
  background: transparent;
  border-radius: 6px;
  padding: 4px 12px;
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}

.rpd-chart-typebtn--on {
  background: #0e1b2c;
  color: #ffffff;
}

.rpd-chart-citiesbtn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 1px solid #e5e7eb;
  background: #ffffff;
  border-radius: 8px;
  padding: 5px 12px;
  font-size: 12px;
  font-weight: 600;
  color: #374151;
  cursor: pointer;
  transition: background 0.15s, color 0.15s, border-color 0.15s;
}

.rpd-chart-citiesbtn--on {
  background: #0e1b2c;
  border-color: #0e1b2c;
  color: #ffffff;
}

.rpd-chart-controls {
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 12px 14px;
  margin-bottom: 16px;
  background: #fbfcfe;
}

.rpd-chart-controls-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

.rpd-chart-controls-title {
  font-size: 12px;
  font-weight: 700;
  color: #111827;
}

.rpd-chart-reset {
  border: none;
  background: transparent;
  font-size: 12px;
  font-weight: 600;
  color: #2d9cdb;
  cursor: pointer;
  padding: 0;
}

.rpd-chart-controls-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 8px 16px;
  max-height: 220px;
  overflow-y: auto;
}

.rpd-chart-cityrow {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  font-size: 12px;
  font-weight: 600;
  color: #374151;
  cursor: pointer;
}

.rpd-chart-citycheck {
  flex-shrink: 0;
  cursor: pointer;
}

.rpd-chart-cityname {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.rpd-chart-colorinput {
  flex-shrink: 0;
  width: 26px;
  height: 22px;
  padding: 0;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: transparent;
  cursor: pointer;
}

.rpd-chart-colorinput:disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

.rpd-chart-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 16px;
  margin-bottom: 16px;
}

.rpd-chart-legenditem {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #374151;
}

.rpd-chart-legenddot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
}

.rpd-chart-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
}

.rpd-chart-card {
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 14px 14px 10px;
  background: #fbfcfe;
}

.rpd-chart-cardtitle {
  font-size: 12px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 8px;
}

.rpd-chart-canvas-wrap {
  position: relative;
  height: 260px;
  width: 100%;
}

.rpd-chart-empty {
  padding: 60px 0;
  text-align: center;
  color: #9ca3af;
  font-size: 14px;
}

.rpd-chart-note {
  margin-top: 12px;
  font-size: 11px;
  color: #9ca3af;
  text-align: right;
}

html.dark .rpd-chart-typetoggle {
  background: rgba(255, 255, 255, 0.07);
}

html.dark .rpd-chart-typebtn {
  color: rgba(255, 255, 255, 0.6);
}

html.dark .rpd-chart-typebtn--on {
  background: #d6e4f0;
  color: #0b1625;
}

html.dark .rpd-chart-legenditem {
  color: rgba(255, 255, 255, 0.8);
}

html.dark .rpd-chart-citiesbtn {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.12);
  color: rgba(255, 255, 255, 0.8);
}

html.dark .rpd-chart-citiesbtn--on {
  background: #d6e4f0;
  border-color: #d6e4f0;
  color: #0b1625;
}

html.dark .rpd-chart-controls {
  background: rgba(255, 255, 255, 0.03);
  border-color: rgba(255, 255, 255, 0.08);
}

html.dark .rpd-chart-controls-title {
  color: #f3f4f6;
}

html.dark .rpd-chart-cityrow {
  color: rgba(255, 255, 255, 0.8);
}

html.dark .rpd-chart-colorinput {
  border-color: rgba(255, 255, 255, 0.18);
}

html.dark .rpd-chart-card {
  background: rgba(255, 255, 255, 0.03);
  border-color: rgba(255, 255, 255, 0.08);
}

html.dark .rpd-chart-cardtitle {
  color: #f3f4f6;
}

/* ── Transition ── */
.cp-modal-enter-active,
.cp-modal-leave-active {
  transition: opacity 0.2s ease;
}

.cp-modal-enter-active .cp-dialog,
.cp-modal-leave-active .cp-dialog {
  transition: transform 0.22s cubic-bezier(0.34, 1.26, 0.64, 1), opacity 0.2s ease;
}

.cp-modal-enter,
.cp-modal-leave-to {
  opacity: 0;
}

.cp-modal-enter .cp-dialog {
  transform: scale(0.94) translateY(12px);
  opacity: 0;
}

.cp-modal-leave-to .cp-dialog {
  transform: scale(0.96) translateY(8px);
  opacity: 0;
}

/* ── Dark mode ── */
html.dark .cp-dialog {
  background: #0e1b2c;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.5);
}

html.dark .cp-header {
  border-bottom-color: rgba(255, 255, 255, 0.08);
}

html.dark .cp-title {
  color: #f9fafb;
}

html.dark .cp-action-btn {
  background: transparent;
  border-color: rgba(255, 255, 255, 0.15);
  color: rgba(255, 255, 255, 0.85);
}

html.dark .cp-action-btn:hover {
  background: #d6e4f0;
  border-color: #d6e4f0;
  color: #0b1625;
}

html.dark .cp-close {
  background: rgba(255, 255, 255, 0.07);
  color: rgba(255, 255, 255, 0.5);
}

html.dark .cp-close:hover {
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
}

html.dark .cp-section-title {
  color: rgba(255, 255, 255, 0.45);
}

html.dark .cp-table-wrap {
  border-color: rgba(255, 255, 255, 0.06);
}

html.dark .cp-table th {
  background: #0b1625;
  color: rgba(255, 255, 255, 0.45);
  border-bottom-color: rgba(255, 255, 255, 0.08);
}

html.dark .cp-table td {
  color: rgba(255, 255, 255, 0.75);
  border-bottom-color: rgba(255, 255, 255, 0.05);
}

html.dark .cp-table tbody tr:hover td {
  background: rgba(255, 255, 255, 0.04);
}

html.dark .cp-city-name {
  color: #f3f4f6;
}

html.dark .cp-empty-row td {
  color: rgba(255, 255, 255, 0.4);
}

html.dark .cp-total-row td {
  background: rgba(255, 255, 255, 0.04);
  color: #f3f4f6;
  border-top-color: rgba(255, 255, 255, 0.12);
}

html.dark .cp-view-btn {
  background: #d6e4f0;
  color: #0b1625;
}

html.dark .cp-view-btn:hover {
  background: #ffffff;
}

/* ── Responsive ── */
@media (max-width: 768px) {
  .cp-overlay {
    padding: 12px;
  }

  .cp-dialog--wide {
    max-width: 100%;
    max-height: 92vh;
  }

  .cp-header {
    padding: 14px 16px 12px;
    flex-wrap: wrap;
  }

  .cp-body {
    padding: 12px 16px 16px;
  }

  .cp-table th,
  .cp-table td {
    padding: 8px 10px;
    font-size: 12px;
  }

  .cp-action-btn {
    font-size: 11px;
    padding: 0 10px;
  }

  .rpd-chart-grid {
    grid-template-columns: 1fr;
  }
}
</style>
